/**
 * Builder.io Component Registration
 *
 * This file registers custom components with Builder.io,
 * making them available in the visual editor.
 *
 * ─────────────────────────────────────────────────────────────────────
 * INSERT-MENU PRIORITY TIERS (PD-34190)
 * ─────────────────────────────────────────────────────────────────────
 * Builder.io's `priority` controls insert-menu order — lower number =
 * higher in the list. We tier components by frequency-of-use so the
 * blocks marketing reaches for many times per page float to the top.
 *
 *  0 – 3   MOST-USED building blocks (used 5–10× per offering)
 *            0  Story: Text Block            (prose, headings + body)
 *            1  Utility: Media Block         (image / video / gif drop-in)
 *            2  Utility: Disclaimer          (footnote under any element)
 *            3  Utility: Video Embed         (YouTube / Vimeo)
 *
 *  4 – 7   PAGE CHROME + CORE STORY SECTIONS (placed once per page)
 *            4  Page: Top Nav
 *            5  Story: Hero
 *            6  Story: Text + Media
 *            7  Story: Stats Grid
 *
 *  8 – 16  COMMON STORY SECTIONS
 *            8  Story: Quote
 *            9  Story: CTA
 *           10  Story: Banner
 *           11  Story: Features
 *           12  Story: Partners / Logos
 *           13  Story: Reference List       (PD-34290 — Notable Investors / Further Research)
 *           14  Story: Reasons to Invest
 *           15  Story: Accordion
 *           16  Story: Email Capture
 *
 * 20 – 30  DATA-BOUND SECTIONS (Team, Terms, Perks, FAQ, Updates, etc.)
 * 40 – 44  LESS-USED UTILITIES (Invest Button, Bubbles, IRA Cross-Link)
 * 99       Page: Footer (always last)
 *
 * Deprecated `Classic:` wrappers are hidden from the insert menu via
 * `hideFromInsertMenu: true` and are not in the priority chain.
 *
 * ─────────────────────────────────────────────────────────────────────
 * CANONICAL INPUT ORDER (PD-34190)
 * ─────────────────────────────────────────────────────────────────────
 * Every `Builder.registerComponent({ inputs: [...] })` block should list
 * its inputs in this order so the editor experience is predictable when
 * marketing/OM switches between blocks. Skip items that don't apply.
 *
 * CORE
 *   1.  sectionId                          (Section ID — anchor)
 *   2.  useDynamicData                     (Use Dynamic Offering Data toggle)
 *   3.  sectionLabel + sectionLabelColor + sectionLabelFontSize
 *   4.  showTitle, title, titleColor, titleHighlight, titleHighlightColor,
 *       titleFontSize, titleMobileFontSize, headerAlignment
 *   5.  subtitle / description / body      (richText or longText)
 *   6.  Primary content block              (stats array, logos, items,
 *                                           quoteText, embed code, etc.)
 *   7.  Image / Media / Video              (image, mediaUrl, videoUrl)
 *   8.  Buttons / CTAs                     (showInvestButton, button props)
 *
 * LAYOUT & PRESENTATION
 *   9.  Layout direction / variant        (mediaPosition, columns, layout)
 *  10.  Background                        (color, image, gradient, overlay)
 *  11.  Section padding / vertical spacing (verticalPadding, etc.)
 *  12.  Content max-width                 (contentMaxWidth — use CONTENT_WIDTH)
 *
 * TYPOGRAPHY & FONTS
 *  13.  Title font (source + family + weights)
 *  14.  Body font (source + family + weights)
 *
 * ADVANCED  (mark with `advanced: true` so they collapse by default)
 *  15.  Border radius, custom margins, anchor offsets, debug flags, etc.
 *
 * Components (in typical page order):
 *   0. Top Nav - Sticky navigation with watchlist and invest buttons
 *   1. Hero Section - Hero with video, invest button, raised banner
 *   2. Story: Text + Media - Two-column text + image/video layout
 *   3. Story: Stats Grid - Traction/metrics with stat boxes
 *   4. Story: Logo Grid - Partner/client logo display
 *   5. Story: Text Block - Flexible text block (formerly Story: Centered)
 *   6. Story: Quote - Testimonial with background image
 *   7. Story: Features - Feature list with icons + media
 *   8. Form Section - Email/phone capture form
 *   9. Team Slider - Team carousel with featured member
 *  10. Terms Section - Offering terms with dynamic data
 *  11. Perks Section - Investor perks with sticky sidebar
 *  12. Discussion Section - Comments/Q&A section
 *  13. CTA Section - Call-to-action with invest button
 *  14. Footer Section - Page footer with disclaimer
 *  15. Invest Button - Standalone invest button (utility)
 *  29. Updates Section - Company updates from offering data
 */

import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
// PD-34301: import from the lightweight constants module rather than the full
// BuilderMediaBlock component, so the dynamic() lazy-load below remains
// effective and BuilderMediaBlock + its Vimeo / MUI / image-optimization deps
// stay out of the eager bundle.
import { DEFAULT_DISCLAIMER_TEXT as MEDIA_BLOCK_DEFAULT_DISCLAIMER_TEXT } from './components/BuilderMediaBlock/constants';
import { SECTION_BORDER_RADIUS_INPUT } from './components/shared/sectionBorderRadius';

// Lazy-load components to reduce initial client bundle size (~270KB -> metadata only)
// Actual component code is loaded on-demand when rendered by BuilderComponent
const BuilderHeroSection = dynamic(() => import('./components/BuilderHeroSection'));
const BuilderSplitSection = dynamic(() => import('./components/BuilderSplitSection'));
const BuilderStatsSection = dynamic(() => import('./components/BuilderStatsSection'));
const BuilderLogosSection = dynamic(() => import('./components/BuilderLogosSection'));
const BuilderCenteredSection = dynamic(() => import('./components/BuilderCenteredSection'));
const BuilderQuoteSection = dynamic(() => import('./components/BuilderQuoteSection'));
const BuilderFeaturesSection = dynamic(() => import('./components/BuilderFeaturesSection'));
const BuilderFormSection = dynamic(() => import('./components/BuilderFormSection'));
const BuilderTeamSlider = dynamic(() => import('./components/BuilderTeamSlider'));
const BuilderTermsSection = dynamic(() => import('./components/BuilderTermsSection'));
const BuilderTableSection = dynamic(() => import('./components/BuilderTableSection'));
const BuilderPerksSection = dynamic(() => import('./components/BuilderPerksSection'));
const BuilderDiscussionSection = dynamic(() => import('./components/BuilderDiscussionSection'));
const BuilderCTASection = dynamic(() => import('./components/BuilderCTASection'));
const BuilderRTISection = dynamic(() => import('./components/BuilderRTISection'));
const BuilderFooterSection = dynamic(() => import('./components/BuilderFooterSection'));
const BuilderFAQSection = dynamic(() => import('./components/BuilderFAQSection'));
const BuilderAccordionSection = dynamic(() => import('./components/BuilderAccordionSection'));
const BuilderAboutSection = dynamic(() => import('./components/BuilderAboutSection'));
const BuilderRewardsSection = dynamic(() => import('./components/BuilderRewardsSection'));
const BuilderHowInvestingWorksSection = dynamic(
  () => import('./components/BuilderHowInvestingWorksSection'),
);
const BuilderWhyStartEngineSection = dynamic(
  () => import('./components/BuilderWhyStartEngineSection'),
);
const BuilderCompanyHighlightsSection = dynamic(
  () => import('./components/BuilderCompanyHighlightsSection'),
);
const BuilderInvestButton = dynamic(() => import('./components/BuilderInvestButton'));
const BuilderTopNavSection = dynamic(() => import('./components/BuilderTopNavSection'));
const BuilderScheduleCallBubble = dynamic(() => import('./components/BuilderScheduleCallBubble'));
const BuilderGetEquityBubble = dynamic(() => import('./components/BuilderGetEquityBubble'));
const BuilderVideoEmbed = dynamic(() => import('./components/BuilderVideoEmbed'));
const BuilderMediaBlock = dynamic(() => import('./components/BuilderMediaBlock'));
const BuilderIraCrossLink = dynamic(() => import('./components/BuilderIraCrossLink'));
const BuilderImage = dynamic(() => import('./components/BuilderImage'));
const BuilderDisclaimer = dynamic(() => import('./components/BuilderDisclaimer'));
const BuilderBannerSection = dynamic(() => import('./components/BuilderBannerSection'));
const BuilderUpdatesSection = dynamic(() => import('./components/BuilderUpdatesSection'));
const BuilderPressSection = dynamic(() => import('./components/BuilderPressSection'));
const BuilderSpotlightsSection = dynamic(() => import('./components/BuilderSpotlightsSection'));
const BuilderReferenceList = dynamic(() => import('./components/BuilderReferenceList'));

// Classic Campaign Page Components
const BuilderClassicStorySection = dynamic(() => import('./components/BuilderClassicStorySection'));
const BuilderClassicSectionNav = dynamic(() => import('./components/BuilderClassicSectionNav'));
const BuilderClassicInvestSidebar = dynamic(
  () => import('./components/BuilderClassicInvestSidebar'),
);
const BuilderClassicTermsSection = dynamic(() => import('./components/BuilderClassicTermsSection'));
const BuilderClassicTeamSection = dynamic(() => import('./components/BuilderClassicTeamSection'));
const BuilderClassicRewardsSection = dynamic(
  () => import('./components/BuilderClassicRewardsSection'),
);
const BuilderClassicTwoColumnLayout = dynamic(
  () => import('./components/BuilderClassicTwoColumnLayout'),
);

// ============================================================
// SHARED DEFAULT THEME COLORS (Light Mode)
// ============================================================
const THEME = {
  // Backgrounds
  transparent: 'transparent',
  pageBg: '#FDFDFB',
  cardBg: '#FFFFFF',
  cardBgSubtle: '#F9FAFB',
  lightBg: '#F9FAFB',

  // Text (Light Mode = dark text on light bg)
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
  textLight: '#FFFFFF',
  textDark: '#111827',

  // Accents (StartEngine Platform Green)
  accent: '#185C65',
  accentHover: '#134950',
  ctaButton: '#F47F2B',
  ctaButtonHover: '#E06F1B',
  ctaButtonText: '#FFFFFF',

  // Borders
  border: '#E5E7EB',
  borderDark: '#D1D5DB',
};

// ============================================================
// COMPONENT PREVIEW ICONS (SVG Data URLs)
// ============================================================
const ICONS = {
  hero: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="8" width="16" height="4" rx="1" fill="#185C65"/><rect x="6" y="16" width="20" height="3" rx="1" fill="#FFFFFF"/><rect x="6" y="22" width="18" height="2" rx="1" fill="#6B7280"/><rect x="6" y="26" width="14" height="2" rx="1" fill="#6B7280"/><rect x="6" y="34" width="10" height="6" rx="2" fill="#185C65"/><rect x="28" y="8" width="14" height="28" rx="2" fill="#374151"/><path d="M32 20L38 24L32 28V20Z" fill="#FFFFFF"/></svg>`)}`,

  perks: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="6" width="14" height="36" rx="2" fill="#0F172A"/><rect x="6" y="10" width="10" height="2" rx="1" fill="#185C65"/><rect x="6" y="14" width="8" height="4" rx="1" fill="#FFFFFF"/><rect x="22" y="6" width="11" height="14" rx="2" fill="#374151"/><rect x="35" y="6" width="11" height="14" rx="2" fill="#374151"/><rect x="22" y="22" width="11" height="14" rx="2" fill="#374151"/><rect x="35" y="22" width="11" height="14" rx="2" fill="#374151"/></svg>`)}`,

  terms: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="6" width="16" height="3" rx="1" fill="#FFFFFF"/><rect x="6" y="14" width="8" height="2" rx="1" fill="#185C65"/><rect x="6" y="18" width="12" height="2" rx="1" fill="#6B7280"/><rect x="6" y="24" width="8" height="2" rx="1" fill="#185C65"/><rect x="6" y="28" width="10" height="2" rx="1" fill="#6B7280"/><rect x="6" y="34" width="8" height="2" rx="1" fill="#185C65"/><rect x="6" y="38" width="14" height="2" rx="1" fill="#6B7280"/><rect x="26" y="14" width="8" height="2" rx="1" fill="#185C65"/><rect x="26" y="18" width="12" height="2" rx="1" fill="#6B7280"/><rect x="26" y="24" width="8" height="2" rx="1" fill="#185C65"/><rect x="26" y="28" width="10" height="2" rx="1" fill="#6B7280"/></svg>`)}`,

  team: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="6" width="12" height="3" rx="1" fill="#185C65"/><rect x="6" y="12" width="16" height="4" rx="1" fill="#FFFFFF"/><circle cx="10" cy="30" r="6" fill="#374151"/><circle cx="24" cy="30" r="6" fill="#374151"/><circle cx="38" cy="30" r="6" fill="#374151"/><rect x="6" y="38" width="8" height="2" rx="1" fill="#6B7280"/><rect x="20" y="38" width="8" height="2" rx="1" fill="#6B7280"/><rect x="34" y="38" width="8" height="2" rx="1" fill="#6B7280"/></svg>`)}`,

  form: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="8" y="8" width="32" height="4" rx="1" fill="#FFFFFF"/><rect x="8" y="18" width="32" height="8" rx="2" fill="#374151" stroke="#6B7280"/><rect x="8" y="30" width="32" height="8" rx="2" fill="#374151" stroke="#6B7280"/><rect x="14" y="42" width="20" height="4" rx="2" fill="#185C65"/></svg>`)}`,

  discussion: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="6" width="18" height="3" rx="1" fill="#FFFFFF"/><rect x="6" y="14" width="36" height="10" rx="2" fill="#374151"/><circle cx="12" cy="32" r="4" fill="#185C65"/><rect x="20" y="28" width="20" height="2" rx="1" fill="#FFFFFF"/><rect x="20" y="32" width="16" height="2" rx="1" fill="#6B7280"/><circle cx="12" cy="44" r="4" fill="#185C65"/><rect x="20" y="40" width="18" height="2" rx="1" fill="#FFFFFF"/><rect x="20" y="44" width="14" height="2" rx="1" fill="#6B7280"/></svg>`)}`,

  footer: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#0F172A"/><rect x="6" y="8" width="8" height="8" rx="2" fill="#374151"/><rect x="6" y="20" width="20" height="2" rx="1" fill="#6B7280"/><rect x="6" y="24" width="18" height="2" rx="1" fill="#6B7280"/><rect x="6" y="28" width="16" height="2" rx="1" fill="#6B7280"/><rect x="30" y="16" width="12" height="6" rx="2" fill="#185C65"/><rect x="6" y="40" width="14" height="2" rx="1" fill="#6B7280"/><rect x="28" y="40" width="8" height="2" rx="1" fill="#185C65"/><rect x="38" y="40" width="6" height="2" rx="1" fill="#185C65"/></svg>`)}`,

  split: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="10" width="8" height="2" rx="1" fill="#185C65"/><rect x="4" y="16" width="16" height="3" rx="1" fill="#FFFFFF"/><rect x="4" y="22" width="14" height="2" rx="1" fill="#6B7280"/><rect x="4" y="26" width="12" height="2" rx="1" fill="#6B7280"/><rect x="4" y="30" width="14" height="2" rx="1" fill="#6B7280"/><rect x="26" y="8" width="18" height="26" rx="2" fill="#374151"/></svg>`)}`,

  stats: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="8" width="8" height="2" rx="1" fill="#185C65"/><rect x="4" y="14" width="14" height="3" rx="1" fill="#FFFFFF"/><rect x="4" y="20" width="12" height="2" rx="1" fill="#6B7280"/><rect x="4" y="24" width="10" height="2" rx="1" fill="#6B7280"/><rect x="26" y="6" width="9" height="16" rx="2" fill="#374151"/><rect x="37" y="6" width="9" height="16" rx="2" fill="#374151"/><rect x="26" y="24" width="9" height="16" rx="2" fill="#374151"/><rect x="37" y="24" width="9" height="16" rx="2" fill="#374151"/><rect x="28" y="10" width="5" height="3" rx="1" fill="#FFFFFF"/><rect x="39" y="10" width="5" height="3" rx="1" fill="#FFFFFF"/><rect x="28" y="28" width="5" height="3" rx="1" fill="#FFFFFF"/><rect x="39" y="28" width="5" height="3" rx="1" fill="#FFFFFF"/></svg>`)}`,

  logos: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="10" width="12" height="3" rx="1" fill="#FFFFFF"/><rect x="4" y="16" width="10" height="2" rx="1" fill="#6B7280"/><rect x="4" y="20" width="8" height="2" rx="1" fill="#6B7280"/><rect x="24" y="6" width="8" height="8" rx="2" fill="#374151"/><rect x="34" y="6" width="8" height="8" rx="2" fill="#374151"/><rect x="24" y="16" width="8" height="8" rx="2" fill="#374151"/><rect x="34" y="16" width="8" height="8" rx="2" fill="#374151"/><rect x="24" y="26" width="8" height="8" rx="2" fill="#374151"/><rect x="34" y="26" width="8" height="8" rx="2" fill="#374151"/></svg>`)}`,

  centered: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="16" y="8" width="16" height="2" rx="1" fill="#185C65"/><rect x="10" y="14" width="28" height="4" rx="1" fill="#FFFFFF"/><rect x="12" y="22" width="24" height="2" rx="1" fill="#6B7280"/><rect x="14" y="26" width="20" height="2" rx="1" fill="#6B7280"/><circle cx="12" cy="38" r="4" fill="#374151"/><circle cx="24" cy="38" r="4" fill="#374151"/><circle cx="36" cy="38" r="4" fill="#374151"/></svg>`)}`,

  quote: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="4" width="40" height="40" rx="2" fill="#374151" opacity="0.5"/><text x="8" y="20" font-size="24" fill="#185C65">"</text><rect x="10" y="22" width="28" height="3" rx="1" fill="#FFFFFF"/><rect x="12" y="28" width="24" height="3" rx="1" fill="#FFFFFF"/><rect x="18" y="36" width="12" height="2" rx="1" fill="#185C65"/></svg>`)}`,

  features: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="4" y="6" width="12" height="3" rx="1" fill="#FFFFFF"/><rect x="4" y="12" width="10" height="2" rx="1" fill="#6B7280"/><circle cx="8" cy="22" r="3" fill="#185C65"/><rect x="14" y="20" width="10" height="2" rx="1" fill="#185C65"/><rect x="14" y="24" width="8" height="1" rx="0.5" fill="#6B7280"/><circle cx="8" cy="32" r="3" fill="#185C65"/><rect x="14" y="30" width="10" height="2" rx="1" fill="#185C65"/><rect x="14" y="34" width="8" height="1" rx="0.5" fill="#6B7280"/><rect x="28" y="8" width="16" height="28" rx="2" fill="#374151"/></svg>`)}`,

  invest: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="8" y="16" width="32" height="16" rx="4" fill="#185C65"/><rect x="14" y="22" width="20" height="4" rx="1" fill="#FFFFFF"/></svg>`)}`,

  topNav: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#0F172A"/><rect x="4" y="16" width="8" height="8" rx="1" fill="#185C65"/><rect x="16" y="18" width="6" height="2" rx="1" fill="#FFFFFF"/><rect x="24" y="18" width="6" height="2" rx="1" fill="#FFFFFF"/><rect x="32" y="18" width="6" height="2" rx="1" fill="#FFFFFF"/><rect x="40" y="16" width="4" height="8" rx="2" fill="#185C65"/><rect x="4" y="22" width="8" height="2" rx="1" fill="#FFFFFF"/></svg>`)}`,

  cta: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="8" y="12" width="32" height="4" rx="1" fill="#FFFFFF"/><rect x="12" y="20" width="24" height="2" rx="1" fill="#6B7280"/><rect x="16" y="30" width="16" height="8" rx="4" fill="#185C65"/></svg>`)}`,

  accordion: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="8" width="36" height="8" rx="2" fill="#374151"/><rect x="8" y="11" width="14" height="2" rx="1" fill="#FFFFFF"/><path d="M38 10L41 14L35 14Z" fill="#185C65"/><rect x="6" y="18" width="36" height="8" rx="2" fill="#0F172A"/><rect x="8" y="21" width="14" height="2" rx="1" fill="#185C65"/><path d="M35 24L41 24L38 20Z" fill="#185C65"/><rect x="8" y="28" width="32" height="1" rx="0.5" fill="#6B7280"/><rect x="8" y="31" width="28" height="1" rx="0.5" fill="#6B7280"/><rect x="6" y="36" width="36" height="8" rx="2" fill="#374151"/><rect x="8" y="39" width="14" height="2" rx="1" fill="#FFFFFF"/><path d="M38 38L41 42L35 42Z" fill="#185C65"/></svg>`)}`,

  about: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><circle cx="24" cy="16" r="8" fill="#374151"/><rect x="22" y="12" width="4" height="3" rx="1" fill="#185C65"/><rect x="22" y="17" width="4" height="5" rx="1" fill="#185C65"/><rect x="8" y="30" width="10" height="2" rx="1" fill="#6B7280"/><rect x="20" y="30" width="8" height="2" rx="1" fill="#FFFFFF"/><rect x="8" y="36" width="10" height="2" rx="1" fill="#6B7280"/><rect x="20" y="36" width="12" height="2" rx="1" fill="#FFFFFF"/><rect x="8" y="42" width="10" height="2" rx="1" fill="#6B7280"/><rect x="20" y="42" width="10" height="2" rx="1" fill="#FFFFFF"/></svg>`)}`,

  faq: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><circle cx="24" cy="14" r="8" fill="#374151"/><rect x="22" y="10" width="4" height="5" rx="1" fill="#185C65"/><rect x="22" y="17" width="4" height="3" rx="1" fill="#185C65"/><rect x="6" y="28" width="36" height="1" rx="0.5" fill="#374151"/><rect x="8" y="32" width="20" height="2" rx="1" fill="#FFFFFF"/><path d="M38 31L41 35L35 35Z" fill="#185C65"/><rect x="6" y="38" width="36" height="1" rx="0.5" fill="#374151"/><rect x="8" y="42" width="16" height="2" rx="1" fill="#FFFFFF"/><path d="M38 41L41 45L35 45Z" fill="#185C65"/></svg>`)}`,

  rewards: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="14" y="14" width="20" height="20" rx="3" fill="#374151"/><rect x="14" y="14" width="20" height="6" rx="3" fill="#185C65"/><rect x="22" y="12" width="4" height="24" fill="#185C65" opacity="0.5"/><rect x="8" y="38" width="32" height="2" rx="1" fill="#6B7280"/><rect x="10" y="42" width="28" height="2" rx="1" fill="#6B7280"/><path d="M20 6L24 12L28 6" stroke="#185C65" stroke-width="2" fill="none"/></svg>`)}`,

  howItWorks: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><circle cx="10" cy="12" r="5" fill="#185C65"/><rect x="10" y="17" width="1" height="8" fill="#374151"/><circle cx="10" cy="30" r="5" fill="#185C65"/><rect x="10" y="35" width="1" height="8" fill="#374151"/><rect x="18" y="10" width="24" height="2" rx="1" fill="#FFFFFF"/><rect x="18" y="14" width="18" height="1" rx="0.5" fill="#6B7280"/><rect x="18" y="28" width="24" height="2" rx="1" fill="#FFFFFF"/><rect x="18" y="32" width="18" height="1" rx="0.5" fill="#6B7280"/><circle cx="10" cy="47" r="3" fill="#374151"/></svg>`)}`,

  pitchDeck: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="10" y="4" width="28" height="40" rx="2" fill="#374151"/><rect x="14" y="8" width="12" height="3" rx="1" fill="#FFFFFF"/><rect x="14" y="14" width="20" height="1" rx="0.5" fill="#6B7280"/><rect x="14" y="17" width="18" height="1" rx="0.5" fill="#6B7280"/><rect x="14" y="20" width="20" height="1" rx="0.5" fill="#6B7280"/><rect x="14" y="26" width="10" height="2" rx="1" fill="#185C65"/><rect x="14" y="30" width="20" height="1" rx="0.5" fill="#6B7280"/><rect x="14" y="33" width="16" height="1" rx="0.5" fill="#6B7280"/><rect x="14" y="36" width="18" height="1" rx="0.5" fill="#6B7280"/></svg>`)}`,

  updates: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><path d="M24 6C18 6 14 10 14 14V22L10 26V28H38V26L34 22V14C34 10 30 6 24 6Z" fill="#185C65"/><rect x="20" y="30" width="8" height="3" rx="1.5" fill="#185C65"/><rect x="6" y="36" width="36" height="2" rx="1" fill="#FFFFFF"/><rect x="8" y="40" width="32" height="1" rx="0.5" fill="#6B7280"/><rect x="8" y="43" width="28" height="1" rx="0.5" fill="#6B7280"/></svg>`)}`,

  video: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="10" width="36" height="28" rx="3" fill="#374151"/><path d="M20 18L32 24L20 30V18Z" fill="#FFFFFF"/></svg>`)}`,

  rti: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="12" y="6" width="24" height="3" rx="1" fill="#FFFFFF"/><rect x="16" y="12" width="16" height="2" rx="1" fill="#6B7280"/><circle cx="10" cy="22" r="4" fill="#185C65"/><path d="M8 22L10 24L13 20" stroke="#FFFFFF" stroke-width="1.5" fill="none"/><rect x="18" y="20" width="24" height="2" rx="1" fill="#FFFFFF"/><rect x="18" y="24" width="18" height="1" rx="0.5" fill="#6B7280"/><circle cx="10" cy="34" r="4" fill="#185C65"/><path d="M8 34L10 36L13 32" stroke="#FFFFFF" stroke-width="1.5" fill="none"/><rect x="18" y="32" width="24" height="2" rx="1" fill="#FFFFFF"/><rect x="18" y="36" width="16" height="1" rx="0.5" fill="#6B7280"/><circle cx="10" cy="46" r="4" fill="#185C65" opacity="0.3"/></svg>`)}`,

  emailCapture: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="10" y="6" width="28" height="4" rx="1" fill="#FFFFFF"/><rect x="14" y="12" width="20" height="2" rx="1" fill="#6B7280"/><path d="M6 22L24 30L42 22V38C42 39 41 40 40 40H8C7 40 6 39 6 38V22Z" fill="#374151"/><path d="M6 22L24 30L42 22" stroke="#185C65" stroke-width="2" fill="none"/><rect x="14" y="42" width="20" height="4" rx="2" fill="#185C65"/></svg>`)}`,

  bubble: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="16" y="8" width="28" height="24" rx="4" fill="#185C65"/><path d="M20 32L16 38L26 32" fill="#185C65"/><rect x="20" y="14" width="20" height="2" rx="1" fill="#FFFFFF"/><rect x="20" y="19" width="16" height="2" rx="1" fill="rgba(255,255,255,0.5)"/><rect x="20" y="24" width="12" height="4" rx="2" fill="#FFFFFF"/></svg>`)}`,

  whySE: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><path d="M24 6L27 16H38L29 22L32 32L24 26L16 32L19 22L10 16H21Z" fill="#185C65"/><rect x="8" y="38" width="32" height="2" rx="1" fill="#FFFFFF"/><rect x="10" y="42" width="28" height="1" rx="0.5" fill="#6B7280"/></svg>`)}`,

  link: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="16" width="36" height="16" rx="4" fill="#374151" stroke="#185C65" stroke-width="1"/><path d="M18 24H30" stroke="#185C65" stroke-width="2"/><circle cx="16" cy="24" r="4" stroke="#185C65" stroke-width="2" fill="none"/><circle cx="32" cy="24" r="4" stroke="#185C65" stroke-width="2" fill="none"/></svg>`)}`,
  media: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="8" width="36" height="26" rx="3" fill="#374151"/><circle cx="15" cy="17" r="3" fill="#185C65"/><path d="M6 28L16 20L24 26L32 18L42 26V31A3 3 0 0139 34H9A3 3 0 016 31V28Z" fill="#185C65" opacity="0.6"/><rect x="6" y="38" width="20" height="2" rx="1" fill="#6B7280"/></svg>`)}`,

  referenceList: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="8" width="14" height="3" rx="1" fill="#FFFFFF"/><rect x="6" y="16" width="6" height="6" rx="1" fill="#185C65"/><rect x="14" y="16" width="20" height="2" rx="1" fill="#FFFFFF"/><rect x="14" y="20" width="28" height="2" rx="1" fill="#6B7280"/><rect x="6" y="26" width="6" height="6" rx="1" fill="#185C65"/><rect x="14" y="26" width="22" height="2" rx="1" fill="#FFFFFF"/><rect x="14" y="30" width="26" height="2" rx="1" fill="#6B7280"/><rect x="6" y="36" width="6" height="6" rx="1" fill="#185C65"/><rect x="14" y="36" width="18" height="2" rx="1" fill="#FFFFFF"/><rect x="14" y="40" width="24" height="2" rx="1" fill="#6B7280"/></svg>`)}`,

  table: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="6" y="8" width="36" height="6" rx="1" fill="#185C65"/><rect x="8" y="10" width="8" height="2" rx="0.5" fill="#FFFFFF"/><rect x="18" y="10" width="6" height="2" rx="0.5" fill="#FFFFFF"/><rect x="26" y="10" width="6" height="2" rx="0.5" fill="#FFFFFF"/><rect x="34" y="10" width="6" height="2" rx="0.5" fill="#FFFFFF"/><rect x="6" y="16" width="36" height="1" fill="#374151"/><rect x="8" y="19" width="8" height="2" rx="0.5" fill="#6B7280"/><rect x="18" y="19" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="26" y="19" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="34" y="19" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="6" y="24" width="36" height="1" fill="#374151"/><rect x="8" y="27" width="8" height="2" rx="0.5" fill="#6B7280"/><rect x="18" y="27" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="26" y="27" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="34" y="27" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="6" y="32" width="36" height="1" fill="#374151"/><rect x="8" y="35" width="8" height="2" rx="0.5" fill="#6B7280"/><rect x="18" y="35" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="26" y="35" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="34" y="35" width="6" height="2" rx="0.5" fill="#6B7280"/><rect x="6" y="40" width="36" height="1" fill="#374151"/></svg>`)}`,

  disclaimer: `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="4" fill="#1A1A2E"/><rect x="8" y="10" width="32" height="20" rx="2" fill="#374151"/><rect x="11" y="14" width="20" height="2" rx="1" fill="#6B7280"/><rect x="11" y="18" width="26" height="2" rx="1" fill="#6B7280"/><rect x="11" y="22" width="22" height="2" rx="1" fill="#6B7280"/><rect x="6" y="36" width="36" height="2" rx="1" fill="#185C65"/><text x="9" y="44" font-size="6" font-style="italic" fill="#FFFFFF" font-family="serif">* disclaimer</text></svg>`)}`,
};

// ============================================================
// SHARED FONT INPUTS (for all components)
// ============================================================

/**
 * Available Adobe Fonts / Platform Fonts for Builder.io components
 * These fonts are loaded via Adobe Typekit
 */
const ADOBE_FONT_OPTIONS = [
  { value: 'owners', label: 'Owners' },
  { value: 'owners-wide', label: 'Owners Wide' },
  { value: 'owners-text', label: 'Owners Text' },
  { value: 'owners-narrow', label: 'Owners Narrow' },
  { value: 'ibm-plex-sans', label: 'IBM Plex Sans' },
];

/**
 * Popular Google Fonts for Builder.io components
 * Curated list of commonly used fonts from fonts.google.com
 */
const GOOGLE_FONT_OPTIONS = [
  // Display / Headline Fonts
  { value: 'Playfair Display', label: 'Playfair Display (Serif)' },
  { value: 'Merriweather', label: 'Merriweather (Serif)' },
  { value: 'Lora', label: 'Lora (Serif)' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond (Serif)' },
  { value: 'Crimson Text', label: 'Crimson Text (Serif)' },
  // Sans-Serif Fonts
  { value: 'Montserrat', label: 'Montserrat (Sans)' },
  { value: 'Open Sans', label: 'Open Sans (Sans)' },
  { value: 'Lato', label: 'Lato (Sans)' },
  { value: 'Roboto', label: 'Roboto (Sans)' },
  { value: 'Poppins', label: 'Poppins (Sans)' },
  { value: 'Inter', label: 'Inter (Sans)' },
  { value: 'Nunito', label: 'Nunito (Sans)' },
  { value: 'Raleway', label: 'Raleway (Sans)' },
  { value: 'Work Sans', label: 'Work Sans (Sans)' },
  { value: 'DM Sans', label: 'DM Sans (Sans)' },
  { value: 'Space Grotesk', label: 'Space Grotesk (Sans)' },
  { value: 'Outfit', label: 'Outfit (Sans)' },
  // Geometric / Modern
  { value: 'Oswald', label: 'Oswald (Condensed)' },
  { value: 'Bebas Neue', label: 'Bebas Neue (Display)' },
  { value: 'Anton', label: 'Anton (Display)' },
  // Custom option for any font not listed
  { value: 'custom', label: '✏️ Custom (Type font name)' },
];

// Title font inputs - place right after title content
const TITLE_FONT_INPUTS = [
  {
    name: 'titleFontSource',
    friendlyName: 'Title Font',
    type: 'enum',
    enum: ['default', 'adobeFonts', 'googleFonts', 'customUpload'],
    enumLabels: ['Platform Default', 'Adobe/Platform Fonts', 'Google Fonts', 'Upload Custom'],
    defaultValue: 'default',
  },
  {
    name: 'titleAdobeFontName',
    friendlyName: 'Select Adobe Font',
    type: 'enum',
    enum: ADOBE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: ADOBE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'owners-wide',
    showIf: 'options.get("titleFontSource") === "adobeFonts"',
  },
  {
    name: 'titleGoogleFontName',
    friendlyName: 'Select Google Font',
    type: 'enum',
    enum: GOOGLE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: GOOGLE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'Playfair Display',
    showIf: 'options.get("titleFontSource") === "googleFonts"',
  },
  {
    name: 'titleGoogleFontCustom',
    friendlyName: 'Custom Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Type exact font name from fonts.google.com',
    showIf:
      'options.get("titleFontSource") === "googleFonts" && options.get("titleGoogleFontName") === "custom"',
  },
  {
    name: 'titleGoogleFontWeights',
    friendlyName: 'Font Weights',
    type: 'string',
    defaultValue: '400;500;700',
    showIf: 'options.get("titleFontSource") === "googleFonts"',
    advanced: true,
  },
  {
    name: 'titleCustomFontUrl',
    friendlyName: 'Upload Font File',
    type: 'file',
    allowedFileTypes: ['woff2', 'woff', 'ttf', 'otf'],
    helperText: '.woff2 recommended',
    showIf: 'options.get("titleFontSource") === "customUpload"',
  },
  {
    name: 'titleCustomFontFamily',
    friendlyName: 'Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Name for your font (e.g., "HW Cigars")',
    showIf: 'options.get("titleFontSource") === "customUpload"',
  },
];

// Body font inputs - place right after body/subtitle content
const BODY_FONT_INPUTS = [
  {
    name: 'bodyFontSource',
    friendlyName: 'Body Font',
    type: 'enum',
    enum: ['default', 'adobeFonts', 'googleFonts', 'customUpload'],
    enumLabels: ['Platform Default', 'Adobe/Platform Fonts', 'Google Fonts', 'Upload Custom'],
    defaultValue: 'default',
  },
  {
    name: 'bodyAdobeFontName',
    friendlyName: 'Select Adobe Font',
    type: 'enum',
    enum: ADOBE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: ADOBE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'owners-text',
    showIf: 'options.get("bodyFontSource") === "adobeFonts"',
  },
  {
    name: 'bodyGoogleFontName',
    friendlyName: 'Select Google Font',
    type: 'enum',
    enum: GOOGLE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: GOOGLE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'Open Sans',
    showIf: 'options.get("bodyFontSource") === "googleFonts"',
  },
  {
    name: 'bodyGoogleFontCustom',
    friendlyName: 'Custom Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Type exact font name from fonts.google.com',
    showIf:
      'options.get("bodyFontSource") === "googleFonts" && options.get("bodyGoogleFontName") === "custom"',
  },
  {
    name: 'bodyGoogleFontWeights',
    friendlyName: 'Font Weights',
    type: 'string',
    defaultValue: '400;500;700',
    showIf: 'options.get("bodyFontSource") === "googleFonts"',
    advanced: true,
  },
  {
    name: 'bodyCustomFontUrl',
    friendlyName: 'Upload Font File',
    type: 'file',
    allowedFileTypes: ['woff2', 'woff', 'ttf', 'otf'],
    helperText: '.woff2 recommended',
    showIf: 'options.get("bodyFontSource") === "customUpload"',
  },
  {
    name: 'bodyCustomFontFamily',
    friendlyName: 'Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Name for your font',
    showIf: 'options.get("bodyFontSource") === "customUpload"',
  },
];

// Single font inputs for simple components (like TopNav) that only need one font
const SINGLE_FONT_INPUTS = [
  {
    name: 'fontSource',
    friendlyName: '📝 Font',
    type: 'enum',
    enum: ['default', 'adobeFonts', 'googleFonts', 'customUpload'],
    enumLabels: ['Platform Default', 'Adobe/Platform Fonts', 'Google Fonts', 'Upload Custom'],
    defaultValue: 'default',
  },
  {
    name: 'adobeFontName',
    friendlyName: 'Select Adobe Font',
    type: 'enum',
    enum: ADOBE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: ADOBE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'owners-text',
    showIf: 'options.get("fontSource") === "adobeFonts"',
  },
  {
    name: 'googleFontName',
    friendlyName: 'Select Google Font',
    type: 'enum',
    enum: GOOGLE_FONT_OPTIONS.map((f) => f.value),
    enumLabels: GOOGLE_FONT_OPTIONS.map((f) => f.label),
    defaultValue: 'Open Sans',
    showIf: 'options.get("fontSource") === "googleFonts"',
  },
  {
    name: 'googleFontCustom',
    friendlyName: 'Custom Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Type exact font name from fonts.google.com',
    showIf:
      'options.get("fontSource") === "googleFonts" && options.get("googleFontName") === "custom"',
  },
  {
    name: 'googleFontWeights',
    friendlyName: 'Font Weights',
    type: 'string',
    defaultValue: '400;500;700',
    showIf: 'options.get("fontSource") === "googleFonts"',
    advanced: true,
  },
  {
    name: 'customFontUrl',
    friendlyName: 'Upload Font File',
    type: 'file',
    allowedFileTypes: ['woff2', 'woff', 'ttf', 'otf'],
    helperText: '.woff2 recommended',
    showIf: 'options.get("fontSource") === "customUpload"',
  },
  {
    name: 'customFontFamily',
    friendlyName: 'Font Name',
    type: 'string',
    defaultValue: '',
    helperText: 'Name for your font',
    showIf: 'options.get("fontSource") === "customUpload"',
  },
];

const BACKGROUND_GRADIENT_INPUTS = [
  {
    name: 'backgroundGradientEnabled',
    friendlyName: 'Use Background Gradient',
    type: 'boolean',
    defaultValue: false,
  },
  {
    name: 'backgroundGradientType',
    friendlyName: 'Gradient Type',
    type: 'enum',
    enum: ['linear', 'radial'],
    defaultValue: 'linear',
    showIf: 'options.get("backgroundGradientEnabled")',
  },
  {
    name: 'backgroundGradientDirection',
    friendlyName: 'Gradient Direction',
    type: 'enum',
    enum: [
      'to bottom',
      'to right',
      'to bottom right',
      'to top',
      'to left',
      'to top right',
      'to top left',
      'to bottom left',
      'custom-angle',
    ],
    defaultValue: 'to bottom',
    showIf:
      'options.get("backgroundGradientEnabled") && options.get("backgroundGradientType") === "linear"',
  },
  {
    name: 'backgroundGradientAngle',
    friendlyName: 'Gradient Angle',
    type: 'number',
    defaultValue: 180,
    showIf:
      'options.get("backgroundGradientEnabled") && options.get("backgroundGradientType") === "linear" && options.get("backgroundGradientDirection") === "custom-angle"',
  },
  {
    name: 'backgroundGradientStops',
    friendlyName: 'Gradient Stops',
    type: 'list',
    showIf: 'options.get("backgroundGradientEnabled")',
    subFields: [
      { name: 'id', type: 'string', required: true },
      { name: 'color', type: 'color', required: true },
      { name: 'position', type: 'number', helperText: '0-100 (percent)' },
    ],
    defaultValue: [
      { id: 'stop-1', color: '#0a0a1a', position: 0 },
      { id: 'stop-2', color: '#185C65', position: 100 },
    ],
  },
  {
    name: 'backgroundGradient',
    friendlyName: 'Background Gradient (CSS)',
    type: 'string',
    helperText: 'Optional override (e.g., linear-gradient(...))',
    advanced: true,
  },
];

const getSectionBackgroundInputs = ({
  includeMobile = false,
  overlayDefault = 'rgba(0,0,0,0.5)',
  overlayHelperText = 'Dark overlay for text readability',
  backgroundColorHelperText = 'Transparent inherits page background',
  overlayShowIf,
} = {}) => {
  const showIfValue =
    overlayShowIf ||
    (includeMobile
      ? 'options.get("backgroundImage") || options.get("backgroundImageMobile")'
      : 'options.get("backgroundImage")');

  const inputs = [
    {
      name: 'backgroundColor',
      friendlyName: '🖼️ Background Color',
      type: 'color',
      defaultValue: THEME.transparent,
      helperText: backgroundColorHelperText,
    },
    ...BACKGROUND_GRADIENT_INPUTS,
    {
      name: 'backgroundImage',
      friendlyName: 'Background Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
    },
  ];

  if (includeMobile) {
    inputs.push({
      name: 'backgroundImageMobile',
      friendlyName: 'Background Image (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText: 'Optional: Shows on screens < 640px. Falls back to desktop if not set.',
      showIf: 'options.get("backgroundImage")',
    });
  }

  inputs.push({
    name: 'backgroundOverlayColor',
    friendlyName: 'Background Overlay',
    type: 'color',
    defaultValue: overlayDefault,
    helperText: overlayHelperText,
    showIf: showIfValue,
  });

  inputs.push(
    {
      name: 'backgroundSize',
      friendlyName: '🔍 Background Size',
      type: 'string',
      enum: ['cover', 'contain', 'auto', '100% 100%'],
      defaultValue: 'cover',
      helperText:
        'cover = fill section (may crop), contain = fit fully (may leave gaps), 100% 100% = stretch.',
      showIf: showIfValue,
      advanced: true,
    },
    {
      name: 'backgroundPosition',
      friendlyName: '🎯 Background Position',
      type: 'string',
      enum: [
        'center',
        'top',
        'bottom',
        'left',
        'right',
        'top left',
        'top right',
        'bottom left',
        'bottom right',
      ],
      defaultValue: 'center',
      helperText: 'Which part of the image stays anchored when the section resizes.',
      showIf: showIfValue,
      advanced: true,
    },
  );

  return inputs;
};

const SECTION_BACKGROUND_INPUTS = getSectionBackgroundInputs();
const SECTION_BACKGROUND_INPUTS_WITH_MOBILE = getSectionBackgroundInputs({ includeMobile: true });

const SECTION_SPACING_INPUTS = [
  {
    name: 'verticalPadding',
    friendlyName: '↕️ Vertical Padding (px)',
    type: 'number',
    defaultValue: 40,
    helperText: 'Top and bottom padding in px (0 to remove)',
  },
  {
    name: 'horizontalPadding',
    friendlyName: '↔️ Horizontal Padding (px)',
    type: 'number',
    defaultValue: 24,
    helperText: 'Left and right padding in px',
  },
  {
    name: 'maxWidth',
    friendlyName: 'Max Content Width (px)',
    type: 'number',
    defaultValue: 1200,
    helperText: 'Maximum width of the inner content area',
    advanced: true,
  },
];

const BUTTON_SIZE_INPUTS = (prefix = 'investButton', showIfField = '') => {
  const showIf = showIfField ? { showIf: `options.get("${showIfField}")` } : {};
  return [
    {
      name: `${prefix}Width`,
      friendlyName: 'Button Width',
      type: 'string',
      defaultValue: 'auto',
      helperText: 'e.g. auto, 100%, 200px, 300px',
      ...showIf,
    },
    {
      name: `${prefix}Padding`,
      friendlyName: 'Button Padding',
      type: 'string',
      defaultValue: '12px 32px',
      helperText: 'CSS padding e.g. 12px 32px, 16px 48px',
      ...showIf,
    },
  ];
};

const BULLET_SPACING_INPUT = {
  name: 'bulletSpacing',
  friendlyName: '📝 Bullet/List Spacing',
  type: 'string',
  defaultValue: '8px',
  helperText: 'Space between list items. e.g. 4px, 8px, 12px, 16px',
  advanced: true,
};

const SECTION_INVEST_BUTTON_INPUTS = [
  {
    name: 'showInvestButton',
    friendlyName: '💰 Show Invest Button',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Add a "Get Shares Now" button at the bottom of this section',
  },
  {
    name: 'investButtonText',
    friendlyName: 'Invest Button Label',
    type: 'string',
    defaultValue: 'Get Shares Now',
    helperText:
      'Auto-resolves: "Get Equity" for Reg D, "Get Shares Now" for CF/A. Override here if needed.',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonSpacing',
    friendlyName: 'Invest Button Spacing Above',
    type: 'string',
    defaultValue: '32px',
    helperText: 'Space above the button (e.g. 16px, 32px, 48px)',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonBackgroundColor',
    friendlyName: 'Invest Button Background',
    type: 'color',
    defaultValue: THEME.ctaButton,
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonTextColor',
    friendlyName: 'Invest Button Text',
    type: 'color',
    defaultValue: '#FFFFFF',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonBorderRadius',
    friendlyName: 'Invest Button Radius',
    type: 'string',
    defaultValue: '16px',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonWidth',
    friendlyName: 'Invest Button Width',
    type: 'string',
    defaultValue: '100%',
    helperText: 'e.g. auto, 100%, 200px, 327px',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonMaxWidth',
    friendlyName: 'Invest Button Max Width',
    type: 'string',
    defaultValue: '327px',
    helperText: 'Max width for the button',
    showIf: 'options.get("showInvestButton")',
  },
  {
    name: 'investButtonPadding',
    friendlyName: 'Invest Button Padding',
    type: 'string',
    defaultValue: '12px 24px',
    helperText: 'CSS padding e.g. 12px 24px',
    showIf: 'options.get("showInvestButton")',
  },
];

// ============================================================
// IMAGE OVERRIDE - next/image backed Builder.io Image block
// ============================================================
Builder.registerComponent(BuilderImage, {
  name: 'Image',
  override: true,
  inputs: [
    { name: 'image', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'gif', 'webp'] },
    { name: 'altText', type: 'string' },
    { name: 'width', type: 'number' },
    { name: 'height', type: 'number' },
    { name: 'fullWidth', type: 'boolean', defaultValue: true },
    { name: 'fitContent', type: 'boolean', defaultValue: true },
    { name: 'aspectRatio', type: 'number' },
    { name: 'backgroundSize', type: 'text', defaultValue: 'contain', enum: ['cover', 'contain'] },
    { name: 'lazy', type: 'boolean', defaultValue: true, advanced: true },
  ],
});

// ============================================================
// TOP NAV SECTION - Sticky navigation with watchlist and invest
// ============================================================
Builder.registerComponent(BuilderTopNavSection, {
  name: 'TopNavSection',
  description: 'Sticky navigation bar with logo, section links, and invest button',
  friendlyName: 'Page: Top Nav',
  image: ICONS.topNav,
  canHaveChildren: false,
  priority: 4,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Dynamic-data toggle, logo, nav links, action buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'useDynamicData',
      friendlyName: 'Use Dynamic Offering Data',
      type: 'boolean',
      defaultValue: true,
      helperText: 'If enabled, logo will be pulled from the offering API',
    },
    {
      name: 'showSELogo',
      friendlyName: '🚀 Use StartEngine Logo',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show the StartEngine logo instead of the offering logo',
    },
    {
      name: 'logoUrl',
      friendlyName: '🏢 Custom Logo Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      helperText: 'Upload a custom logo (only used when SE logo is off)',
      showIf: '!options.get("showSELogo")',
    },
    {
      name: 'showLogo',
      friendlyName: 'Show Logo',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'logoMaxHeight',
      friendlyName: 'Logo Max Height',
      type: 'string',
      defaultValue: '40px',
      showIf: 'options.get("showLogo")',
    },
    {
      name: 'logoLink',
      friendlyName: 'Logo Link URL',
      type: 'string',
      defaultValue: 'https://www.startengine.com',
      helperText: 'Where the logo links to when clicked (e.g. StartEngine homepage)',
    },
    {
      name: 'logoText',
      friendlyName: 'Logo Text',
      type: 'string',
      defaultValue: 'Company',
      helperText: 'Company name shown next to logo (hidden when SE logo is on)',
      showIf: '!options.get("showSELogo")',
    },
    {
      name: 'showLogoText',
      friendlyName: 'Show Logo Text',
      type: 'boolean',
      defaultValue: true,
      showIf: '!options.get("showSELogo")',
    },
    {
      name: 'logoTextColor',
      friendlyName: 'Logo Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: '!options.get("showSELogo")',
    },
    {
      name: 'navLinks',
      friendlyName: '🔗 Navigation Links',
      type: 'list',
      helperText: 'Add links that scroll to sections on the page',
      subFields: [
        {
          name: 'text',
          friendlyName: 'Link Text',
          type: 'string',
          defaultValue: 'Section',
        },
        {
          name: 'sectionId',
          friendlyName: 'Section ID',
          type: 'string',
          helperText: 'ID of the section to scroll to (e.g., "our-story")',
        },
        {
          name: 'url',
          friendlyName: 'External URL (optional)',
          type: 'string',
          helperText: 'Use this instead of Section ID for external links',
        },
      ],
      defaultValue: [
        { text: 'The Pitch', sectionId: 'the-pitch' },
        { text: 'Our Story', sectionId: 'our-story' },
        { text: 'Traction', sectionId: 'traction' },
        { text: 'Team', sectionId: 'team' },
        { text: 'Perks', sectionId: 'perks' },
      ],
    },
    {
      name: 'autoHideEmptyNavLinks',
      friendlyName: '🔄 Auto-Hide Empty Sections',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'Automatically hide nav links for sections with no content (e.g. Updates when there are no updates)',
    },
    {
      name: 'navLinkColor',
      friendlyName: 'Link Color',
      type: 'color',
      defaultValue: '#FCFBF8',
    },
    {
      name: 'navLinkHoverColor',
      friendlyName: 'Link Hover Color',
      type: 'color',
      defaultValue: 'rgba(252,251,248,0.7)',
    },
    {
      name: 'showWatchlistButton',
      friendlyName: '❤️ Show Watchlist Button',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'watchlistButtonStyle',
      friendlyName: 'Button Style',
      type: 'string',
      enum: ['outlined', 'contained', 'text'],
      defaultValue: 'outlined',
      showIf: 'options.get("showWatchlistButton")',
    },
    {
      name: 'watchlistButtonColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showWatchlistButton")',
    },
    {
      name: 'watchlistButtonBorderColor',
      friendlyName: 'Button Border Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showWatchlistButton")',
    },
    {
      name: 'watchlistActiveColor',
      friendlyName: 'Active (Following) Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showWatchlistButton")',
    },
    {
      name: 'showLoginButton',
      friendlyName: '🔑 Show Sign Up / Log In Button',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Orange CTA button shown to non-signed-in users (hidden when logged in)',
    },
    {
      name: 'loginButtonText',
      friendlyName: 'Button Text',
      type: 'string',
      defaultValue: 'Sign Up / Log In',
      showIf: 'options.get("showLoginButton")',
    },
    {
      name: 'loginButtonBackgroundColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: '#F47F2B',
      showIf: 'options.get("showLoginButton")',
    },
    {
      name: 'loginButtonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showLoginButton")',
    },
    {
      name: 'loginButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '30px',
      showIf: 'options.get("showLoginButton")',
    },
    {
      name: 'showInvestButton',
      friendlyName: '💰 Show Invest Button',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Orange "Get Shares Now" button in the nav bar',
    },
    {
      name: 'investButtonText',
      friendlyName: 'Invest Button Text',
      type: 'string',
      defaultValue: 'Get Shares Now',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'waitlistButtonText',
      friendlyName: '✍️ Waitlist Button Text',
      type: 'string',
      defaultValue: 'Join the Waitlist',
      helperText: 'Shown when offering is over-subscribed',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'reserveButtonText',
      friendlyName: '🟧 Reserve Button Text (TTW)',
      type: 'string',
      defaultValue: 'Reserve Now',
      helperText:
        'Shown when the offering is in TTW mode (auto-detected via status, or manually flipped via the toggle below).',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'forceReserveCta',
      friendlyName: '🟧 Force Reserve Now CTA (TTW Mode)',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Toggle ON to flip the TopNav invest button to "Reserve Now" + /reserve URL, regardless of offering status. Use for Reg A+ / Reg CF Test-The-Waters previews where the BE doesn\'t expose a TTW signal yet (e.g. gacw-reg-a, status `not_submitted`). Requires "Use Dynamic Offering Data" ON.',
      showIf: 'options.get("showInvestButton") && options.get("useDynamicData")',
    },
    {
      name: 'investButtonBackgroundColor',
      friendlyName: 'Invest Button Background',
      type: 'color',
      defaultValue: '#F47F2B',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'reserveButtonBackgroundColor',
      friendlyName: 'Reserve Button Background (TTW)',
      type: 'color',
      defaultValue: '#E06004',
      helperText: 'Background color used when the TTW (Reserve Now) CTA is active.',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'investButtonTextColor',
      friendlyName: 'Invest Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'investButtonBorderRadius',
      friendlyName: 'Invest Button Radius',
      type: 'string',
      defaultValue: '30px',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'showAccountButton',
      friendlyName: '👤 Show Account Button',
      type: 'boolean',
      defaultValue: true,
      helperText: 'User icon that opens login modal (logged out) or account dropdown (logged in)',
    },
    {
      name: 'accountButtonColor',
      friendlyName: 'Account Icon Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showAccountButton")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, height, sticky, mobile menu
    // ═══════════════════════════════════════════════════════════
    {
      name: 'backgroundColor',
      friendlyName: '🎨 Background Color',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'navHeight',
      friendlyName: 'Nav Height (px)',
      type: 'number',
      defaultValue: 64,
    },
    {
      name: 'isSticky',
      friendlyName: 'Fixed Navigation',
      type: 'boolean',
      defaultValue: true,
      helperText: 'When enabled, nav stays fixed at the top of the screen while scrolling',
    },
    {
      name: 'mobileMenuBackgroundColor',
      friendlyName: 'Mobile Menu Background',
      type: 'color',
      defaultValue: '#185C65',
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...SINGLE_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Nav-link scroll offset
    // ═══════════════════════════════════════════════════════════
    {
      name: 'scrollOffset',
      friendlyName: 'Scroll Offset (px)',
      type: 'number',
      defaultValue: 40,
      helperText:
        'Extra space above the section when scrolling via nav links (prevents content from being cut off)',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// HERO SECTION - Full-featured customizable hero
// ============================================================
Builder.registerComponent(BuilderHeroSection, {
  name: 'HeroSection',
  description: 'Full-featured hero with video, stats, invest button, and early bird callout',
  friendlyName: 'Story: Hero',
  image: ICONS.hero,
  canHaveChildren: false,
  priority: 5,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic-data, eyebrow, title, subtitle,
    // video, stats, invest button, price info, raised banner,
    // disclaimer, bullets, early bird callout
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'useDynamicData',
      friendlyName: 'Auto-Update from Offering',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Live data, OFF = Manual entry',
    },
    {
      name: 'forceReserveCta',
      friendlyName: '🟧 Force Reserve Now CTA (TTW Mode)',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Toggle ON to render the orange "Reserve Now" button + route to /reserve. Use for Reg A+ / Reg CF Test-The-Waters previews where the offering is `not_submitted` and the BE doesn\'t expose a TTW signal yet (e.g. gacw-reg-a). The four unambiguous TTW statuses (`testing`, `test_the_waters`, `pre_launch`, `ttw`) auto-flip without this toggle. Requires "Auto-Update from Offering" ON — the Hero needs the offering payload to compute the /reserve route.',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'backgroundColor',
      friendlyName: 'Hero Background Color',
      type: 'color',
      defaultValue: THEME.transparent,
      helperText:
        'Transparent inherits the background selected in Builder styles; choose a color here to override it.',
    },
    // ─── Eyebrow text ───────────────────────────────────────────
    {
      name: 'showEyebrow',
      friendlyName: '🏷️ Eyebrow Text',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'ON = Show eyebrow text above title, OFF = Hidden. Auto-generates "Invest in {Company}" when Auto-Update is ON and no custom text is entered.',
    },
    {
      name: 'eyebrowText',
      friendlyName: 'Custom Eyebrow Text',
      type: 'string',
      defaultValue: '',
      helperText: 'Leave empty to auto-generate "Invest in {Company Name}" from offering data',
      showIf: 'options.get("showEyebrow")',
    },
    {
      name: 'eyebrowColor',
      friendlyName: 'Eyebrow Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showEyebrow")',
    },
    {
      name: 'eyebrowFontSize',
      friendlyName: 'Eyebrow Font Size',
      type: 'string',
      defaultValue: '16px',
      showIf: 'options.get("showEyebrow")',
      advanced: true,
    },
    {
      name: 'eyebrowFontWeight',
      friendlyName: 'Eyebrow Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700],
      defaultValue: 400,
      showIf: 'options.get("showEyebrow")',
      advanced: true,
    },
    {
      name: 'eyebrowLetterSpacing',
      friendlyName: 'Eyebrow Letter Spacing',
      type: 'string',
      defaultValue: '2px',
      showIf: 'options.get("showEyebrow")',
      advanced: true,
    },
    {
      name: 'eyebrowTextTransform',
      friendlyName: 'Eyebrow Text Transform',
      type: 'enum',
      enum: ['none', 'uppercase', 'lowercase', 'capitalize'],
      defaultValue: 'none',
      showIf: 'options.get("showEyebrow")',
      advanced: true,
    },
    {
      name: 'eyebrowMarginBottom',
      friendlyName: 'Eyebrow Bottom Spacing (px)',
      type: 'number',
      defaultValue: 8,
      showIf: 'options.get("showEyebrow")',
      advanced: true,
    },
    // ═══════════════════════════════════════════════════════════
    // TITLE
    // ═══════════════════════════════════════════════════════════
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'longText',
      defaultValue: 'Invest in the Future',
      helperText: 'Main headline. Use | for line breaks.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    ...TITLE_FONT_INPUTS,
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '56px',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText:
        'Optional. Smaller size for narrow viewports (e.g. 36px). Leave blank to inherit desktop size. Use this when long words like "Autonomous" wrap awkwardly on Android.',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
    },
    {
      name: 'titleLineHeight',
      friendlyName: 'Title Line Height',
      type: 'string',
      defaultValue: '1.15',
      helperText: 'e.g. 1.15, 1.3, 1.5',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Highlight Words',
      type: 'string',
      defaultValue: '',
      helperText: 'Words to highlight in accent color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    // ═══════════════════════════════════════════════════════════
    // SUBTITLE
    // ═══════════════════════════════════════════════════════════
    {
      name: 'subtitle',
      friendlyName: '✍️ Subtitle',
      type: 'richText',
      defaultValue: 'Join thousands of investors backing innovative companies.',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: THEME.textMuted,
    },
    ...BODY_FONT_INPUTS,
    {
      name: 'subtitleFontSize',
      friendlyName: 'Subtitle Font Size',
      type: 'string',
      defaultValue: '18px',
    },
    {
      name: 'subtitleLineHeight',
      friendlyName: 'Subtitle Line Height',
      type: 'string',
      defaultValue: '1.6',
      helperText: 'e.g. 1.4, 1.6, 1.8',
    },
    // ═══════════════════════════════════════════════════════════
    // VIDEO / HERO IMAGE
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showVideo',
      friendlyName: '🎬 Show Video',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'showHeroImage',
      friendlyName: '🖼️ Show Hero Image',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'vimeoUrl',
      friendlyName: 'Vimeo Video ID',
      type: 'string',
      helperText:
        'Auto-fills from offering data when empty. Override with a Vimeo ID or URL (e.g., 1158518997)',
      showIf: 'options.get("showVideo")',
    },
    {
      name: 'videoThumbnail',
      friendlyName: 'Hero Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText: 'Upload a hero image (used when no Vimeo ID is set)',
      showIf: 'options.get("showHeroImage")',
    },
    {
      name: 'useDynamicHeroImage',
      friendlyName: 'Use Dynamic Hero Image',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Uses coverDesktopUrl when no Vimeo URL is set',
      showIf: 'options.get("showHeroImage")',
    },
    {
      name: 'videoBackgroundMode',
      friendlyName: 'Autoplay Mode',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Autoplay muted loop (unmute button appears)',
      showIf: 'options.get("showVideo") && options.get("vimeoUrl")',
    },
    {
      name: 'showUnmuteButton',
      friendlyName: 'Show Unmute Button',
      type: 'boolean',
      defaultValue: true,
      showIf:
        'options.get("showVideo") && options.get("vimeoUrl") && options.get("videoBackgroundMode")',
    },
    {
      name: 'unmuteButtonPosition',
      friendlyName: 'Unmute Button Position',
      type: 'enum',
      enum: ['center', 'top-right', 'top-left', 'bottom-right', 'bottom-left'],
      defaultValue: 'center',
      showIf:
        'options.get("showVideo") && options.get("vimeoUrl") && options.get("videoBackgroundMode")',
    },
    {
      name: 'videoBorderRadius',
      friendlyName: 'Video Border Radius',
      type: 'string',
      defaultValue: '16px',
      showIf: 'options.get("showVideo") || options.get("showHeroImage")',
    },
    {
      name: 'videoFrameWidth',
      friendlyName: 'Media Frame Width',
      type: 'string',
      defaultValue: '12px',
      helperText:
        'White "polaroid" frame around the hero media. Set to "0px" for edge-to-edge media that fills the entire wrapper. Default 12px matches the existing campaign-page look.',
      showIf: 'options.get("showVideo") || options.get("showHeroImage")',
    },
    {
      name: 'videoFrameColor',
      friendlyName: 'Media Frame Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      helperText:
        'Color of the frame around the hero media. Ignored when Media Frame Width is "0px".',
      showIf:
        '(options.get("showVideo") || options.get("showHeroImage")) && options.get("videoFrameWidth") !== "0px"',
    },
    {
      name: 'videoFrameShadow',
      friendlyName: 'Media Frame Shadow',
      type: 'string',
      defaultValue: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      helperText:
        'Drop shadow under the hero media wrapper. Set to "none" to remove. Standard CSS box-shadow value.',
      showIf: 'options.get("showVideo") || options.get("showHeroImage")',
      advanced: true,
    },
    {
      name: 'mediaWidthPercent',
      friendlyName: 'Media Width %',
      type: 'number',
      defaultValue: 50,
      helperText:
        'Width of the video/image as a percentage of the hero (30–70). 50 = balanced, 60 = larger media.',
      showIf: 'options.get("showVideo") || options.get("showHeroImage")',
    },
    // ═══════════════════════════════════════════════════════════
    // STATS GRID (below image)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showStatsGrid',
      friendlyName: '📊 Show Stats Grid',
      type: 'boolean',
      defaultValue: true,
      helperText: '2x2 grid below the hero image showing offering stats',
    },
    {
      name: 'statsGridItems',
      friendlyName: 'Stats Items',
      type: 'list',
      showIf: 'options.get("showStatsGrid")',
      helperText:
        'Each stat pulls from offering data if dataKey is set, otherwise uses the manual value',
      subFields: [
        {
          name: 'label',
          friendlyName: 'Label',
          type: 'string',
          defaultValue: 'Stat Label',
        },
        {
          name: 'value',
          friendlyName: 'Manual Value',
          type: 'string',
          defaultValue: '$XXX',
          helperText: 'Used when data is not available or dataKey is "none"',
        },
        {
          name: 'dataKey',
          friendlyName: 'Data Source',
          type: 'enum',
          enum: [
            'none',
            'previouslyCrowdfunded',
            'currentRaise',
            'investorCount',
            'valuation',
            'minInvestment',
            'fundingGoal',
          ],
          enumLabels: [
            'Manual (use value above)',
            'Previously Crowdfunded',
            'Amount Raised',
            'Investor Count',
            'Valuation',
            'Min Investment',
            'Funding Goal',
          ],
          defaultValue: 'none',
        },
        {
          name: 'hideWhenEmpty',
          friendlyName: 'Hide When Below $124,000',
          type: 'boolean',
          defaultValue: false,
          helperText:
            'Auto-hide this stat. Amount Raised hides when below $124,000. Other stats hide when no data.',
        },
      ],
      defaultValue: [
        {
          label: 'Previously Crowdfunded',
          value: '$XXX',
          dataKey: 'previouslyCrowdfunded',
          hideWhenEmpty: true,
        },
        { label: 'Amount Raised', value: '$XXX', dataKey: 'currentRaise', hideWhenEmpty: true },
        { label: 'Investors', value: 'XXX', dataKey: 'investorCount', hideWhenEmpty: true },
        {
          label: 'Minimum Investment',
          value: '$XXX',
          dataKey: 'minInvestment',
          hideWhenEmpty: true,
        },
        { label: 'Funding Goal', value: '$XXX', dataKey: 'fundingGoal', hideWhenEmpty: true },
      ],
    },
    {
      name: 'statsGridBackgroundColor',
      friendlyName: 'Stats Card Background',
      type: 'color',
      defaultValue: 'rgba(24,92,101,0.06)',
      showIf: 'options.get("showStatsGrid")',
    },
    {
      name: 'statsGridBorderColor',
      friendlyName: 'Stats Border Color',
      type: 'color',
      defaultValue: 'transparent',
      showIf: 'options.get("showStatsGrid")',
    },
    {
      name: 'statsGridLabelColor',
      friendlyName: 'Stats Label Color',
      type: 'color',
      defaultValue: 'rgba(0,0,0,0.5)',
      showIf: 'options.get("showStatsGrid")',
    },
    {
      name: 'statsGridValueColor',
      friendlyName: 'Stats Value Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("showStatsGrid")',
    },
    // ═══════════════════════════════════════════════════════════
    // INVEST BUTTON
    // ═══════════════════════════════════════════════════════════
    {
      name: 'investButtonText',
      friendlyName: '🔘 Button Text',
      type: 'string',
      defaultValue: 'Get Shares Now',
    },
    {
      name: 'waitlistButtonText',
      friendlyName: '✍️ Waitlist Button Text',
      type: 'string',
      defaultValue: 'Join the Waitlist',
      helperText: 'Shown when offering is over-subscribed',
    },
    {
      name: 'investButtonBackgroundColor',
      friendlyName: 'Button Color',
      type: 'color',
      defaultValue: '#F47F2B',
    },
    {
      name: 'reserveButtonBackgroundColor',
      friendlyName: 'Reserve Now Button Color (TTW)',
      type: 'color',
      defaultValue: '#E06004',
      helperText:
        'Used automatically when the offering is in Test-the-Waters status. Overrides Button Color so TTW pages match the sidebar Reserve Now styling.',
    },
    {
      name: 'investButtonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: THEME.ctaButtonText,
    },
    {
      name: 'investButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '30px',
    },
    {
      name: 'investButtonWidth',
      friendlyName: 'Button Width',
      type: 'string',
      defaultValue: 'auto',
      helperText: 'e.g. auto, 100%, 287.5px',
    },
    {
      name: 'investButtonFontSize',
      friendlyName: 'Button Font Size',
      type: 'string',
      defaultValue: '16px',
    },
    {
      name: 'investButtonFontWeight',
      friendlyName: 'Button Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700],
      defaultValue: 600,
    },
    ...BUTTON_SIZE_INPUTS('investButton'),
    // ═══════════════════════════════════════════════════════════
    // PRICE INFO
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showPriceInfo',
      friendlyName: '💰 Show Price Info',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show Share Price and Min Investment',
    },
    {
      name: 'showSharePrice',
      friendlyName: 'Show Share Price',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showPriceInfo")',
    },
    {
      name: 'sharePriceLabel',
      friendlyName: 'Share Price Label',
      type: 'string',
      defaultValue: 'Share Price',
      showIf: 'options.get("showPriceInfo") && options.get("showSharePrice")',
    },
    {
      name: 'manualSharePrice',
      friendlyName: 'Share Price (Manual)',
      type: 'string',
      defaultValue: '$10.00',
      showIf:
        'options.get("showPriceInfo") && options.get("showSharePrice") && !options.get("useDynamicData")',
    },
    {
      name: 'showMinInvestment',
      friendlyName: 'Show Min Investment',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showPriceInfo")',
    },
    {
      name: 'minInvestmentLabel',
      friendlyName: 'Min Investment Label',
      type: 'string',
      defaultValue: 'Min Investment',
      showIf: 'options.get("showPriceInfo") && options.get("showMinInvestment")',
    },
    {
      name: 'manualMinInvestment',
      friendlyName: 'Min Investment (Manual)',
      type: 'string',
      defaultValue: '$100',
      showIf:
        'options.get("showPriceInfo") && options.get("showMinInvestment") && !options.get("useDynamicData")',
    },
    {
      name: 'priceInfoColor',
      friendlyName: 'Price Info Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showPriceInfo")',
    },
    // ═══════════════════════════════════════════════════════════
    // RAISED BANNER (Optional Feature)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showRaisedBanner',
      friendlyName: '📢 Show Raised Banner',
      type: 'boolean',
      defaultValue: true,
      helperText: '"$X Raised" banner at top',
    },
    {
      name: 'useDynamicRaisedAmount',
      friendlyName: 'Auto-Update Amount',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Live data, OFF = Manual entry',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerSuffixText',
      friendlyName: 'Banner Suffix',
      type: 'string',
      defaultValue: 'Raised',
      showIf: 'options.get("showRaisedBanner") && options.get("useDynamicRaisedAmount")',
    },
    {
      name: 'raisedBannerManualText',
      friendlyName: 'Banner Text (Manual)',
      type: 'string',
      defaultValue: '$XXX,XXX,XXX Raised',
      showIf: 'options.get("showRaisedBanner") && !options.get("useDynamicRaisedAmount")',
    },
    {
      name: 'raisedBannerBackgroundColor',
      friendlyName: 'Banner Background',
      type: 'color',
      defaultValue: '#185C65',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerTextColor',
      friendlyName: 'Banner Text Color',
      type: 'color',
      defaultValue: THEME.textLight,
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerGradient',
      friendlyName: 'Banner Gradient',
      type: 'string',
      defaultValue: '',
      helperText:
        'CSS gradient e.g. linear-gradient(90deg, #185C65, #2A8F9C). Overrides background color.',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerPadding',
      friendlyName: 'Banner Padding',
      type: 'string',
      defaultValue: '12px 32px 12px 24px',
      helperText: 'CSS padding e.g. 12px 32px, 16px 48px',
      showIf: 'options.get("showRaisedBanner")',
      advanced: true,
    },
    {
      name: 'raisedBannerMarginTop',
      friendlyName: '↕️ Banner Margin Top',
      type: 'string',
      defaultValue: '48px',
      helperText: 'Desktop spacing above the banner',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerMarginBottom',
      friendlyName: '↕️ Banner Margin Bottom',
      type: 'string',
      defaultValue: '48px',
      helperText: 'Desktop spacing below the banner',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerMarginTopMobile',
      friendlyName: '📱 Banner Margin Top (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText: 'Leave empty to use desktop value',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerMarginBottomMobile',
      friendlyName: '📱 Banner Margin Bottom (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText: 'Leave empty to use desktop value',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'raisedBannerFontSize',
      friendlyName: 'Banner Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 12px, 14px, 16px',
      showIf: 'options.get("showRaisedBanner")',
      advanced: true,
    },
    {
      name: 'raisedBannerFontWeight',
      friendlyName: 'Banner Font Weight',
      type: 'number',
      defaultValue: 600,
      helperText: '400 = normal, 600 = semi-bold, 700 = bold',
      showIf: 'options.get("showRaisedBanner")',
      advanced: true,
    },
    {
      name: 'raisedBannerLetterSpacing',
      friendlyName: 'Banner Letter Spacing',
      type: 'string',
      defaultValue: '0.02em',
      showIf: 'options.get("showRaisedBanner")',
      advanced: true,
    },
    {
      name: 'raisedBannerBorderRadius',
      friendlyName: 'Banner Border Radius',
      type: 'string',
      defaultValue: '0 999px 999px 0',
      helperText: 'e.g. 0 999px 999px 0 (pill right), 999px (full pill), 8px',
      showIf: 'options.get("showRaisedBanner")',
      advanced: true,
    },
    {
      name: 'raisedBannerPosition',
      friendlyName: '📍 Banner Position',
      type: 'enum',
      enum: ['top', 'belowMedia'],
      enumLabels: ['Top (above content)', 'Below Image/Video'],
      defaultValue: 'top',
      showIf: 'options.get("showRaisedBanner")',
    },
    {
      name: 'showStatusBadges',
      friendlyName: '🏷️ Show Status Badges',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Dynamic badges (Waitlist, TTW, Accredited/QP) near the top',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'manualStatusBadges',
      friendlyName: 'Status Badges (Manual)',
      type: 'list',
      showIf: '!options.get("useDynamicData") && options.get("showStatusBadges")',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'label',
          type: 'string',
          required: true,
          defaultValue: 'ACCREDITED INVESTOR ONLY',
        },
        {
          name: 'backgroundColor',
          type: 'color',
          defaultValue: 'rgba(24, 92, 101, 0.1)',
        },
        {
          name: 'textColor',
          type: 'color',
          defaultValue: '#185C65',
        },
      ],
      defaultValue: [],
    },
    // ═══════════════════════════════════════════════════════════
    // DISCLAIMER
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showDisclaimer',
      friendlyName: '⚖️ Show Disclaimer',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'disclaimerPrefix',
      friendlyName: 'Disclaimer Prefix',
      type: 'string',
      defaultValue: 'Offered pursuant to',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerLinkText',
      friendlyName: 'Disclaimer Link Text',
      type: 'string',
      defaultValue: 'Reg CF',
      helperText: 'e.g., "Reg CF" or "Reg A+"',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerLinkUrl',
      friendlyName: 'Disclaimer Link URL',
      type: 'url',
      defaultValue: 'https://www.sec.gov/education/smallbusiness/exemptofferings/regcrowdfunding',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textMuted,
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerLinkColor',
      friendlyName: 'Disclaimer Link Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'additionalDisclaimerText',
      friendlyName: 'Additional Disclaimer Text',
      type: 'richText',
      defaultValue: '',
      helperText:
        'Optional compliance-approved text displayed directly below the automatic offering disclaimer.',
      showIf: 'options.get("showDisclaimer")',
    },
    // ═══════════════════════════════════════════════════════════
    // BULLET POINTS (Optional)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showBullets',
      friendlyName: '• Bullet Points',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Show bullet points below subtitle',
    },
    {
      name: 'bulletItems',
      friendlyName: 'Bullet Items',
      type: 'list',
      showIf: 'options.get("showBullets")',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'icon',
          type: 'string',
          defaultValue: '▸',
          helperText: 'Icon (e.g., ▸, ★, •)',
        },
        {
          name: 'text',
          type: 'richText',
          required: true,
        },
      ],
      defaultValue: [],
    },
    {
      name: 'bulletIconColor',
      friendlyName: 'Bullet Icon Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showBullets")',
    },
    {
      name: 'bulletTextColor',
      friendlyName: 'Bullet Text Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showBullets")',
    },
    // ═══════════════════════════════════════════════════════════
    // EARLY BIRD CALLOUT
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showEarlyBird',
      friendlyName: '⏳ Show Early Bird Callout',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'Only appears on the live page when a Countdown Deadline is set. Auto-hides after deadline passes.',
    },
    {
      name: 'earlyBirdBadgeText',
      friendlyName: 'Badge Text',
      type: 'string',
      defaultValue: '⏳ Early Bird',
      showIf: 'options.get("showEarlyBird")',
    },
    {
      name: 'earlyBirdText',
      friendlyName: 'Callout Text',
      type: 'string',
      defaultValue: 'Invest now for',
      showIf: 'options.get("showEarlyBird")',
    },
    {
      name: 'earlyBirdHighlight',
      friendlyName: 'Highlighted Text',
      type: 'string',
      defaultValue: '25% Bonus Shares',
      helperText: 'Bold white text after the callout text',
      showIf: 'options.get("showEarlyBird")',
    },
    {
      name: 'earlyBirdDeadline',
      friendlyName: 'Countdown Deadline',
      type: 'date',
      helperText: 'Date/time for the countdown timer. Leave empty to hide countdown.',
      showIf: 'options.get("showEarlyBird")',
    },
    {
      name: 'earlyBirdBackground',
      friendlyName: 'Callout Background',
      type: 'string',
      defaultValue: 'linear-gradient(135deg, #185C65, #1a7a86)',
      showIf: 'options.get("showEarlyBird")',
    },
    {
      name: 'earlyBirdBadgeBackground',
      friendlyName: 'Badge Background',
      type: 'color',
      defaultValue: 'rgba(1,209,184,0.2)',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdBadgeColor',
      friendlyName: 'Badge Text Color',
      type: 'color',
      defaultValue: '#5ef5e0',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdTextColor',
      friendlyName: 'Text Color',
      type: 'color',
      defaultValue: 'rgba(255,255,255,0.75)',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdCountdownBackground',
      friendlyName: 'Countdown Background',
      type: 'color',
      defaultValue: 'rgba(255,255,255,0.12)',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdCountdownColor',
      friendlyName: 'Countdown Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showEarlyBird")',
      advanced: true,
    },
    {
      name: 'earlyBirdSortStrategy',
      friendlyName: 'Which Reward to Display',
      type: 'enum',
      enum: ['highestBonus', 'lowestMin', 'highestMin', 'soonestExpiring'],
      enumLabels: [
        'Highest Bonus %',
        'Lowest Min Investment',
        'Highest Min Investment',
        'Soonest Expiring',
      ],
      defaultValue: 'highestBonus',
      helperText: 'When multiple early bird rewards exist, which one to feature',
      showIf: 'options.get("showEarlyBird")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, content spacing, max-width
    // ═══════════════════════════════════════════════════════════
    ...getSectionBackgroundInputs({
      overlayDefault: 'rgba(11, 17, 33, 0.85)',
      overlayHelperText: 'Dark overlay on image',
    }),
    {
      name: 'contentTopSpacing',
      friendlyName: 'Hero Content Top Spacing',
      type: 'string',
      defaultValue: '0px',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Content Width',
      type: 'number',
      defaultValue: 1280,
    },
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Padding + gap micro-tuning
    // ═══════════════════════════════════════════════════════════
    {
      name: 'contentPadding',
      friendlyName: 'Content Padding',
      type: 'string',
      defaultValue: '8px 24px 64px',
      advanced: true,
    },
    {
      name: 'contentPaddingMobile',
      friendlyName: 'Content Padding (Mobile)',
      type: 'string',
      defaultValue: '16px 16px 32px',
      advanced: true,
    },
    {
      name: 'contentGapMobile',
      friendlyName: 'Content Gap (Mobile)',
      type: 'string',
      defaultValue: '20px',
      advanced: true,
    },
    {
      name: 'contentGapDesktop',
      friendlyName: 'Content Gap (Desktop)',
      type: 'string',
      defaultValue: '48px',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// SPLIT SECTION - Versatile text + media layout for story sections
// ============================================================
Builder.registerComponent(BuilderSplitSection, {
  name: 'SplitSection',
  description:
    'Two-column layout with text + image/video/chart. Great for Opportunity, About, or any story section.',
  friendlyName: 'Story: Text + Media',
  image: ICONS.split,
  canHaveChildren: false,
  noWrap: true,
  priority: 6,
  inputs: [
    {
      name: 'spacingContractVersion',
      type: 'number',
      defaultValue: 2,
      hideFromUI: true,
    },
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, media, disclaimer, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small uppercase label (e.g., "THE OPPORTUNITY")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'string',
      defaultValue: 'Our innovation has been recognized.',
      helperText: 'Full title text. Use | for line breaks.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: 'Our innovation',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 24px, 2rem, 1.5em (empty = default responsive sizes)',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 20px, 1.25rem (empty = default mobile size)',
    },
    {
      name: 'bodyText',
      friendlyName: '✍️ Body Text',
      type: 'richText',
      defaultValue:
        '<p>Learn more about this innovative company and the investment opportunity they are offering on StartEngine.</p>',
    },
    {
      name: 'bodyTextColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'bodyFontSize',
      friendlyName: 'Body Font Size',
      type: 'string',
      defaultValue: '1rem',
      helperText: 'e.g. 14px, 16px, 1rem, 1.125rem',
    },
    {
      name: 'mediaType',
      friendlyName: '🖼️ Media Type',
      type: 'string',
      enum: ['image', 'video'],
      defaultValue: 'image',
    },
    {
      name: 'mediaUrl',
      friendlyName: 'Media File',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg', 'mp4', 'webm'],
    },
    {
      name: 'videoUrl',
      friendlyName: '🎬 Video URL (alternate)',
      type: 'text',
      helperText:
        'Paste a direct video link (.mp4) instead of uploading. Used when media type is Video.',
      showIf: 'options.get("mediaType") === "video"',
    },
    {
      name: 'mediaUrlMobile',
      friendlyName: 'Media File (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg', 'mp4', 'webm'],
      helperText: 'Optional: Shows on screens < 640px. Falls back to desktop if not set.',
    },
    {
      name: 'mediaAlt',
      friendlyName: 'Alt Text',
      type: 'string',
      helperText: 'Accessibility description',
    },
    {
      name: 'disclaimer',
      friendlyName: '⚖️ Text Disclaimer',
      type: 'richText',
      helperText: 'Small text below body content',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Text Disclaimer Color',
      type: 'color',
      defaultValue: '#6B7280',
    },
    {
      name: 'mediaDisclaimer',
      friendlyName: '⚖️ Image Disclaimer',
      type: 'richText',
      defaultValue: '',
      helperText: 'Small text below the image',
    },
    {
      name: 'mediaDisclaimerColor',
      friendlyName: 'Image Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textMuted,
      showIf: 'options.get("mediaDisclaimer")',
    },
    {
      name: 'mediaDisclaimerFontSize',
      friendlyName: 'Image Disclaimer Size',
      type: 'string',
      enum: ['9px', '10px', '11px', '12px', '13px', '14px'],
      defaultValue: '11px',
      showIf: 'options.get("mediaDisclaimer")',
    },
    {
      name: 'mediaDisclaimerAlign',
      friendlyName: 'Image Disclaimer Align',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left',
      showIf: 'options.get("mediaDisclaimer")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Variant, alignment, media sizing,
    // background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'imagePosition',
      friendlyName: '🖼️ Image Position',
      type: 'string',
      enum: ['side', 'center', 'textOnly'],
      defaultValue: 'side',
      helperText: 'Side = two-column, Center = image above text, Text Only = no media',
    },
    {
      name: 'layout',
      friendlyName: '↔️ Side Layout',
      type: 'string',
      enum: ['textLeft', 'textRight'],
      defaultValue: 'textLeft',
      helperText: 'Which side the text goes on',
      showIf: 'options.get("imagePosition") === "side" || !options.get("imagePosition")',
    },
    {
      name: 'textAlign',
      friendlyName: '📝 Text Alignment',
      type: 'string',
      enum: ['left', 'center'],
      defaultValue: 'left',
      helperText: 'Align text content',
    },
    {
      name: 'mediaWidthPercent',
      friendlyName: '📐 Media Width %',
      type: 'number',
      defaultValue: 50,
      helperText:
        'Width of the media column as a percentage (30–70). 50 = equal split, 60 = larger media.',
      showIf: 'options.get("imagePosition") === "side" || !options.get("imagePosition")',
    },
    {
      name: 'verticalAlignment',
      friendlyName: '↕️ Vertical Alignment',
      type: 'enum',
      enum: ['top', 'center', 'bottom'],
      defaultValue: 'center',
      helperText:
        'Align text and media columns vertically. Center = media aligns to middle of text.',
      showIf: 'options.get("imagePosition") === "side" || !options.get("imagePosition")',
    },
    {
      name: 'mediaMaxWidth',
      friendlyName: 'Media Max Width',
      type: 'string',
      defaultValue: '',
      helperText: 'e.g., 300px, 400px, 50% (empty = full width)',
    },
    {
      name: 'mediaBoxShadow',
      friendlyName: 'Add Shadow',
      type: 'boolean',
      defaultValue: false,
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 24,
      helperText:
        'Left/right padding in px. Increase to push text inward (useful with background images).',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
    },
    {
      name: 'textMaxWidth',
      friendlyName: 'Text Max Width',
      type: 'string',
      defaultValue: '',
      helperText:
        'Limit text column width e.g. 500px, 50%. Useful when background image has blank space to fill.',
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Media radius + bullet spacing
    // ═══════════════════════════════════════════════════════════
    {
      name: 'mediaBorderRadius',
      friendlyName: 'Media Border Radius',
      type: 'string',
      defaultValue: '0px',
      advanced: true,
    },
    BULLET_SPACING_INPUT,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '40px',
    paddingRight: '24px',
    paddingBottom: '40px',
    paddingLeft: '24px',
  },
});

// ============================================================
// STATS SECTION - Side-by-side or centered stat cards with icons
// ============================================================
Builder.registerComponent(BuilderStatsSection, {
  name: 'StatsSection',
  description:
    'Stat cards with optional icons. Side-by-side (Problem) or centered grid (Traction) layout.',
  friendlyName: 'Story: Stats Grid',
  image: ICONS.stats,
  canHaveChildren: false,
  priority: 7,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, stats, footer copy, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: 'OUR TRACTION',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'string',
      defaultValue: 'Growing Momentum',
      helperText: 'Full title text. Use | for line breaks.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: 'Growing',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'subtitle',
      friendlyName: '✍️ Subtitle',
      type: 'richText',
      defaultValue: '',
      helperText: 'Centered paragraph below title (centered layout only)',
      showIf: 'options.get("displayLayout") === "centered"',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") === "centered"',
    },
    {
      name: 'bodyText',
      friendlyName: '✍️ Body Text',
      type: 'richText',
      defaultValue: '',
      showIf: 'options.get("displayLayout") !== "centered"',
    },
    {
      name: 'bodyTextColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") !== "centered"',
    },
    {
      name: 'stats',
      friendlyName: '📊 Stats / Cards',
      type: 'list',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'value',
          friendlyName: 'Title',
          type: 'richText',
          required: true,
          helperText: 'Bold heading (e.g., $2M+, Struggling, <1%). Supports hyperlinks.',
        },
        {
          name: 'valueSuperscript',
          friendlyName: 'Title Footnote #',
          type: 'string',
        },
        {
          name: 'label',
          friendlyName: 'Subtitle',
          type: 'richText',
          required: true,
          helperText: 'Description text below the title. Supports hyperlinks.',
        },
        {
          name: 'labelSuperscript',
          friendlyName: 'Subtitle Footnote #',
          type: 'string',
        },
        {
          name: 'iconUrl',
          friendlyName: 'Icon Image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          helperText: 'Optional icon/logo displayed above the title',
        },
        {
          name: 'iconLabel',
          friendlyName: 'Icon Label',
          type: 'string',
          helperText: 'Optional text displayed below the icon/logo',
        },
      ],
      defaultValue: [
        { id: '1', value: '$X.XM', label: 'Revenue' },
        { id: '2', value: 'XX,XXX', label: 'Customers' },
        { id: '3', value: 'XXX%', label: 'Growth Rate' },
        { id: '4', value: 'XX', label: 'Team Members' },
      ],
    },
    {
      name: 'footerTitle',
      friendlyName: '📝 Footer Heading',
      type: 'string',
      defaultValue: '',
      helperText: 'Optional heading below the stats grid',
      showIf: 'options.get("displayLayout") === "centered"',
    },
    {
      name: 'footerTitleColor',
      friendlyName: 'Footer Heading Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("displayLayout") === "centered" && options.get("footerTitle")',
    },
    {
      name: 'footerBody',
      friendlyName: '📝 Footer Text',
      type: 'richText',
      defaultValue: '',
      helperText: 'Optional paragraph below the footer heading',
      showIf: 'options.get("displayLayout") === "centered"',
    },
    {
      name: 'footerBodyColor',
      friendlyName: 'Footer Text Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") === "centered" && options.get("footerBody")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Variant, stat-card styling, background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'displayLayout',
      friendlyName: '🎨 Display Layout',
      type: 'string',
      enum: ['sideBySide', 'centered'],
      enumLabels: {
        sideBySide: 'Side by Side (Text + Stats)',
        centered: 'Centered (Header + Cards)',
      },
      defaultValue: 'sideBySide',
    },
    {
      name: 'layout',
      friendlyName: '↔️ Text Position',
      type: 'string',
      enum: ['textLeft', 'textRight'],
      defaultValue: 'textLeft',
      showIf: 'options.get("displayLayout") !== "centered"',
    },
    {
      name: 'statCardBackgroundColor',
      friendlyName: '🎨 Card Background',
      type: 'color',
      defaultValue: 'rgba(255,255,255,0.05)',
    },
    {
      name: 'statCardBackgroundImage',
      friendlyName: 'Card Background Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText: 'Same image for all stat cards',
    },
    {
      name: 'statCardBackgroundImageMobile',
      friendlyName: 'Card Background Image (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText: 'Optional: Shows on screens < 640px. Falls back to desktop if not set.',
    },
    {
      name: 'statCardBackgroundOverlay',
      friendlyName: 'Card Image Overlay',
      type: 'color',
      defaultValue: 'rgba(0,0,0,0.3)',
      helperText: 'Dark overlay for text readability',
      showIf:
        'options.get("statCardBackgroundImage") || options.get("statCardBackgroundImageMobile")',
    },
    {
      name: 'statCardBorderColor',
      friendlyName: 'Card Border',
      type: 'color',
      defaultValue: '#e0ddd8',
    },
    {
      name: 'statValueColor',
      friendlyName: 'Card Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'statLabelColor',
      friendlyName: 'Card Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
    },
    {
      name: 'statIconBackgroundColor',
      friendlyName: 'Icon Background',
      type: 'color',
      defaultValue: 'rgba(244,127,43,0.08)',
    },
    {
      name: 'showIconBackground',
      friendlyName: 'Show Icon Background Box',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Turn OFF for clean logo display without a colored box behind it',
    },
    {
      name: 'statIconColor',
      friendlyName: 'Icon Color',
      type: 'color',
      defaultValue: '#F47F2B',
    },
    {
      name: 'statIconSize',
      friendlyName: 'Icon / Logo Size (px)',
      type: 'number',
      defaultValue: 40,
      helperText: 'Image size in pixels. Use 60–120 for logos.',
    },
    {
      name: 'statIconWrapperSize',
      friendlyName: 'Icon Container Size (px)',
      type: 'number',
      defaultValue: 80,
      helperText: 'Background box size. Only visible when "Show Icon Background Box" is ON.',
      showIf: 'options.get("showIconBackground")',
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1280,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Grid column overrides
    // ═══════════════════════════════════════════════════════════
    {
      name: 'gridColumns',
      friendlyName: 'Grid Columns (Side by Side)',
      type: 'number',
      defaultValue: 2,
      enum: [1, 2],
      showIf: 'options.get("displayLayout") !== "centered"',
      advanced: true,
    },
    {
      name: 'centeredGridColumns',
      friendlyName: 'Grid Columns (Centered)',
      type: 'number',
      defaultValue: 3,
      enum: [2, 3, 4],
      showIf: 'options.get("displayLayout") === "centered"',
      advanced: true,
    },
    {
      name: 'centeredGridColumnsMobile',
      friendlyName: 'Grid Columns (Mobile)',
      type: 'number',
      defaultValue: 2,
      enum: [1, 2],
      showIf: 'options.get("displayLayout") === "centered"',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// LOGOS / PARTNERS SECTION - Inline partner names or logo grid
// ============================================================
Builder.registerComponent(BuilderLogosSection, {
  name: 'LogosSection',
  description:
    'Display partner names inline (Strategic Partnerships) or as a logo image grid (Awards).',
  friendlyName: 'Story: Partners / Logos',
  image: ICONS.logos,
  canHaveChildren: false,
  priority: 12,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    // ═══════════════════════════════════════════════════════════
    // CORE — Inline mode content, grid mode content, logos, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'inlineLabel',
      friendlyName: '✍️ Label',
      type: 'string',
      defaultValue: 'Strategic Partnerships',
      showIf: 'options.get("displayMode") === "inline"',
    },
    {
      name: 'inlineLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayMode") === "inline"',
    },
    {
      name: 'partnerNames',
      friendlyName: '🏢 Partner Names',
      type: 'list',
      subFields: [{ name: 'name', friendlyName: 'Name', type: 'string', required: true }],
      defaultValue: [
        { name: 'Partner 1' },
        { name: 'Partner 2' },
        { name: 'Partner 3' },
        { name: 'Partner 4' },
        { name: 'Partner 5' },
        { name: 'Partner 6' },
      ],
      showIf: 'options.get("displayMode") === "inline"',
    },
    {
      name: 'partnerNameColor',
      friendlyName: 'Name Color',
      type: 'color',
      defaultValue: 'rgba(0,0,0,0.3)',
      showIf: 'options.get("displayMode") === "inline"',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: '',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'string',
      defaultValue: '',
      helperText: 'Full title text. Use | for line breaks.',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: '',
      helperText: 'These words will be highlighted in accent color',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'bodyText',
      friendlyName: '✍️ Body Text',
      type: 'richText',
      defaultValue: '',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'bodyTextColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'logos',
      friendlyName: '🏢 Partner Logos',
      type: 'list',
      subFields: [
        { name: 'id', type: 'string', required: true },
        { name: 'name', type: 'string', helperText: 'Partner name for alt text' },
        {
          name: 'imageUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
        },
      ],
      defaultValue: [
        { id: '1', name: 'Partner 1' },
        { id: '2', name: 'Partner 2' },
        { id: '3', name: 'Partner 3' },
        { id: '4', name: 'Partner 4' },
        { id: '5', name: 'Partner 5' },
        { id: '6', name: 'Partner 6' },
      ],
      showIf: 'options.get("displayMode") === "grid"',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Variant, grid layout, card/border style,
    // background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'displayMode',
      friendlyName: '🎨 Display Mode',
      type: 'string',
      enum: ['inline', 'grid'],
      enumLabels: { inline: 'Inline Partner Names', grid: 'Logo Image Grid' },
      defaultValue: 'inline',
      helperText: 'Inline: horizontal text names. Grid: two-column layout with logo images.',
    },
    {
      name: 'layout',
      friendlyName: '↔️ Layout',
      type: 'string',
      enum: ['textLeft', 'textRight'],
      defaultValue: 'textLeft',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'gridColumns',
      friendlyName: 'Grid Columns (Desktop)',
      type: 'number',
      defaultValue: 3,
      enum: [2, 3, 4, 5, 6],
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'mobileGridColumns',
      friendlyName: 'Grid Columns (Mobile)',
      type: 'number',
      defaultValue: 2,
      enum: [1, 2, 3],
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'gridRatio',
      friendlyName: 'Text / Logos Ratio',
      type: 'string',
      enum: ['1fr 1fr', '1fr 2fr', '2fr 1fr', '1fr 3fr', '3fr 1fr'],
      enumLabels: {
        '1fr 1fr': '50 / 50',
        '1fr 2fr': '33 / 67 (Awards style)',
        '2fr 1fr': '67 / 33',
        '1fr 3fr': '25 / 75',
        '3fr 1fr': '75 / 25',
      },
      defaultValue: '1fr 2fr',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'logoMaxHeight',
      friendlyName: '🖼️ Logo Size',
      type: 'number',
      enum: [40, 60, 90, 120, 160],
      enumLabels: {
        40: 'Small (40px)',
        60: 'Medium (60px) — default',
        90: 'Large (90px)',
        120: 'XL (120px)',
        160: 'XXL (160px)',
      },
      defaultValue: 60,
      helperText:
        'Maximum logo height on desktop. Width auto-scales so logos keep their aspect ratio.',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'logoMaxHeightMobile',
      friendlyName: '🖼️ Logo Size — Mobile',
      type: 'number',
      enum: [32, 40, 60, 80, 100],
      enumLabels: {
        32: 'Small (32px)',
        40: 'Medium (40px) — default',
        60: 'Large (60px)',
        80: 'XL (80px)',
        100: 'XXL (100px)',
      },
      defaultValue: 40,
      helperText: 'Maximum logo height on phones. Keep smaller than desktop to fit narrow screens.',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'cardBackground',
      friendlyName: '🃏 Card Background',
      type: 'color',
      defaultValue: '',
      helperText: 'Set a color to wrap content in a card. Leave empty for no card.',
      showIf: 'options.get("displayMode") === "grid"',
    },
    {
      name: 'cardBorderRadius',
      friendlyName: 'Card Border Radius',
      type: 'string',
      defaultValue: '16px',
      showIf: 'options.get("displayMode") === "grid" && options.get("cardBackground")',
    },
    {
      name: 'cardPadding',
      friendlyName: 'Card Padding',
      type: 'string',
      defaultValue: '40px 64px',
      showIf: 'options.get("displayMode") === "grid" && options.get("cardBackground")',
    },
    {
      name: 'borderTop',
      friendlyName: 'Top Border',
      type: 'string',
      defaultValue: '1px solid #e0ddd8',
      helperText: 'CSS border value. Set "none" to remove.',
    },
    {
      name: 'borderBottom',
      friendlyName: 'Bottom Border',
      type: 'string',
      defaultValue: '1px solid #e0ddd8',
      helperText: 'CSS border value. Set "none" to remove.',
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1280,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Title micro-spacing
    // ═══════════════════════════════════════════════════════════
    {
      name: 'titleMarginTop',
      friendlyName: 'Title Margin Top',
      type: 'number',
      defaultValue: 0,
      helperText: 'Adjust vertical position of title (in pixels)',
      showIf: 'options.get("displayMode") === "grid"',
      advanced: true,
    },
    {
      name: 'titleMarginBottom',
      friendlyName: 'Title Margin Bottom',
      type: 'number',
      defaultValue: 24,
      helperText: 'Space below title (in pixels)',
      showIf: 'options.get("displayMode") === "grid"',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 13. REFERENCE LIST – Notable Investors / Further Research / Source Citations
// PD-34278 introduced the component; PD-34290 polish added logoPosition,
// dividers, hover states, alt-text fallback, and slotted it into the
// canonical Story priority chain (between Partners/Logos and RTI).
// ============================================================
Builder.registerComponent(BuilderReferenceList, {
  name: 'ReferenceList',
  friendlyName: 'Story: Reference List',
  description:
    'List of sources or references — Notable Investors (logos + portfolio companies), Further Research (numbered citations), or any list of (logo + bold name + description) rows. Picks up where Story: Text Block leaves off.',
  image: ICONS.referenceList,
  canHaveChildren: false,
  priority: 13,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, items, optional disclaimer
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label (eyebrow)',
      type: 'string',
      defaultValue: 'NOTABLE INVESTORS',
      helperText: 'Small label above the title (e.g. "NOTABLE INVESTORS", "FURTHER RESEARCH")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Section Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Section Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Section Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote ref next to the eyebrow (e.g. "1", "*").',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title (optional)',
      type: 'string',
      defaultValue: '',
      helperText: 'Bigger heading above the list. Leave blank to skip.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("title")',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Title — Words to Highlight',
      type: 'string',
      defaultValue: '',
      helperText: 'These words inside the title will be colored in the highlight color.',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Title — Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote ref next to the title (e.g. "1", "*").',
      showIf: 'options.get("title")',
    },
    {
      name: 'bodyText',
      friendlyName: '✍️ Body Text (optional intro)',
      type: 'richText',
      defaultValue: '',
      helperText:
        'Optional intro paragraph between the title and the list. Supports bold, italic, links.',
    },
    {
      name: 'bodyTextColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave blank to inherit the default text color.',
      showIf: 'options.get("bodyText")',
    },
    {
      name: 'items',
      friendlyName: '📋 Items',
      type: 'list',
      subFields: [
        {
          name: 'logo',
          friendlyName: 'Logo',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          helperText: 'Optional small image. Auto-hides when not set.',
        },
        {
          name: 'logoAlt',
          friendlyName: 'Logo Alt Text',
          type: 'string',
          helperText: 'Falls back to the item name when blank.',
          showIf: 'options.get("logo")',
        },
        {
          name: 'logoMaxHeight',
          friendlyName: 'Logo Max Height (px) — override',
          type: 'number',
          helperText:
            'Override the section-wide max-height for this row. Leave blank to use the global default. Useful when one logo is taller than the rest.',
          showIf: 'options.get("logo")',
          advanced: true,
        },
        {
          name: 'logoMaxWidth',
          friendlyName: 'Logo Max Width (px) — override',
          type: 'number',
          helperText:
            'Override the section-wide max-width for this row. Useful for unusually wide wordmarks (e.g. "Coinbase Ventures") that need extra horizontal room.',
          showIf: 'options.get("logo")',
          advanced: true,
        },
        {
          name: 'logoMaxHeightMobile',
          friendlyName: 'Logo Max Height — Mobile (px) — override',
          type: 'number',
          helperText:
            'Override mobile max-height for this row only. Leave blank to use the section-wide mobile value (or, if that is also blank, the desktop value as-is — height is never auto-capped). PD-34303.',
          showIf: 'options.get("logo")',
          advanced: true,
        },
        {
          name: 'logoMaxWidthMobile',
          friendlyName: 'Logo Max Width — Mobile (px) — override',
          type: 'number',
          helperText:
            'Override mobile max-width for this row only. Leave blank to use the section-wide mobile value (or, if that is also blank, auto-cap at 120px or the desktop value if smaller — prevents wide wordmarks from dominating narrow viewports). PD-34303.',
          showIf: 'options.get("logo")',
          advanced: true,
        },
        {
          name: 'name',
          friendlyName: 'Name',
          type: 'string',
          required: true,
          helperText: 'Bold label at the start of the row (e.g. "Founders Fund", "Blockworks")',
        },
        {
          name: 'nameUrl',
          friendlyName: 'Name URL (optional link)',
          type: 'url',
          helperText: 'Turn the name into a link (article URL, Crunchbase profile, etc.)',
        },
        {
          name: 'openInNewTab',
          friendlyName: 'Open Link in New Tab',
          type: 'boolean',
          defaultValue: true,
          showIf: 'options.get("nameUrl")',
        },
        {
          name: 'superscript',
          friendlyName: 'Footnote # (superscript)',
          type: 'string',
          helperText: 'Small superscript next to the name (e.g. "1", "2", "*")',
        },
        {
          name: 'description',
          friendlyName: 'Description',
          type: 'richText',
          helperText:
            'Body of the row. Supports bold, italic, links. ' +
            'For Notable Investors: comma-separated portfolio companies. ' +
            'For Further Research: article title.',
        },
      ],
      defaultValue: [
        {
          name: 'Founders Fund',
          superscript: '1',
          description: 'OpenAI, SpaceX, Stripe, Anduril Industries, Palantir',
        },
        {
          name: 'Peter Thiel',
          superscript: '2',
          description: 'Affirm, Brex, Affirm, Stripe, OpenAI',
        },
        {
          name: 'General Catalyst',
          superscript: '3',
          description: 'Anthropic, Airbnb, Ramp, Altos Labs, Warby Parker',
        },
      ],
    },
    {
      name: 'showDisclaimer',
      friendlyName: 'Show Bottom Disclaimer',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Adds an italic, muted footnote line below the list.',
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'longText',
      defaultValue:
        'The information above may not be accurate or complete and has not been independently verified. See footnotes for sources.',
      helperText: 'Generic boilerplate is pre-filled — edit per-page as needed.',
      showIf: 'options.get("showDisclaimer")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — List style, logos, spacing, alignment
    // ═══════════════════════════════════════════════════════════
    {
      name: 'listStyle',
      friendlyName: 'List Style',
      type: 'string',
      enum: ['none', 'bulleted', 'numbered'],
      enumLabels: { none: 'No markers', bulleted: 'Bullets', numbered: 'Numbered (1, 2, 3…)' },
      defaultValue: 'none',
      helperText:
        'Use "Numbered" for Further Research / Source Citations. ' +
        'Pair with "No markers" when displaying logos so the list marker doesn\'t collide with the logo column.',
    },
    {
      name: 'markerColor',
      friendlyName: 'List Marker Color',
      type: 'color',
      defaultValue: '',
      helperText:
        'Color of the bullet/number marker. Leave blank to inherit the surrounding text color.',
      showIf: 'options.get("listStyle") !== "none"',
    },
    {
      name: 'showLogos',
      friendlyName: 'Show Logos',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Hide all logos at once (useful when the list is text-only).',
    },
    {
      name: 'logoSize',
      friendlyName: 'Logo Max Height (px) — Desktop',
      type: 'number',
      defaultValue: 40,
      helperText:
        "Maximum logo height on desktop. Width scales to preserve the source image's aspect ratio (square logos render square, wide logos render wider — capped by Logo Max Width below).",
      showIf: 'options.get("showLogos")',
    },
    {
      name: 'logoMaxWidth',
      friendlyName: 'Logo Max Width (px) — Desktop',
      type: 'number',
      defaultValue: 120,
      helperText:
        'Caps how wide a logo can render on desktop before its width starts to shrink the height. For mixed-aspect logo sets, override per-item under each entry\'s "Logo Max Width" advanced field.',
      showIf: 'options.get("showLogos")',
    },
    // PD-34303: explicit mobile overrides. When left blank, the component
    // auto-caps the desktop value at 120w / 40h so existing campaign content
    // with large desktop wordmarks doesn't render at the same size on phones.
    {
      name: 'logoSizeMobile',
      friendlyName: 'Logo Max Height (px) — Mobile',
      type: 'number',
      helperText:
        'Optional. Maximum logo height on mobile. Leave blank to use the desktop value as-is (no auto-cap on height, so square headshots and portrait photos render at full size). Set explicitly to override.',
      showIf: 'options.get("showLogos")',
    },
    {
      name: 'logoMaxWidthMobile',
      friendlyName: 'Logo Max Width (px) — Mobile',
      type: 'number',
      helperText:
        'Optional. Maximum logo width on mobile. Leave blank to auto-cap at 120px (or the desktop value if smaller) — prevents wide wordmarks from dominating narrow viewports. Set explicitly to go larger.',
      showIf: 'options.get("showLogos")',
    },
    {
      name: 'logoPosition',
      friendlyName: 'Logo Position',
      type: 'string',
      enum: ['top', 'left'],
      enumLabels: { top: 'Above text (stacked)', left: 'Left of text (inline row)' },
      defaultValue: 'top',
      helperText:
        'Top: logo stacks above the name + description (good for prominent logos and mixed-aspect logo sets — recommended). Left: logo and text on the same row.',
      showIf: 'options.get("showLogos")',
    },
    {
      name: 'itemSpacing',
      friendlyName: 'Spacing Between Items (px)',
      type: 'number',
      defaultValue: 16,
    },
    {
      name: 'showDividers',
      friendlyName: 'Show Dividers Between Items',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Draws a horizontal line between rows. The last row never has a trailing line.',
    },
    {
      name: 'dividerColor',
      friendlyName: 'Divider Color',
      type: 'color',
      defaultValue: '#E5E7EB',
      showIf: 'options.get("showDividers")',
    },
    {
      name: 'dividerStyle',
      friendlyName: 'Divider Style',
      type: 'string',
      enum: ['solid', 'dashed', 'dotted'],
      defaultValue: 'solid',
      showIf: 'options.get("showDividers")',
    },
    {
      name: 'dividerThickness',
      friendlyName: 'Divider Thickness (px)',
      type: 'number',
      defaultValue: 1,
      showIf: 'options.get("showDividers")',
    },
    {
      name: 'textAlign',
      friendlyName: 'Text Alignment',
      type: 'string',
      enum: ['left', 'center'],
      defaultValue: 'left',
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY — Per-item Name and Description styling overrides
    // ═══════════════════════════════════════════════════════════
    {
      name: 'nameColor',
      friendlyName: 'Name Color',
      type: 'color',
      defaultValue: '',
      helperText:
        'Color the bold name label (e.g. "Industry:", "Founders Fund"). Leave blank to inherit the default text color.',
    },
    {
      name: 'nameFontWeight',
      friendlyName: 'Name Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      enumLabels: {
        400: 'Regular (400)',
        500: 'Medium (500)',
        600: 'Semibold (600)',
        700: 'Bold (700) — default',
        800: 'Extra Bold (800)',
      },
      defaultValue: 700,
    },
    {
      name: 'nameFontStyle',
      friendlyName: 'Name Italic',
      type: 'string',
      enum: ['normal', 'italic'],
      enumLabels: { normal: 'Normal', italic: 'Italic' },
      defaultValue: 'normal',
    },
    {
      name: 'descriptionColor',
      friendlyName: 'Description Color',
      type: 'color',
      defaultValue: '',
      helperText:
        'Color the description text (the part after the colon). Leave blank to inherit the default text color.',
      advanced: true,
    },
    {
      name: 'descriptionFontStyle',
      friendlyName: 'Description Italic',
      type: 'string',
      enum: ['normal', 'italic'],
      enumLabels: { normal: 'Normal', italic: 'Italic' },
      defaultValue: 'normal',
      advanced: true,
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding (px)',
      type: 'number',
      defaultValue: null,
      helperText:
        'Top + bottom section padding. Leave blank to use the standard section padding (96px desktop / 64px mobile).',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Content Max Width (px)',
      type: 'number',
      // CONTENT_WIDTH.reading from designTokens — kept literal here to match
      // the convention used by other components in this file.
      defaultValue: 720,
      helperText: 'Caps the section width on desktop. Mobile always shrinks to viewport.',
    },
    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: 'transparent',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
  },
});

// ============================================================
// BANNER SECTION - Banner image with text above/below
// ============================================================
Builder.registerComponent(BuilderBannerSection, {
  name: 'BannerSection',
  description: 'Full-width banner image or video with optional text above and below.',
  friendlyName: 'Story: Banner',
  image:
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMS41IiByeD0iMC41IiBmaWxsPSIjMTg1QzY1IiBvcGFjaXR5PSIwLjQiLz48cmVjdCB4PSIzIiB5PSI2IiB3aWR0aD0iMTQiIGhlaWdodD0iMS41IiByeD0iMC41IiBmaWxsPSIjMTg1QzY1IiBvcGFjaXR5PSIwLjQiLz48cmVjdCB4PSIyIiB5PSIxMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjUiIHJ4PSIxIiBmaWxsPSIjMTg1QzY1Ii8+PHJlY3QgeD0iMyIgeT0iMTgiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxLjUiIHJ4PSIwLjUiIGZpbGw9IiMxODVDNjUiIG9wYWNpdHk9IjAuNCIvPjxyZWN0IHg9IjMiIHk9IjIxIiB3aWR0aD0iMTIiIGhlaWdodD0iMS41IiByeD0iMC41IiBmaWxsPSIjMTg1QzY1IiBvcGFjaXR5PSIwLjQiLz48L3N2Zz4=',
  canHaveChildren: false,
  priority: 10,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, media, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '🏷️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Bold label above everything (e.g. "THE PROBLEM")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'title',
      friendlyName: '📝 Title',
      type: 'string',
      defaultValue: '',
      helperText: 'Main heading for the section. Use | for line breaks.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("title")',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Title Highlight Text',
      type: 'string',
      defaultValue: '',
      helperText: 'Text within the title to highlight in a different color',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleHighlightItalic',
      friendlyName: 'Highlight Italic',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
      showIf: 'options.get("title")',
    },
    {
      name: 'subtitle',
      friendlyName: '📝 Subtitle',
      type: 'richText',
      defaultValue: '',
      helperText: 'Subtitle or description text below the title',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: 'rgba(255,255,255,0.9)',
      showIf: 'options.get("subtitle")',
    },
    {
      name: 'textAbove',
      friendlyName: '📝 Text Above Banner',
      type: 'richText',
      defaultValue: '',
      helperText: 'Body text displayed above the banner image',
    },
    {
      name: 'bannerImage',
      friendlyName: '🖼️ Banner Media',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg', 'mp4', 'webm', 'mov'],
      helperText: 'Upload an image or video for the banner',
    },
    {
      name: 'bannerMediaType',
      friendlyName: 'Media Type',
      type: 'enum',
      enum: ['image', 'video'],
      defaultValue: 'image',
      helperText: 'Select "video" for MP4/WebM files',
      showIf: 'options.get("bannerImage")',
    },
    {
      name: 'bannerImageAlt',
      friendlyName: 'Banner Alt Text',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'textBelow',
      friendlyName: '📝 Text Below Banner',
      type: 'richText',
      defaultValue: '',
      helperText: 'Body text displayed below the banner image',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Alignment, spacing, banner sizing,
    // text styling, background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'textAlign',
      friendlyName: '📐 Text Alignment',
      type: 'enum',
      enum: ['left', 'center'],
      defaultValue: 'left',
      helperText: 'Align all text content (title, subtitle, body text)',
    },
    {
      name: 'titleMarginBottom',
      friendlyName: 'Title Spacing Below',
      type: 'string',
      defaultValue: '16px',
      showIf: 'options.get("title")',
    },
    {
      name: 'bannerBorderRadius',
      friendlyName: 'Banner Border Radius',
      type: 'string',
      defaultValue: '8px',
    },
    {
      name: 'bannerMaxWidth',
      friendlyName: 'Banner Max Width',
      type: 'string',
      defaultValue: '100%',
      helperText: 'e.g. 100%, 800px, 90%',
    },
    {
      name: 'bannerAlignment',
      friendlyName: 'Banner Alignment',
      type: 'enum',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left',
      helperText: 'Horizontal alignment when banner is narrower than the section',
    },
    {
      name: 'bannerSpacingTop',
      friendlyName: 'Spacing Above Banner',
      type: 'string',
      defaultValue: '24px',
    },
    {
      name: 'bannerSpacingBottom',
      friendlyName: 'Spacing Below Banner',
      type: 'string',
      defaultValue: '24px',
    },
    {
      name: 'textColor',
      friendlyName: '🎨 Text Color',
      type: 'color',
      defaultValue: THEME.textSecondary,
    },
    {
      name: 'textFontSize',
      friendlyName: 'Text Font Size',
      type: 'string',
      defaultValue: '18px',
    },
    {
      name: 'textLineHeight',
      friendlyName: 'Text Line Height',
      type: 'string',
      defaultValue: '1.8',
    },
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding',
      type: 'number',
      defaultValue: 60,
      helperText: 'Top and bottom padding in pixels',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// CENTERED SECTION - Full-width centered content
// ============================================================
Builder.registerComponent(BuilderCenteredSection, {
  name: 'CenteredSection',
  description:
    'Flexible text block. Use for THE PITCH, problem statements, body copy under an image, or any free-form text. Title, eyebrow, and subtitle are all optional — leave them blank for plain prose. Defaults to left-aligned; flip to centered in Layout when you want a hero-style stanza.',
  friendlyName: 'Story: Text Block',
  image: ICONS.centered,
  canHaveChildren: false,
  priority: 0,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, primary content, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label (optional)',
      type: 'string',
      defaultValue: '',
      helperText: 'Small eyebrow above the title (e.g. "THE PITCH"). Leave blank to skip.',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title (optional)',
      type: 'string',
      defaultValue: '',
      helperText: 'Heading text. Leave blank for a pure body-text block. Use | for line breaks.',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("title")',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 24px, 2rem, 1.5em (empty = default desktop size)',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 20px, 1.25rem (empty = default mobile size)',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: '',
      helperText: 'These words will be highlighted in accent color',
      showIf: 'options.get("title")',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
      showIf: 'options.get("title")',
    },
    {
      name: 'subtitle',
      friendlyName: '✍️ Body Text',
      type: 'richText',
      defaultValue: '',
      helperText: 'The main paragraph copy. Supports bold, italic, and links.',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'subtitleFontSize',
      friendlyName: 'Subtitle Font Size',
      type: 'string',
      defaultValue: '18px',
      helperText: 'e.g. 14px, 16px, 18px, 1.125rem',
    },
    {
      name: 'subtitleLineHeight',
      friendlyName: 'Subtitle Line Height',
      type: 'string',
      defaultValue: '1.7',
      helperText: 'e.g. 1.4, 1.6, 1.8',
    },
    {
      name: 'showIconColumns',
      friendlyName: '🏷️ Show Icon Columns',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'iconColumns',
      friendlyName: 'Icon Columns',
      type: 'list',
      showIf: 'options.get("showIconColumns")',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['svg', 'png', 'jpg', 'jpeg', 'webp'],
          helperText: 'Upload icon image (SVG recommended)',
        },
        { name: 'text', type: 'richText', helperText: 'Use <strong> for bold' },
      ],
      defaultValue: [
        {
          id: '1',
          icon: '',
          text: 'Market size is <strong>growing rapidly</strong> year over year',
        },
        {
          id: '2',
          icon: '',
          text: 'Current solutions are <strong>outdated or inefficient</strong>',
        },
        {
          id: '3',
          icon: '',
          text: 'Customers are <strong>seeking better alternatives</strong>',
        },
      ],
    },
    {
      name: 'iconSize',
      friendlyName: 'Icon Size',
      type: 'number',
      defaultValue: 48,
      helperText: 'Icon width/height in pixels',
      showIf: 'options.get("showIconColumns")',
    },
    {
      name: 'iconColor',
      friendlyName: 'Icon Color',
      type: 'color',
      defaultValue: '#1A365D',
      showIf: 'options.get("showIconColumns")',
    },
    {
      name: 'iconTextColor',
      friendlyName: 'Icon Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("showIconColumns")',
    },
    {
      name: 'disclaimer',
      friendlyName: '⚖️ Disclaimer',
      type: 'richText',
      helperText: 'Small text at bottom',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textMuted,
    },
    {
      name: 'disclaimerSpacing',
      friendlyName: 'Disclaimer Spacing Above',
      type: 'string',
      defaultValue: '40px',
      helperText: 'Space between content and disclaimer (e.g. 16px, 24px, 40px)',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    {
      name: 'investButtonNote',
      friendlyName: 'Button Side Note',
      type: 'string',
      defaultValue: '',
      helperText: 'Small text beside the button (e.g., "Min. investment: $645")',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'investButtonNoteColor',
      friendlyName: 'Side Note Color',
      type: 'color',
      defaultValue: 'rgba(255,255,255,0.5)',
      showIf: 'options.get("investButtonNote")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Alignment, background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'textAlign',
      friendlyName: '📐 Text Alignment',
      type: 'enum',
      enum: ['left', 'center'],
      defaultValue: 'left',
      helperText: 'Align all text in this section. Switch to "center" for hero-style stanzas.',
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding (px)',
      type: 'number',
      defaultValue: 40,
      helperText: 'Top + bottom padding on desktop. Mobile inherits unless set below.',
    },
    {
      name: 'verticalPaddingMobile',
      friendlyName: '↕️ Vertical Padding — Mobile (px)',
      type: 'number',
      defaultValue: null,
      helperText:
        'Optional mobile override (< 900px). Leave blank to inherit the desktop value. ' +
        'Previously this section was pinned to 64px on mobile regardless of the desktop value.',
      advanced: true,
    },
    {
      name: 'horizontalPadding',
      friendlyName: '↔️ Horizontal Padding (px)',
      type: 'number',
      defaultValue: 24,
      helperText: 'Left + right padding. Applies to both desktop and mobile.',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 768,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED
    // ═══════════════════════════════════════════════════════════
    BULLET_SPACING_INPUT,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// QUOTE SECTION - Testimonial with background image
// ============================================================
Builder.registerComponent(BuilderQuoteSection, {
  name: 'QuoteSection',
  description: 'Testimonial or quote overlaid on a background image.',
  friendlyName: 'Story: Quote',
  image: ICONS.quote,
  canHaveChildren: false,
  priority: 8,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, primary content, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'quoteText',
      friendlyName: '💬 Quote Text',
      type: 'richText',
      defaultValue:
        '"Your compelling quote or testimonial goes here. Make it impactful and memorable."',
    },
    {
      name: 'quoteTextColor',
      friendlyName: 'Quote Color',
      type: 'color',
      defaultValue: THEME.textLight,
    },
    {
      name: 'quoteFontSize',
      friendlyName: 'Quote Font Size',
      type: 'number',
      defaultValue: 36,
    },
    {
      name: 'attributionText',
      friendlyName: 'Attribution',
      type: 'string',
      defaultValue: '—Source Name',
      helperText: 'Who said it (e.g., "—Forbes")',
    },
    {
      name: 'attributionColor',
      friendlyName: 'Attribution Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'attributionSuperscript',
      friendlyName: 'Attribution Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'showDisclaimer',
      friendlyName: '⚖️ Show Disclaimer',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      defaultValue:
        '*This testimonial may not be representative of the opinions of others and is not a guarantee of future performance or success.',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textLight,
      showIf: 'options.get("showDisclaimer")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'backgroundImage',
      friendlyName: '🖼️ Background Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
    },
    ...BACKGROUND_GRADIENT_INPUTS,
    {
      name: 'backgroundImageMobile',
      friendlyName: 'Background Image (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText: 'Optional: Shows on screens < 640px. Falls back to desktop if not set.',
    },
    {
      name: 'showOverlay',
      friendlyName: 'Show Overlay',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Darken background for readability',
      showIf: 'options.get("backgroundImage") || options.get("backgroundImageMobile")',
    },
    {
      name: 'overlayColor',
      friendlyName: 'Overlay Color',
      type: 'color',
      defaultValue: 'rgba(0, 0, 0, 0.5)',
      helperText: 'Darken background (use rgba)',
      showIf: 'options.get("showOverlay")',
    },
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Inner Vertical Padding',
      type: 'number',
      defaultValue: 60,
      helperText: 'Padding inside the background (px)',
    },
    {
      name: 'horizontalPadding',
      friendlyName: '↔️ Inner Horizontal Padding',
      type: 'number',
      defaultValue: 24,
      helperText: 'Padding inside the background (px)',
    },
    {
      name: 'contentMaxWidth',
      friendlyName: 'Content Max Width',
      type: 'number',
      defaultValue: 900,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// FEATURES SECTION - Feature list with icons + media, or centered rich cards
// ============================================================
Builder.registerComponent(BuilderFeaturesSection, {
  name: 'FeaturesSection',
  description:
    'Feature list with icons (side-by-side), or centered rich product cards (Solutions).',
  friendlyName: 'Story: Features',
  image: ICONS.features,
  canHaveChildren: false,
  priority: 11,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, primary content,
    // media, disclaimer, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'string',
      defaultValue: 'Enabling a New',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Title Highlight',
      type: 'string',
      defaultValue: 'Class of Features',
      helperText: 'Text within the title that should be highlighted with a different color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'centeredSubtitle',
      friendlyName: '✍️ Subtitle',
      type: 'richText',
      defaultValue: '',
      helperText: 'Subtitle text below the title in centered mode',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'centeredSubtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'bodyText',
      friendlyName: '✍️ Body Text',
      type: 'richText',
      defaultValue: 'Discover how this technology can transform the way you work and live.',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'bodyTextColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'subtitle',
      friendlyName: 'Subtitle (Above Features)',
      type: 'string',
      defaultValue: '',
      helperText: 'e.g., "Key benefits:"',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'richCards',
      friendlyName: '🃏 Product Cards',
      type: 'list',
      showIf: 'options.get("displayLayout") === "centeredCards"',
      helperText: 'Add cards with icon, title, tagline, body text, and optional stats or CTA',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'iconUrl',
          friendlyName: 'Icon Image',
          type: 'file',
          allowedFileTypes: ['svg', 'png', 'webp'],
          helperText: 'Card icon (24×24 recommended)',
        },
        {
          name: 'iconBackground',
          friendlyName: 'Icon Background',
          type: 'color',
          defaultValue: 'rgba(24,92,101,0.08)',
        },
        {
          name: 'title',
          friendlyName: 'Card Title',
          type: 'string',
          required: true,
        },
        {
          name: 'tagline',
          friendlyName: 'Tagline',
          type: 'string',
          helperText: 'Short tagline below the title (e.g., "For Accredited Investors")',
        },
        {
          name: 'taglineColor',
          friendlyName: 'Tagline Color',
          type: 'color',
          defaultValue: '#185C65',
        },
        {
          name: 'body',
          friendlyName: 'Body Content',
          type: 'richText',
          helperText: 'Rich text body content for the card',
        },
        {
          name: 'stats',
          friendlyName: 'Mini Stats',
          type: 'list',
          helperText: 'Optional 2×2 stat grid inside the card',
          subFields: [
            {
              name: 'value',
              friendlyName: 'Value',
              type: 'string',
              helperText: 'e.g., "$20M+", "25+"',
            },
            {
              name: 'label',
              friendlyName: 'Label',
              type: 'string',
              helperText: 'e.g., "Average Deal Size"',
            },
          ],
          defaultValue: [],
        },
        {
          name: 'ctaText',
          friendlyName: 'CTA Text',
          type: 'string',
          helperText: 'Optional call-to-action link text (e.g., "Learn More")',
        },
        {
          name: 'ctaColor',
          friendlyName: 'CTA Color',
          type: 'color',
          defaultValue: '#F47F2B',
        },
      ],
      defaultValue: [
        {
          id: '1',
          iconUrl: '',
          iconBackground: 'rgba(24,92,101,0.08)',
          title: 'Product One',
          tagline: 'For Accredited Investors',
          taglineColor: '#185C65',
          body: '<p>Description of this product or solution and its key benefits for investors.</p>',
          stats: [
            { value: '$20M+', label: 'Average Deal Size' },
            { value: '25+', label: 'Companies Funded' },
          ],
          ctaText: '',
          ctaColor: '#F47F2B',
        },
        {
          id: '2',
          iconUrl: '',
          iconBackground: 'rgba(244,127,43,0.08)',
          title: 'Product Two',
          tagline: 'For All Investors',
          taglineColor: '#185C65',
          body: '<p>Description of this product or solution and what makes it unique.</p>',
          stats: [],
          ctaText: 'Learn More',
          ctaColor: '#F47F2B',
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════
    // RICH CARD STYLING (centered mode only)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'richCardColumns',
      friendlyName: 'Card Columns (Desktop)',
      type: 'number',
      enum: [1, 2, 3],
      defaultValue: 2,
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardBackgroundColor',
      friendlyName: '🎨 Card Background',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardBorderColor',
      friendlyName: 'Card Border Color',
      type: 'color',
      defaultValue: '#e0ddd8',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardTitleColor',
      friendlyName: 'Card Title Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardTaglineColor',
      friendlyName: 'Card Tagline Color',
      type: 'color',
      defaultValue: '#185C65',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardBodyColor',
      friendlyName: 'Card Body Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardStatBackground',
      friendlyName: 'Stat Chip Background',
      type: 'color',
      defaultValue: 'rgba(24,92,101,0.06)',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardStatValueColor',
      friendlyName: 'Stat Value Color',
      type: 'color',
      defaultValue: '#185C65',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardStatLabelColor',
      friendlyName: 'Stat Label Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    {
      name: 'richCardCtaColor',
      friendlyName: 'CTA Color',
      type: 'color',
      defaultValue: '#F47F2B',
      showIf: 'options.get("displayLayout") === "centeredCards"',
    },
    // ═══════════════════════════════════════════════════════════
    // FEATURES LIST (side-by-side only)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'features',
      friendlyName: '✨ Features',
      type: 'list',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
      subFields: [
        { name: 'id', type: 'string', required: true },
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['svg', 'png'],
          helperText: '24x24 icon',
        },
        { name: 'title', type: 'richText', required: true, helperText: 'Supports hyperlinks.' },
        { name: 'titleSuperscript', friendlyName: 'Title Footnote #', type: 'string' },
        { name: 'description', type: 'richText', helperText: 'Supports hyperlinks.' },
      ],
      defaultValue: [
        { id: '1', icon: '', title: 'Feature One', description: 'Brief description' },
        { id: '2', icon: '', title: 'Feature Two', description: 'Brief description' },
        { id: '3', icon: '', title: 'Feature Three', description: 'Brief description' },
      ],
    },
    // ═══════════════════════════════════════════════════════════
    // FEATURE CARD STYLING (side-by-side only)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'featureCardBackgroundColor',
      friendlyName: '🎨 Card Background',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'featureCardBorderColor',
      friendlyName: 'Card Border',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'featureIconBackgroundColor',
      friendlyName: 'Icon Background',
      type: 'color',
      defaultValue: 'rgba(16, 185, 129, 0.15)',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'featureTitleColor',
      friendlyName: 'Feature Title Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'featureDescriptionColor',
      friendlyName: 'Feature Description Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'featureDescriptionFontSize',
      friendlyName: 'Feature Description Size',
      type: 'string',
      enum: ['12px', '13px', '14px', '15px', '16px', '18px'],
      defaultValue: '14px',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    // ═══════════════════════════════════════════════════════════
    // MEDIA (side-by-side only)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'mediaUrl',
      friendlyName: '🖼️ Media (Image/Video)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'mp4', 'webm', 'mov'],
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'mediaUrlMobile',
      friendlyName: 'Media (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'mp4', 'webm', 'mov'],
      helperText: 'Optional: Shows on screens < 640px. Falls back to desktop if not set.',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'mediaAlt',
      friendlyName: 'Alt Text',
      type: 'string',
      defaultValue: 'Feature image',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    {
      name: 'showDisclaimer',
      friendlyName: '⚠️ Show Disclaimer',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Show disclaimer text below the features list',
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      showIf: 'options.get("showDisclaimer")',
      helperText: 'Disclaimer / legal text displayed below features. Supports links.',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: '#999999',
      showIf: 'options.get("showDisclaimer")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Variant, text position, background,
    // padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'displayLayout',
      friendlyName: '🔀 Display Mode',
      type: 'string',
      enum: ['sideBySide', 'centeredCards'],
      enumLabels: {
        sideBySide: 'Side-by-Side (Features + Media)',
        centeredCards: 'Centered Header + Rich Cards',
      },
      defaultValue: 'sideBySide',
      helperText:
        'Side-by-Side: features list with media. Centered Cards: header with product/solution cards below.',
    },
    {
      name: 'layout',
      friendlyName: '↔️ Layout',
      type: 'string',
      enum: ['textLeft', 'textRight'],
      defaultValue: 'textLeft',
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1280,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Granular spacing + media radius
    // ═══════════════════════════════════════════════════════════
    {
      name: 'mediaBorderRadius',
      friendlyName: 'Media Border Radius',
      type: 'string',
      defaultValue: '12px',
      advanced: true,
      showIf: 'options.get("displayLayout") !== "centeredCards"',
    },
    BULLET_SPACING_INPUT,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// FORM SECTION - Email/Phone signup form
// ============================================================
Builder.registerComponent(BuilderFormSection, {
  name: 'FormSection',
  description: 'Email and phone capture form. Submits to StartEngine gateway for lead collection.',
  friendlyName: 'Story: Email Capture',
  image: ICONS.emailCapture,
  canHaveChildren: false,
  priority: 16,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title, body, form inputs, button,
    // disclaimer, success/error messages, secondary buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabel',
      friendlyName: '🏷️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small text above the title (e.g., "Our Solutions")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'titlePrefix',
      friendlyName: '✍️ Title Text',
      type: 'string',
      defaultValue: 'Turning Limited Bandwidth Into',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titlePrefixColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Highlight Text',
      type: 'string',
      defaultValue: 'Unlimited Opportunity',
      helperText: 'These words appear in the highlight color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'subtitleText',
      friendlyName: '📝 Subtitle / Body Text',
      type: 'richText',
      defaultValue: '',
      helperText: 'Paragraph below the title',
    },
    {
      name: 'subtitleTextColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
    },
    {
      name: 'emailPlaceholder',
      friendlyName: '📧 Email Placeholder',
      type: 'string',
      defaultValue: 'Enter your email address',
    },
    {
      name: 'phonePlaceholder',
      friendlyName: '📱 Phone Placeholder',
      type: 'string',
      defaultValue: 'Enter your phone number',
    },
    {
      name: 'buttonText',
      friendlyName: '🔘 Button Text',
      type: 'string',
      defaultValue: 'Learn More',
    },
    {
      name: 'disclaimerText',
      friendlyName: '⚖️ Disclaimer Text',
      type: 'richText',
      defaultValue:
        'By clicking "Learn More", you consent to receive marketing communications including text messages and emails (e.g., promotions, cart reminders, funding updates) from StartEngine Inc. using the contact information you provide. You understand and agree that messages may be sent using an autodialer or automated systems. Consent is not a condition of purchase. Message and data rates may apply; message frequency varies. You may unsubscribe from email communications at any time by clicking the unsubscribe link in the email, and from text messages by replying STOP. For more information, see our <a href="/privacy">Privacy Policy</a> & <a href="/terms">Terms</a>.',
    },
    {
      name: 'disclaimerTextColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: '#999',
    },
    {
      name: 'disclaimerLinkColor',
      friendlyName: 'Disclaimer Link Color',
      type: 'color',
      defaultValue: '#999',
    },
    {
      name: 'successMessage',
      friendlyName: '✅ Success Message',
      type: 'string',
      defaultValue: 'Thank you! Your submission has been received.',
    },
    {
      name: 'errorMessage',
      friendlyName: '❌ Error Message',
      type: 'string',
      defaultValue: 'Oops! Something went wrong. Please try again.',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Title weight, input styling, button styling, background, padding
    // ═══════════════════════════════════════════════════════════
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      defaultValue: 400,
    },
    {
      name: 'inputBackgroundColor',
      friendlyName: 'Input Background',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'inputTextColor',
      friendlyName: 'Input Text Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'inputBorderRadius',
      friendlyName: 'Input Border Radius',
      type: 'string',
      defaultValue: '30px',
    },
    {
      name: 'buttonBackgroundColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'buttonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'buttonBorderColor',
      friendlyName: 'Button Border',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'buttonHoverBackgroundColor',
      friendlyName: 'Button Hover Background',
      type: 'color',
      defaultValue: '#134a52',
    },
    {
      name: 'buttonHoverTextColor',
      friendlyName: 'Button Hover Text',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'buttonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '30px',
    },
    ...SECTION_BACKGROUND_INPUTS.map((input) =>
      input.name === 'backgroundColor' ? { ...input, defaultValue: '#f0ece6' } : input,
    ),
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding',
      type: 'number',
      defaultValue: 40,
      helperText: 'Top and bottom padding in pixels',
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — API endpoint
    // ═══════════════════════════════════════════════════════════
    {
      name: 'apiEndpoint',
      friendlyName: 'API Endpoint',
      type: 'string',
      defaultValue: 'https://gateway.startengine.com/user_svc/user/createFromIssuer',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// TEAM SLIDER - Horizontal carousel with featured member
// ============================================================
Builder.registerComponent(BuilderTeamSlider, {
  name: 'TeamSlider',
  description:
    'Team slider with featured member card. Auto-pulls from offering data or enter manually.',
  friendlyName: 'Data: Team',
  image: ICONS.team,
  canHaveChildren: false,
  priority: 20,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic data, header copy, featured/team list
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links (e.g., "team")',
    },
    {
      name: 'layout',
      friendlyName: '🧩 Layout',
      type: 'enum',
      enum: [
        { label: 'Slider (with featured member)', value: 'slider' },
        { label: 'Classic grid (clickable cards)', value: 'classic' },
      ],
      defaultValue: 'slider',
      helperText:
        'Slider = the default carousel with a featured-member card on top. Classic grid = the legacy "Classic: Team Section" look (clickable cards opening a member-detail modal). Both layouts work with auto-pulled data and manual entries.',
    },
    {
      name: 'useOfferingData',
      friendlyName: '🔄 Pull from Offering Data',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Auto-load team members from the offering API. OFF = Enter manually below.',
    },
    {
      name: 'headerLabel',
      friendlyName: '✍️ Header Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small text above title (e.g., "LEADERSHIP")',
    },
    {
      name: 'headerTitle',
      friendlyName: 'Header Title',
      type: 'string',
      defaultValue: 'TEAM',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'headerTitleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave blank to inherit the section Text Color below.',
    },
    {
      name: 'headerTitleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'headerTitleFontSize',
      friendlyName: 'Header Title Font Size',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 40px, 2.5rem (empty = default desktop size)',
      showIf: 'options.get("headerTitle")',
    },
    {
      name: 'headerTitleMobileFontSize',
      friendlyName: 'Header Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '',
      helperText: 'Valid CSS length e.g. 28px, 1.75rem (empty = default mobile size)',
      showIf: 'options.get("headerTitle")',
    },
    {
      name: 'headerTitleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: '',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'headerTitleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("headerTitleHighlight")',
    },
    {
      name: 'headerSubtitle',
      friendlyName: 'Header Subtitle',
      type: 'richText',
      defaultValue: '',
    },
    {
      name: 'showFeaturedMember',
      friendlyName: '⭐ Feature the CEO / Founder',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'ON = Highlight the first founder (or first member) with a larger card at top. OFF = All members in slider equally. (Slider layout only.)',
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'featuredMemberImage',
      friendlyName: 'Featured Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      showIf:
        'options.get("layout") !== "classic" && !options.get("useOfferingData") && options.get("showFeaturedMember")',
    },
    {
      name: 'featuredMemberName',
      friendlyName: 'Featured Name',
      type: 'string',
      defaultValue: '',
      showIf:
        'options.get("layout") !== "classic" && !options.get("useOfferingData") && options.get("showFeaturedMember")',
    },
    {
      name: 'featuredMemberTitle',
      friendlyName: 'Featured Title',
      type: 'string',
      defaultValue: '',
      showIf:
        'options.get("layout") !== "classic" && !options.get("useOfferingData") && options.get("showFeaturedMember")',
    },
    {
      name: 'featuredMemberTitleSuperscript',
      friendlyName: 'Featured Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'e.g. "1", "2", "*"',
      showIf:
        'options.get("layout") !== "classic" && !options.get("useOfferingData") && options.get("showFeaturedMember")',
    },
    {
      name: 'featuredMemberBio',
      friendlyName: 'Featured Bio',
      type: 'richText',
      showIf:
        'options.get("layout") !== "classic" && !options.get("useOfferingData") && options.get("showFeaturedMember")',
    },
    {
      name: 'teamMembers',
      friendlyName: '👥 Team Members (Manual)',
      type: 'list',
      showIf: '!options.get("useOfferingData")',
      helperText: 'Only used when "Pull from Offering Data" is OFF',
      subFields: [
        { name: 'id', type: 'string', required: true },
        { name: 'name', type: 'string', required: true },
        { name: 'title', type: 'string', required: true },
        {
          name: 'titleSuperscript',
          friendlyName: 'Title Footnote #',
          type: 'string',
          helperText: 'e.g. "1", "2", "*"',
        },
        { name: 'imageUrl', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'] },
        { name: 'bio', type: 'richText' },
        { name: 'linkedinUrl', friendlyName: 'LinkedIn URL', type: 'string' },
        { name: 'twitterUrl', friendlyName: 'Twitter URL', type: 'string' },
        { name: 'facebookUrl', friendlyName: 'Facebook URL', type: 'string' },
      ],
      defaultValue: [
        { id: '1', name: 'Jane Doe', title: 'CTO' },
        { id: '2', name: 'Mike Johnson', title: 'CFO' },
        { id: '3', name: 'Sarah Williams', title: 'COO' },
      ],
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Alignment, display options, colors,
    // navigation, background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'headerAlignment',
      friendlyName: '📐 Header Alignment',
      type: 'enum',
      enum: ['left', 'center'],
      defaultValue: 'center',
    },
    {
      name: 'showBioOnCards',
      friendlyName: '📝 Show Bio on Cards',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'cardBioMaxLines',
      friendlyName: 'Card Bio Max Lines',
      type: 'number',
      defaultValue: 3,
      helperText:
        'Lines shown under each card. Cards are 140px wide; 3 lines fit cleanly. Set to 0 to show the full bio under every card with no clamp (cards will grow as tall as needed).',
      showIf: 'options.get("layout") !== "classic" && options.get("showBioOnCards")',
    },
    {
      name: 'showSocialLinks',
      friendlyName: '🔗 Show Social Links',
      type: 'boolean',
      defaultValue: false,
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'featuredBioMaxLines',
      friendlyName: 'Featured Bio Max Lines',
      type: 'number',
      defaultValue: 4,
      helperText: 'Lines shown before "Show more" button. Set 0 for no limit.',
      showIf: 'options.get("layout") !== "classic" && options.get("showFeaturedMember")',
    },
    {
      name: 'memberImageShape',
      friendlyName: '🖼️ Image Shape',
      type: 'enum',
      enum: ['circle', 'rounded'],
      defaultValue: 'circle',
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'textColor',
      friendlyName: '🎨 Text Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'accentColor',
      friendlyName: 'Accent Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'navigationStyle',
      friendlyName: '🧭 Navigation Style',
      type: 'enum',
      enum: ['arrows', 'none'],
      defaultValue: 'arrows',
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'enableSwipe',
      friendlyName: 'Enable Touch Swipe',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("layout") !== "classic"',
    },
    {
      name: 'classicHorizontalPadding',
      friendlyName: 'Horizontal Padding (Classic)',
      type: 'number',
      defaultValue: 0,
      helperText: 'Padding on left/right of the classic grid. Slider layout ignores this.',
      showIf: 'options.get("layout") === "classic"',
    },
    {
      name: 'classicImageFit',
      friendlyName: '🖼️ Card Image Fit (Classic)',
      type: 'enum',
      enum: [
        { label: 'Contain — fits whole image, may letterbox', value: 'contain' },
        { label: 'Cover — fills card, may crop', value: 'cover' },
      ],
      defaultValue: 'contain',
      showIf: 'options.get("layout") === "classic"',
    },
    {
      name: 'classicImageFitBackgroundColor',
      friendlyName: 'Card Background (Classic, Contain mode)',
      type: 'color',
      defaultValue: '#F3F4F6',
      helperText:
        'Background color shown behind images when fit is "Contain". Ignored for "Cover".',
      showIf: 'options.get("layout") === "classic" && options.get("classicImageFit") === "contain"',
    },
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width (px)',
      type: 'number',
      defaultValue: 1280,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// TERMS SECTION - Offering terms with dynamic data
// ============================================================
Builder.registerComponent(BuilderTermsSection, {
  name: 'TermsSection',
  description: 'Offering terms grid with price, valuation, deadline, and more from the API.',
  friendlyName: 'Data: Terms',
  image: ICONS.terms,
  canHaveChildren: false,
  priority: 21,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic data, company name, terms grids,
    // custom rows, disclaimer
    // ═══════════════════════════════════════════════════════════
    {
      name: 'useDynamicData',
      friendlyName: '🔄 Auto-Update from Offering',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Live data, OFF = Manual entry',
    },
    {
      name: 'forceShowOnTtw',
      friendlyName: '🔓 Force Show on Test the Waters',
      type: 'boolean',
      defaultValue: false,
      showIf: 'options.get("useDynamicData")',
      helperText:
        'OFF (default): hide this section automatically on Test-the-Waters offerings — TTW campaigns rarely have final pricing/valuation/deadline numbers, so the section would render placeholder values like "$X" or "Nov XX, 2025" that mislead visitors. ON: render terms anyway (e.g. partial-disclosure preview, or you\u2019ve filled in custom rows).',
    },
    {
      name: 'companyName',
      friendlyName: '✍️ Company Name',
      type: 'string',
      defaultValue: 'Company',
      helperText: 'Used in title: "[Company] Terms"',
    },
    // ═══════════════════════════════════════════════════════════
    // OVERVIEW SECTION
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showOverviewSection',
      friendlyName: '📋 Show Overview Section',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'overviewTitle',
      friendlyName: 'Overview Title',
      type: 'string',
      defaultValue: 'Overview',
      showIf: 'options.get("showOverviewSection")',
    },
    {
      name: 'showPricePerShare',
      friendlyName: 'Show Price Per Share',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showOverviewSection")',
    },
    {
      name: 'manualPricePerShare',
      friendlyName: 'Price Per Share (Manual)',
      type: 'string',
      defaultValue: '$X',
      showIf:
        'options.get("showOverviewSection") && options.get("showPricePerShare") && !options.get("useDynamicData")',
    },
    {
      name: 'showValuation',
      friendlyName: 'Show Valuation',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showOverviewSection")',
    },
    {
      name: 'manualValuation',
      friendlyName: 'Valuation (Manual)',
      type: 'string',
      defaultValue: '$XX.XXM',
      showIf:
        'options.get("showOverviewSection") && options.get("showValuation") && !options.get("useDynamicData")',
    },
    {
      name: 'showDeadline',
      friendlyName: 'Show Deadline',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showOverviewSection")',
    },
    {
      name: 'manualDeadline',
      friendlyName: 'Deadline (Manual)',
      type: 'string',
      defaultValue: 'Nov. XX, 2025 at 11:59 PM PDT',
      showIf:
        'options.get("showOverviewSection") && options.get("showDeadline") && !options.get("useDynamicData")',
    },
    {
      name: 'showFundingGoal',
      friendlyName: 'Show Funding Goal',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showOverviewSection")',
    },
    {
      name: 'manualFundingGoal',
      friendlyName: 'Funding Goal (Manual)',
      type: 'string',
      defaultValue: '$XXX – $XXM',
      showIf:
        'options.get("showOverviewSection") && options.get("showFundingGoal") && !options.get("useDynamicData")',
    },
    // ═══════════════════════════════════════════════════════════
    // BREAKDOWN SECTION
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showBreakdownSection',
      friendlyName: '📊 Show Breakdown Section',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'breakdownTitle',
      friendlyName: 'Breakdown Title',
      type: 'string',
      defaultValue: 'Breakdown',
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'showMinInvestment',
      friendlyName: 'Show Min Investment',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualMinInvestment',
      friendlyName: 'Min Investment (Manual)',
      type: 'string',
      defaultValue: '$XXX',
      showIf:
        'options.get("showBreakdownSection") && options.get("showMinInvestment") && !options.get("useDynamicData")',
    },
    {
      name: 'showOfferingType',
      friendlyName: 'Show Offering Type',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualOfferingType',
      friendlyName: 'Offering Type (Manual)',
      type: 'string',
      defaultValue: 'Reg A+',
      showIf:
        'options.get("showBreakdownSection") && options.get("showOfferingType") && !options.get("useDynamicData")',
    },
    {
      name: 'showMaxInvestment',
      friendlyName: 'Show Max Investment',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualMaxInvestment',
      friendlyName: 'Max Investment (Manual)',
      type: 'string',
      defaultValue: '$X,000,000',
      showIf:
        'options.get("showBreakdownSection") && options.get("showMaxInvestment") && !options.get("useDynamicData")',
    },
    {
      name: 'showAssetType',
      friendlyName: 'Show Asset Type',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualAssetType',
      friendlyName: 'Asset Type (Manual)',
      type: 'string',
      defaultValue: 'Equity',
      showIf:
        'options.get("showBreakdownSection") && options.get("showAssetType") && !options.get("useDynamicData")',
    },
    {
      name: 'showMinShares',
      friendlyName: 'Show Min Shares',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualMinShares',
      friendlyName: 'Min Shares (Manual)',
      type: 'string',
      defaultValue: 'XXX',
      showIf:
        'options.get("showBreakdownSection") && options.get("showMinShares") && !options.get("useDynamicData")',
    },
    {
      name: 'showSharesOffered',
      friendlyName: 'Show Shares Offered',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualSharesOffered',
      friendlyName: 'Shares Offered (Manual)',
      type: 'string',
      defaultValue: 'XXX',
      showIf:
        'options.get("showBreakdownSection") && options.get("showSharesOffered") && !options.get("useDynamicData")',
    },
    {
      name: 'showMaxShares',
      friendlyName: 'Show Max Shares',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showBreakdownSection")',
    },
    {
      name: 'manualMaxShares',
      friendlyName: 'Max Shares (Manual)',
      type: 'string',
      defaultValue: 'X,X00,000',
      showIf:
        'options.get("showBreakdownSection") && options.get("showMaxShares") && !options.get("useDynamicData")',
    },
    // ═══════════════════════════════════════════════════════════
    // CUSTOM TERMS (for Reg D or any additional rows)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'customTerms',
      friendlyName: '➕ Custom Terms',
      type: 'list',
      helperText: 'Add custom label/value rows (e.g., Membership Interest, Annual Fee)',
      subFields: [
        {
          name: 'label',
          friendlyName: 'Label',
          type: 'string',
          defaultValue: 'CUSTOM LABEL',
        },
        {
          name: 'value',
          friendlyName: 'Value',
          type: 'richText',
          defaultValue: 'Value',
        },
        {
          name: 'superscript',
          friendlyName: 'Footnote #',
          type: 'string',
          defaultValue: '',
        },
        {
          name: 'section',
          friendlyName: 'Section',
          type: 'string',
          enum: ['overview', 'breakdown'],
          defaultValue: 'breakdown',
          helperText: 'Which grid to add this term to',
        },
      ],
      defaultValue: [],
    },
    // ═══════════════════════════════════════════════════════════
    // DISCLAIMER
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showDisclaimer',
      friendlyName: '⚖️ Show Disclaimer',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      defaultValue: 'Maximum Number of Shares Offered subject to adjustment for bonus shares',
      showIf: 'options.get("showDisclaimer")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Title/label/value colors, background
    // ═══════════════════════════════════════════════════════════
    {
      name: 'titleColor',
      friendlyName: '🎨 Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'labelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'valueColor',
      friendlyName: 'Value Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    ...SECTION_BACKGROUND_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED
    // ═══════════════════════════════════════════════════════════
    {
      name: 'dividerColor',
      friendlyName: 'Divider Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      advanced: true,
    },
    {
      name: 'pricePerShareLabel',
      friendlyName: 'Price Per Share Label',
      type: 'string',
      defaultValue: 'PRICE PER SHARE',
      showIf: 'options.get("showOverviewSection") && options.get("showPricePerShare")',
    },
    {
      name: 'valuationLabel',
      friendlyName: 'Valuation Label',
      type: 'string',
      defaultValue: 'VALUATION',
      showIf: 'options.get("showOverviewSection") && options.get("showValuation")',
    },
    {
      name: 'deadlineLabel',
      friendlyName: 'Deadline Label',
      type: 'string',
      defaultValue: 'DEADLINE',
      showIf: 'options.get("showOverviewSection") && options.get("showDeadline")',
    },
    {
      name: 'deadlineSuperscript',
      friendlyName: 'Deadline Footnote #',
      type: 'string',
      defaultValue: '1',
      showIf: 'options.get("showOverviewSection") && options.get("showDeadline")',
    },
    {
      name: 'fundingGoalLabel',
      friendlyName: 'Funding Goal Label',
      type: 'string',
      defaultValue: 'FUNDING GOAL',
      showIf: 'options.get("showOverviewSection") && options.get("showFundingGoal")',
    },
    {
      name: 'fundingGoalSuperscript',
      friendlyName: 'Funding Goal Footnote #',
      type: 'string',
      defaultValue: '2',
      showIf: 'options.get("showOverviewSection") && options.get("showFundingGoal")',
    },
    {
      name: 'minInvestmentLabel',
      friendlyName: 'Min Investment Label',
      type: 'string',
      defaultValue: 'MIN INVESTMENT',
      showIf: 'options.get("showBreakdownSection") && options.get("showMinInvestment")',
    },
    {
      name: 'minInvestmentSuperscript',
      friendlyName: 'Min Investment Footnote #',
      type: 'string',
      defaultValue: '3',
      showIf: 'options.get("showBreakdownSection") && options.get("showMinInvestment")',
    },
    {
      name: 'offeringTypeLabel',
      friendlyName: 'Offering Type Label',
      type: 'string',
      defaultValue: 'OFFERING TYPE',
      showIf: 'options.get("showBreakdownSection") && options.get("showOfferingType")',
    },
    {
      name: 'maxInvestmentLabel',
      friendlyName: 'Max Investment Label',
      type: 'string',
      defaultValue: 'MAX INVESTMENT',
      showIf: 'options.get("showBreakdownSection") && options.get("showMaxInvestment")',
    },
    {
      name: 'maxInvestmentSuperscript',
      friendlyName: 'Max Investment Footnote #',
      type: 'string',
      defaultValue: '4',
      showIf: 'options.get("showBreakdownSection") && options.get("showMaxInvestment")',
    },
    {
      name: 'assetTypeLabel',
      friendlyName: 'Asset Type Label',
      type: 'string',
      defaultValue: 'ASSET TYPE',
      showIf: 'options.get("showBreakdownSection") && options.get("showAssetType")',
    },
    {
      name: 'minSharesLabel',
      friendlyName: 'Min Shares Label',
      type: 'string',
      defaultValue: 'MIN NUMBER OF SHARES OFFERED',
      showIf: 'options.get("showBreakdownSection") && options.get("showMinShares")',
    },
    {
      name: 'sharesOfferedLabel',
      friendlyName: 'Shares Offered Label',
      type: 'string',
      defaultValue: 'SHARES OFFERED',
      showIf: 'options.get("showBreakdownSection") && options.get("showSharesOffered")',
    },
    {
      name: 'maxSharesLabel',
      friendlyName: 'Max Shares Label',
      type: 'string',
      defaultValue: 'MAX NUMBER OF SHARES OFFERED',
      showIf: 'options.get("showBreakdownSection") && options.get("showMaxShares")',
    },
    // ═══════════════════════════════════════════════════════════
    // SUPPLEMENTARY SECTIONS (below the terms card)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showSECLinks',
      friendlyName: '📎 Show SEC & Document Links',
      type: 'boolean',
      defaultValue: true,
      helperText: 'SEC Filing, Offering Circular, Offering Memorandum links',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'showFinancials',
      friendlyName: '📊 Show Financials',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Expandable financials table (prior/recent year)',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'showRisks',
      friendlyName: '⚠️ Show Risks',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Expandable risk disclosure',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'showPerksInfo',
      friendlyName: '🎁 Show Perks & Bonuses',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Investment incentives, bonuses, and perks info',
      showIf: 'options.get("useDynamicData")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// TABLE SECTION - Data table with configurable columns & rows
// ============================================================
Builder.registerComponent(BuilderTableSection, {
  name: 'TableSection',
  friendlyName: 'Story: Table',
  description: 'Configurable data table for fund compositions, securities, and structured data.',
  image: ICONS.table,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, eyebrow, title/subtitle, columns, rows,
    // caption, header/row styling, behavior toggles
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: '🔗 Section ID',
      type: 'string',
      defaultValue: '',
      helperText: 'Anchor ID for scrolling to this section',
    },
    {
      name: 'sectionLabel',
      friendlyName: '🏷️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small uppercase label above the title',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: '#185C65',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'titleText',
      friendlyName: '📝 Title',
      type: 'string',
      defaultValue: '',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'subtitleText',
      friendlyName: '✍️ Subtitle',
      type: 'richText',
      defaultValue: '',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#666666',
    },
    {
      name: 'columns',
      friendlyName: '📊 Columns',
      type: 'list',
      helperText: 'Define table columns. Each row will have a cell for every column.',
      subFields: [
        {
          name: 'header',
          friendlyName: 'Header Text',
          type: 'string',
          required: true,
          defaultValue: 'Column',
        },
        {
          name: 'key',
          friendlyName: 'Key (optional)',
          type: 'string',
          defaultValue: '',
          helperText: 'Unique key for this column (used internally)',
          advanced: true,
        },
        {
          name: 'align',
          friendlyName: 'Alignment',
          type: 'enum',
          enum: ['left', 'center', 'right'],
          defaultValue: 'left',
        },
        {
          name: 'width',
          friendlyName: 'Width',
          type: 'string',
          defaultValue: '',
          helperText: 'CSS width (e.g., "200px", "25%"). Leave empty for auto.',
          advanced: true,
        },
        {
          name: 'bold',
          friendlyName: 'Bold Values',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Make cell values in this column bold',
        },
      ],
      defaultValue: [
        { header: 'Company', align: 'left', bold: true },
        { header: 'Type of Share', align: 'left' },
        { header: 'Up to Total $', align: 'right' },
        { header: 'PPS', align: 'right' },
        { header: 'Up to Shares', align: 'right' },
        { header: 'Allocation', align: 'right' },
      ],
    },
    {
      name: 'rows',
      friendlyName: '📋 Rows',
      type: 'list',
      helperText: 'Add data rows. Use the "cells" list to add values for each column (in order).',
      subFields: [
        {
          name: 'cells',
          friendlyName: 'Cell Values',
          type: 'list',
          subFields: [
            {
              name: 'value',
              friendlyName: 'Value',
              type: 'richText',
              defaultValue: '',
            },
          ],
        },
      ],
      defaultValue: [],
    },
    {
      name: 'showCaption',
      friendlyName: '📝 Show Caption',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'captionText',
      friendlyName: 'Caption Text',
      type: 'richText',
      defaultValue: '',
      showIf: 'options.get("showCaption")',
    },
    {
      name: 'captionColor',
      friendlyName: 'Caption Color',
      type: 'color',
      defaultValue: '#999999',
      showIf: 'options.get("showCaption")',
    },
    {
      name: 'headerBackgroundColor',
      friendlyName: '🎨 Header Background',
      type: 'color',
      defaultValue: '#f7f5f2',
    },
    {
      name: 'headerTextColor',
      friendlyName: 'Header Text Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'rowTextColor',
      friendlyName: 'Row Text Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'rowBorderColor',
      friendlyName: 'Row Border Color',
      type: 'color',
      defaultValue: '#e0ddd8',
    },
    {
      name: 'rowHoverColor',
      friendlyName: 'Row Hover Color',
      type: 'color',
      defaultValue: '#fafaf8',
    },
    {
      name: 'cardBackgroundColor',
      friendlyName: 'Card Background',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'cardBorderColor',
      friendlyName: 'Card Border Color',
      type: 'color',
      defaultValue: '#e0ddd8',
    },
    {
      name: 'striped',
      friendlyName: 'Striped Rows',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'stripedColor',
      friendlyName: 'Striped Row Color',
      type: 'color',
      defaultValue: '#fafaf8',
      showIf: 'options.get("striped")',
    },
    {
      name: 'compactMode',
      friendlyName: 'Compact Mode',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Reduce padding for denser tables',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, section padding
    // ═══════════════════════════════════════════════════════════
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'sectionPadding',
      friendlyName: '📐 Section Padding',
      type: 'string',
      defaultValue: '',
      helperText: 'CSS padding (e.g., "96px 24px")',
      advanced: true,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// PERKS SECTION - Sticky left, scrolling right layout
// ============================================================
Builder.registerComponent(BuilderPerksSection, {
  name: 'PerksSection',
  description:
    'Investment perks with sticky header and scrolling perk cards. Auto-pulls from offering data.',
  friendlyName: 'Data: Perks',
  image: ICONS.perks,
  canHaveChildren: false,
  priority: 22,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic data, header copy, perk groups,
    // disclaimer, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'useDynamicData',
      friendlyName: '🔄 Auto-Update Perks',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Use offering rewards, OFF = Manual perks',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: 'PERKS',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'sectionTitle',
      friendlyName: '✍️ Section Title',
      type: 'longText',
      defaultValue: 'EARN BONUS | SHARES',
      helperText: 'Use | for line breaks (e.g., "Line1 | Line2")',
    },
    {
      name: 'sectionTitleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'sectionTitleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'sectionTitleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionTitleHighlight")',
    },
    {
      name: 'sectionTitleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'sectionTitleAlign',
      friendlyName: 'Title Alignment',
      type: 'enum',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left',
    },
    ...TITLE_FONT_INPUTS,
    {
      name: 'sectionSubtitle',
      friendlyName: '✍️ Subtitle',
      type: 'richText',
      defaultValue:
        'Your belief in our mission deserves more, and in recognition of that support, we are extending a limited-time package of premium perks that enhance your potential returns and deliver added value.',
    },
    {
      name: 'sectionSubtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: THEME.textMuted,
    },
    {
      name: 'investButtonText',
      friendlyName: '🔘 Button Text',
      type: 'string',
      defaultValue: 'INVEST',
    },
    {
      name: 'investButtonBackgroundColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: THEME.ctaButton,
    },
    {
      name: 'investButtonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: THEME.ctaButtonText,
    },
    {
      name: 'perkGroups',
      friendlyName: '🎁 Perk Groups',
      type: 'list',
      showIf: '!options.get("useDynamicData")',
      subFields: [
        {
          name: 'groupTitle',
          type: 'string',
          helperText: 'e.g., "TIME-BASED PERKS"',
        },
        {
          name: 'perks',
          type: 'list',
          subFields: [
            { name: 'title', type: 'string', required: true },
            { name: 'description', type: 'richText', required: true },
          ],
          defaultValue: [{ title: 'PERK 1', description: 'Description' }],
        },
      ],
      defaultValue: [
        {
          groupTitle: 'TIME-BASED PERKS',
          perks: [
            { title: 'EARLY BIRD 1', description: 'Invest $500+ within 2 weeks for 1% bonus' },
            { title: 'EARLY BIRD 2', description: 'Invest $1,000+ within 2 weeks for 2% bonus' },
          ],
        },
        {
          groupTitle: 'AMOUNT-BASED PERKS',
          perks: [
            { title: 'TIER 1', description: 'Invest $2,000+ for 1% bonus shares' },
            { title: 'TIER 2', description: 'Invest $5,000+ for 2% bonus shares' },
          ],
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Perk card styling, background, columns
    // ═══════════════════════════════════════════════════════════
    {
      name: 'perkCardBackgroundColor',
      friendlyName: '🎨 Card Background',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'perkCardBackgroundImage',
      friendlyName: 'Card Background Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
    },
    {
      name: 'perkCardImageOverlayColor',
      friendlyName: 'Card Image Overlay',
      type: 'color',
      defaultValue: 'rgba(0, 0, 0, 0.5)',
      helperText: 'Dark overlay on card background image for text readability',
      showIf: 'options.get("perkCardBackgroundImage")',
    },
    {
      name: 'perkCardTitleColor',
      friendlyName: 'Card Title Color',
      type: 'color',
      defaultValue: THEME.textDark,
    },
    {
      name: 'perkCardDescriptionColor',
      friendlyName: 'Card Description Color',
      type: 'color',
      defaultValue: '#4B5563',
    },
    {
      name: 'perkGroupTitleColor',
      friendlyName: 'Group Title Color',
      type: 'color',
      defaultValue: THEME.textDark,
    },
    {
      name: 'disclaimerText',
      friendlyName: '📜 Disclaimer Text',
      type: 'richText',
      helperText: 'Optional disclaimer text at the bottom of the section',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textMuted,
      showIf: 'options.get("disclaimerText")',
    },
    {
      name: 'disclaimerFontSize',
      friendlyName: 'Disclaimer Font Size',
      type: 'enum',
      enum: ['9px', '10px', '11px', '12px', '13px', '14px'],
      defaultValue: '12px',
      showIf: 'options.get("disclaimerText")',
    },
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'leftColumnBackgroundColor',
      friendlyName: 'Left Column Background',
      type: 'color',
      defaultValue: THEME.transparent,
    },
    {
      name: 'rightColumnBackgroundColor',
      friendlyName: 'Right Column Background',
      type: 'color',
      defaultValue: THEME.transparent,
    },
    // ═══════════════════════════════════════════════════════════
    // ADVANCED
    // ═══════════════════════════════════════════════════════════
    {
      name: 'investButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '8px',
      advanced: true,
    },
    ...BUTTON_SIZE_INPUTS('investButton'),
    {
      name: 'perkCardBorderRadius',
      friendlyName: 'Card Border Radius',
      type: 'string',
      defaultValue: '12px',
      advanced: true,
    },
    {
      name: 'showMainInvestButton',
      friendlyName: '💰 Show Section Invest Button',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Add a centered Invest button at the bottom of this section',
    },
    {
      name: 'mainInvestButtonBorderRadius',
      friendlyName: 'Section Button Radius',
      type: 'string',
      defaultValue: '16px',
      showIf: 'options.get("showMainInvestButton")',
    },
    {
      name: 'mainInvestButtonWidth',
      friendlyName: 'Section Button Width',
      type: 'string',
      defaultValue: '100%',
      showIf: 'options.get("showMainInvestButton")',
    },
    {
      name: 'mainInvestButtonMaxWidth',
      friendlyName: 'Section Button Max Width',
      type: 'string',
      defaultValue: '327px',
      showIf: 'options.get("showMainInvestButton")',
    },
    {
      name: 'mainInvestButtonPadding',
      friendlyName: 'Section Button Padding',
      type: 'string',
      defaultValue: '12px 24px',
      showIf: 'options.get("showMainInvestButton")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
    overflow: 'visible',
  },
});

// ============================================================
// DISCUSSION SECTION - Comment feed with post functionality
// ============================================================
Builder.registerComponent(BuilderDiscussionSection, {
  name: 'DiscussionSection',
  description: 'Comment feed with user discussion, posting, and upvotes.',
  friendlyName: 'Data: Discussion',
  image: ICONS.discussion,
  canHaveChildren: false,
  priority: 23,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic data, title, input + post button,
    // pagination behavior, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'useDynamicData',
      friendlyName: 'Use Dynamic Comment Thread',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Use offering thread, OFF = Manual thread ID',
    },
    {
      name: 'externalEntityIdOverride',
      friendlyName: 'Comment Thread ID',
      type: 'string',
      helperText: 'Provide a thread ID when dynamic data is off',
      showIf: '!options.get("useDynamicData")',
    },
    {
      name: 'sectionTitle',
      friendlyName: '✍️ Section Title',
      type: 'string',
      defaultValue: 'Join the Discussion',
    },
    {
      name: 'sectionTitleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'inputPlaceholder',
      friendlyName: '💬 Input Placeholder',
      type: 'string',
      defaultValue: "What's on your mind?",
    },
    {
      name: 'inputBackgroundColor',
      friendlyName: 'Input Background',
      type: 'color',
      defaultValue: THEME.lightBg,
    },
    {
      name: 'inputTextColor',
      friendlyName: 'Input Text Color',
      type: 'color',
      defaultValue: THEME.textDark,
    },
    {
      name: 'postButtonText',
      friendlyName: '🔘 Post Button Text',
      type: 'string',
      defaultValue: 'Post',
    },
    {
      name: 'postButtonBackgroundColor',
      friendlyName: 'Post Button Background',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'postButtonTextColor',
      friendlyName: 'Post Button Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    ...BUTTON_SIZE_INPUTS('postButton'),
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Input/avatar/comment/pagination styling, background, spacing
    // ═══════════════════════════════════════════════════════════
    {
      name: 'avatarBackgroundColor',
      friendlyName: '🎨 Avatar Background',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'avatarTextColor',
      friendlyName: 'Avatar Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'commentTextColor',
      friendlyName: '🎨 Comment Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'commentMetaColor',
      friendlyName: 'Comment Meta Color',
      type: 'color',
      defaultValue: THEME.textMuted,
    },
    {
      name: 'showMoreColor',
      friendlyName: 'Show More Link Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'commentBorderColor',
      friendlyName: 'Comment Border Color',
      type: 'color',
      defaultValue: 'rgba(255, 255, 255, 0.1)',
    },
    {
      name: 'commentNavigation',
      friendlyName: 'Comment Navigation',
      type: 'string',
      enum: ['pagination', 'load-more'],
      defaultValue: 'pagination',
      helperText: 'Pagination shows arrows, Load more shows a button',
    },
    {
      name: 'paginationButtonBackgroundColor',
      friendlyName: 'Pagination Active',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'paginationButtonTextColor',
      friendlyName: 'Pagination Text',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'paginationButtonInactiveBackgroundColor',
      friendlyName: 'Pagination Inactive',
      type: 'color',
      defaultValue: '#374151',
    },
    ...SECTION_BACKGROUND_INPUTS,
    ...SECTION_SPACING_INPUTS,
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// OFFERING PAGE SECTIONS - Reuse native offering components
// ============================================================
Builder.registerComponent(BuilderHowInvestingWorksSection, {
  name: 'HowInvestingWorksSection',
  description: 'How investing works timeline from the offering page.',
  friendlyName: 'Data: How It Works',
  image: ICONS.howItWorks,
  canHaveChildren: false,
  priority: 24,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, subtitle override, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'showSubtitle',
      friendlyName: '📝 Show Subtitle',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'Toggle OFF to hide the cancellation subtitle entirely. When ON, it auto-detects based on regulation type unless you override below.',
    },
    {
      name: 'subtitle',
      friendlyName: 'Subtitle Override',
      type: 'richText',
      defaultValue: '',
      helperText:
        'Leave empty to auto-detect based on regulation type (Reg A shows cancellation language, Reg D shows nothing). Enter custom text to override.',
      showIf: 'options.get("showSubtitle")',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for default dark color',
      showIf: 'options.get("showSubtitle")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_SPACING_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

Builder.registerComponent(BuilderWhyStartEngineSection, {
  name: 'WhyStartEngineSection',
  description: 'Why StartEngine content from the offering page.',
  friendlyName: 'Data: Why StartEngine',
  image: ICONS.whySE,
  canHaveChildren: false,
  priority: 25,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_SPACING_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// COMPANY HIGHLIGHTS / PITCH DECK - Dynamic content from API
// ============================================================
Builder.registerComponent(BuilderCompanyHighlightsSection, {
  name: 'CompanyHighlightsSection',
  description: 'Renders the campaign page / pitch deck HTML content from the offering data.',
  friendlyName: 'Data: Pitch Deck (Legacy Froala)',
  image: ICONS.pitchDeck,
  canHaveChildren: false,
  hideFromInsertMenu: true,
  priority: 26,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic data, custom title, disclaimer,
    // invest button (deprecated/hidden component — kept for legacy)
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links (e.g., "highlights")',
    },
    {
      name: 'useDynamicData',
      friendlyName: '📡 Use Dynamic Data',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Pull pitch deck content from the offering API',
    },
    {
      name: 'showTitle',
      friendlyName: '✍️ Show Custom Title',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Show a custom title above the pitch deck content',
    },
    {
      name: 'titleText',
      friendlyName: 'Title Text',
      type: 'string',
      defaultValue: 'THE PITCH',
      helperText: 'Use | for line breaks',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '28px',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      defaultValue: 800,
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleLetterSpacing',
      friendlyName: 'Title Letter Spacing',
      type: 'string',
      defaultValue: '0.02em',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleMarginBottom',
      friendlyName: 'Title Margin Bottom (px)',
      type: 'number',
      defaultValue: 32,
      showIf: 'options.get("showTitle")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Image sizing, background, padding
    // ═══════════════════════════════════════════════════════════
    {
      name: 'imageMaxWidth',
      friendlyName: '🖼️ Image Max Width (Desktop)',
      type: 'string',
      defaultValue: '740px',
      helperText: 'Max width for pitch deck images on desktop (e.g. 50%, 740px, 100%)',
    },
    {
      name: 'mobileImageMaxWidth',
      friendlyName: '📱 Image Max Width (Mobile)',
      type: 'string',
      defaultValue: '100%',
      helperText:
        'Max width for pitch deck images on mobile. Small images (team headshots) are protected automatically.',
    },
    {
      name: 'backgroundColor',
      friendlyName: '🎨 Background Color',
      type: 'color',
      defaultValue: 'transparent',
    },
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding (px)',
      type: 'number',
      defaultValue: 40,
      helperText: 'Top and bottom padding in px',
    },
    {
      name: 'horizontalPadding',
      friendlyName: '↔️ Horizontal Padding (px)',
      type: 'number',
      defaultValue: 24,
      helperText: 'Left and right padding in px',
    },
    {
      name: 'maxContentWidth',
      friendlyName: 'Max Content Width',
      type: 'string',
      defaultValue: '1200px',
      helperText: 'Maximum width of the content area (e.g., 740px, 900px, 100%)',
    },
    {
      name: 'showDisclaimer',
      friendlyName: '⚖️ Show Disclaimer',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      defaultValue: '',
      helperText: 'Disclaimer text shown below the pitch deck content',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: '#6B7280',
      showIf: 'options.get("showDisclaimer")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

Builder.registerComponent(BuilderFAQSection, {
  name: 'FAQSection',
  description: 'FAQs pulled from the offering data.',
  friendlyName: 'Data: FAQs',
  image: ICONS.faq,
  canHaveChildren: false,
  priority: 27,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, title toggle, more-link toggle, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'showTitle',
      friendlyName: 'Show Title',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showMoreLink',
      friendlyName: 'Show More FAQs Link',
      type: 'boolean',
      defaultValue: true,
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_SPACING_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// ACCORDION SECTION - Customizable expandable content
// ============================================================
Builder.registerComponent(BuilderAccordionSection, {
  name: 'AccordionSection',
  description:
    'Expandable accordion with title, category, and rich body content per item (Tech Areas).',
  friendlyName: 'Story: Accordion',
  image: ICONS.accordion,
  canHaveChildren: false,
  priority: 15,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, title, items, behavior toggle, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'title',
      friendlyName: '📝 Section Title',
      type: 'string',
      defaultValue: 'About the Technology Areas',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titleColor',
      friendlyName: '🎨 Title / Item Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#6b6b6b',
    },
    {
      name: 'bodyColor',
      friendlyName: 'Body Text Color',
      type: 'color',
      defaultValue: '#6b6b6b',
    },
    {
      name: 'items',
      friendlyName: '📋 Accordion Items',
      type: 'list',
      defaultValue: [
        {
          title: 'Item One',
          subtitle: 'Short description',
          body: '<p>Expanded content goes here with details about this item.</p>',
        },
        {
          title: 'Item Two',
          subtitle: 'Another description',
          body: '<p>More detailed content for the second item.</p>',
        },
        {
          title: 'Item Three',
          subtitle: 'Third description',
          body: '<p>Content for the third accordion item.</p>',
        },
      ],
      subFields: [
        {
          name: 'icon',
          friendlyName: 'Icon / Logo',
          type: 'file',
          allowedFileTypes: ['svg', 'png', 'webp', 'jpeg', 'jpg'],
          helperText: 'Optional icon or logo shown next to the title',
        },
        {
          name: 'title',
          friendlyName: 'Title',
          type: 'string',
          defaultValue: 'Item Title',
        },
        {
          name: 'titleSuperscript',
          friendlyName: 'Title Footnote #',
          type: 'string',
        },
        {
          name: 'subtitle',
          friendlyName: 'Subtitle',
          type: 'string',
          defaultValue: '',
          helperText: 'Lighter text next to title (optional)',
        },
        {
          name: 'body',
          friendlyName: 'Body Content',
          type: 'richText',
          defaultValue: '<p>Content here...</p>',
        },
      ],
    },
    {
      name: 'allowMultipleOpen',
      friendlyName: 'Allow Multiple Open',
      type: 'boolean',
      defaultValue: false,
      helperText: 'If ON, multiple items can be expanded at once',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_BACKGROUND_INPUTS,
    ...SECTION_SPACING_INPUTS.map((input) =>
      input.name === 'maxWidth' ? { ...input, defaultValue: 1280 } : input,
    ),
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Toggle button styling
    // ═══════════════════════════════════════════════════════════
    {
      name: 'toggleButtonColor',
      friendlyName: 'Toggle Button Color',
      type: 'color',
      defaultValue: '#1A1A1A',
      advanced: true,
    },
    {
      name: 'toggleIconColor',
      friendlyName: 'Toggle Icon Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

Builder.registerComponent(BuilderAboutSection, {
  name: 'AboutSection',
  description: 'Company info (HQ, website, elevator pitch) from offering data.',
  friendlyName: 'Data: About',
  image: ICONS.about,
  canHaveChildren: false,
  priority: 28,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, content toggles, label/title colors, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
    },
    {
      name: 'sectionLabelSuperscript',
      friendlyName: 'Label Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number for the section label',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'showHeadquarters',
      friendlyName: '📍 Show Headquarters',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showWebsite',
      friendlyName: '🔗 Show Website',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showElevatorPitch',
      friendlyName: '📝 Show Elevator Pitch',
      type: 'boolean',
      defaultValue: true,
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'backgroundColor',
      friendlyName: '🎨 Background',
      type: 'color',
      defaultValue: 'transparent',
    },
    ...SECTION_SPACING_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

Builder.registerComponent(BuilderRewardsSection, {
  name: 'RewardsSection',
  description: 'Rewards section from the offering data.',
  friendlyName: 'Data: Rewards',
  image: ICONS.rewards,
  canHaveChildren: false,
  priority: 29,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, invest button
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_SPACING_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// CTA SECTION - Call-to-action with invest button
// ============================================================
Builder.registerComponent(BuilderCTASection, {
  name: 'CTASection',
  description: 'Call-to-action section with title, subtitle, and optional button.',
  friendlyName: 'Story: CTA',
  image: ICONS.cta,
  priority: 9,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, title, body, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'title',
      friendlyName: '✍️ Title',
      type: 'string',
      defaultValue: "Join us today. When we change data, | we're going to change everything.",
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titleColor',
      friendlyName: '🎨 Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: 'change everything.',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleHighlightItalic',
      friendlyName: 'Highlight Italic',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'subtitle',
      friendlyName: 'Subtitle',
      type: 'richText',
      defaultValue: '',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: THEME.textSecondary,
      showIf: 'options.get("subtitle")',
    },
    {
      name: 'showButton',
      friendlyName: '🔘 Show Button',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'buttonText',
      friendlyName: 'Button Text',
      type: 'string',
      defaultValue: 'Invest Now',
      showIf: 'options.get("showButton")',
    },
    {
      name: 'buttonBackgroundColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showButton")',
    },
    {
      name: 'buttonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: '#1A1A2E',
      showIf: 'options.get("showButton")',
    },
    {
      name: 'buttonHoverBackgroundColor',
      friendlyName: 'Button Hover',
      type: 'color',
      defaultValue: '#F0F0F0',
      showIf: 'options.get("showButton")',
    },
    {
      name: 'buttonBorderRadius',
      friendlyName: 'Button Radius',
      type: 'string',
      defaultValue: '50px',
      showIf: 'options.get("showButton")',
    },
    {
      name: 'buttonMarginTop',
      friendlyName: 'Button Spacing',
      type: 'number',
      defaultValue: 32,
      helperText: 'Distance between text and button (in pixels)',
      showIf: 'options.get("showButton")',
    },
    ...BUTTON_SIZE_INPUTS('button', 'showButton'),
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 50,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// RTI SECTION - Reasons to Invest
// ============================================================
Builder.registerComponent(BuilderRTISection, {
  name: 'RTISection',
  description: 'Numbered reasons to invest with icons and dividers.',
  friendlyName: 'Story: Reasons to Invest',
  image: ICONS.rti,
  canHaveChildren: false,
  priority: 14,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic-data, eyebrow, title, body,
    // reasons list, dividers, disclaimer, buttons
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'ID for navigation links (e.g., "reasons-to-invest")',
    },
    {
      name: 'useOfferingData',
      friendlyName: '🔄 Pull from Offering Data',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'ON = Auto-load reasons & disclaimers from the offering API (individual reasons can still be overridden below). OFF = "Reasons (Manual / Override)" below is used as the full list instead.',
    },
    {
      name: 'sectionLabel',
      friendlyName: '✍️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small text above title (e.g., "WHY INVEST")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: '🎨 Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'title',
      friendlyName: 'Title',
      type: 'string',
      defaultValue: 'REASONS TO INVEST',
      helperText: 'Use | for line breaks',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Words to Highlight',
      type: 'string',
      defaultValue: '',
      helperText: 'These words will be highlighted in accent color',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'subtitle',
      friendlyName: 'Subtitle',
      type: 'richText',
      defaultValue: '',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: THEME.textSecondary,
      showIf: 'options.get("subtitle")',
    },
    {
      name: 'reasons',
      friendlyName: '📝 Reasons (Manual / Override)',
      type: 'list',
      helperText:
        'Leave empty to fully auto-pull from the offering. To override ONE auto-pulled reason, set ID to its position (1, 2, 3…) and fill in Title/Description — the rest keep pulling from the offering. Add an ID that doesn\'t match any position (e.g. "4") to append an extra reason. When "Pull from Offering Data" is OFF, this list is used as-is instead.',
      subFields: [
        {
          name: 'id',
          friendlyName: 'Position to Override',
          type: 'string',
          required: true,
          helperText: 'e.g. "2" overrides the 2nd auto-pulled reason',
        },
        {
          name: 'title',
          friendlyName: 'Title (optional)',
          type: 'string',
          helperText: 'Bold heading for this reason. Leave blank to keep the auto-pulled title.',
        },
        {
          name: 'text',
          friendlyName: 'Description',
          type: 'richText',
          helperText: 'Leave blank to keep the auto-pulled description.',
        },
      ],
      defaultValue: [],
    },
    {
      name: 'showDisclaimer',
      friendlyName: '⚠️ Show Disclaimer',
      type: 'boolean',
      defaultValue: true,
      helperText: 'When "Pull from Offering Data" is ON, disclaimers are loaded automatically',
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text (Override)',
      type: 'richText',
      showIf: 'options.get("showDisclaimer")',
      helperText: 'Overrides API disclaimer when provided. Leave empty to use offering data.',
    },
    {
      name: 'disclaimerColor',
      friendlyName: 'Disclaimer Color',
      type: 'color',
      defaultValue: THEME.textMuted,
      showIf: 'options.get("showDisclaimer") || options.get("useOfferingData")',
    },
    {
      name: 'disclaimerFontSize',
      friendlyName: 'Disclaimer Font Size',
      type: 'string',
      defaultValue: '12px',
      showIf: 'options.get("showDisclaimer") || options.get("useOfferingData")',
    },
    ...SECTION_INVEST_BUTTON_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Variant, alignment, icons, dividers,
    // reason styling, background, padding, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'layout',
      friendlyName: '📐 Layout Style',
      type: 'enum',
      enum: ['auto', 'regA', 'regCF'],
      defaultValue: 'auto',
      helperText:
        'Auto detects from regulation type. Reg A = separate title/text. Reg CF = inline paragraph, narrower centered list.',
    },
    {
      name: 'contentMaxWidth',
      friendlyName: 'Reasons List Max Width (px)',
      type: 'number',
      defaultValue: 550,
      helperText: 'Max width of the reasons list in Reg CF layout',
      showIf: 'options.get("layout") === "regCF" || options.get("layout") === "auto"',
    },
    {
      name: 'headerAlignment',
      friendlyName: '📐 Header Alignment',
      type: 'enum',
      enum: ['left', 'center'],
      defaultValue: 'center',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '36px',
      helperText: 'e.g. 28px, 36px, 42px, 48px',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '28px',
      helperText: 'Font size on screens < 600px',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      defaultValue: 700,
      helperText: '400 = normal, 600 = semi-bold, 700 = bold, 800 = extra-bold',
    },
    {
      name: 'titleLineHeight',
      friendlyName: 'Title Line Height',
      type: 'string',
      defaultValue: '1.2',
    },
    {
      name: 'titleLetterSpacing',
      friendlyName: 'Title Letter Spacing',
      type: 'string',
      defaultValue: '-0.01em',
    },
    {
      name: 'titleTextTransform',
      friendlyName: 'Title Text Transform',
      type: 'enum',
      enum: ['none', 'uppercase', 'lowercase', 'capitalize'],
      defaultValue: 'uppercase',
    },
    {
      name: 'titleMarginBottom',
      friendlyName: 'Title Spacing Below',
      type: 'string',
      defaultValue: '16px',
      helperText: 'Space between title and content below',
    },
    {
      name: 'iconType',
      friendlyName: '🔢 Icon Style',
      type: 'enum',
      enum: ['default', 'clock', 'checkmark'],
      defaultValue: 'default',
      helperText: 'Default (OG image), clock SVG, or checkmark circle',
    },
    {
      name: 'customIcon',
      friendlyName: 'Custom Icon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      helperText: 'Upload a custom icon to override the default',
    },
    {
      name: 'iconColor',
      friendlyName: 'Icon Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: '!options.get("customIcon")',
    },
    {
      name: 'reasonTitleColor',
      friendlyName: '🎨 Reason Title Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'reasonTitleFontSize',
      friendlyName: 'Reason Title Font Size',
      type: 'string',
      defaultValue: '18px',
    },
    {
      name: 'reasonTitleFontWeight',
      friendlyName: 'Reason Title Font Weight',
      type: 'number',
      defaultValue: 700,
    },
    {
      name: 'reasonTextColor',
      friendlyName: 'Reason Text Color',
      type: 'color',
      defaultValue: THEME.textSecondary,
    },
    {
      name: 'reasonTextFontSize',
      friendlyName: 'Reason Text Font Size',
      type: 'string',
      defaultValue: '16px',
    },
    {
      name: 'showDividers',
      friendlyName: '➖ Show Dividers',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'dividerStyle',
      friendlyName: 'Divider Style',
      type: 'enum',
      enum: ['dotted', 'dashed', 'solid'],
      defaultValue: 'dotted',
      showIf: 'options.get("showDividers")',
    },
    {
      name: 'dividerColor',
      friendlyName: 'Divider Color',
      type: 'color',
      defaultValue: '#d4d0cb',
      showIf: 'options.get("showDividers")',
    },
    ...SECTION_BACKGROUND_INPUTS_WITH_MOBILE,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 40,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width (px)',
      type: 'number',
      defaultValue: 1200,
    },
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Icon size
    // ═══════════════════════════════════════════════════════════
    {
      name: 'iconSize',
      friendlyName: 'Icon Size (px)',
      type: 'number',
      defaultValue: 30,
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// FOOTER SECTION - Page footer with logo, disclaimer, invest button
// ============================================================
Builder.registerComponent(BuilderFooterSection, {
  name: 'FooterSection',
  description: 'Page footer with company logo, disclaimer, invest button, and links.',
  friendlyName: 'Page: Footer',
  image: ICONS.footer,
  canHaveChildren: false,
  priority: 99,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, dynamic-data, logo, company, disclaimer,
    // invest button, copyright + footer links
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'useDynamicData',
      friendlyName: '🔄 Auto-Update Company Info',
      type: 'boolean',
      defaultValue: true,
      helperText: 'ON = Use offering data, OFF = Manual',
    },
    {
      name: 'showLogo',
      friendlyName: '🏢 Show Logo',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'logoUrl',
      friendlyName: 'Logo Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      helperText: 'Empty = use offering logo',
      showIf: 'options.get("showLogo")',
    },
    {
      name: 'showCompanyName',
      friendlyName: 'Show Company Name',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'companyName',
      friendlyName: 'Company Name',
      type: 'string',
      defaultValue: '',
      helperText: 'Empty = use offering name',
      showIf: 'options.get("showCompanyName")',
    },
    {
      name: 'companyNameColor',
      friendlyName: 'Company Name Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
      showIf: 'options.get("showCompanyName")',
    },
    {
      name: 'disclaimerTitle',
      friendlyName: '⚖️ Disclaimer Title',
      type: 'string',
      defaultValue: 'LEGAL DISCLAIMER',
    },
    {
      name: 'disclaimerTitleColor',
      friendlyName: 'Disclaimer Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'disclaimerTitleSuperscript',
      friendlyName: 'Disclaimer Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      defaultValue:
        'This Reg CF offering is made available through StartEngine Primary, LLC, a member of FINRA/SIPC. This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment.',
    },
    {
      name: 'disclaimerTextColor',
      friendlyName: 'Disclaimer Text Color',
      type: 'color',
      defaultValue: THEME.textMuted,
    },
    {
      name: 'showInvestButton',
      friendlyName: '🔘 Show Invest Button',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'investButtonText',
      friendlyName: 'Button Text',
      type: 'string',
      defaultValue: 'Get Shares Now',
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'investButtonBackgroundColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: THEME.ctaButton,
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'investButtonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: THEME.ctaButtonText,
      showIf: 'options.get("showInvestButton")',
    },
    {
      name: 'copyrightText',
      friendlyName: '©️ Copyright Text',
      type: 'string',
      defaultValue: '© 2025 Company® All Rights Reserved',
    },
    {
      name: 'copyrightTextColor',
      friendlyName: 'Copyright Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'privacyPolicyText',
      friendlyName: 'Privacy Policy Text',
      type: 'string',
      defaultValue: 'Privacy Policy',
    },
    {
      name: 'privacyPolicyUrl',
      friendlyName: 'Privacy Policy URL',
      type: 'string',
      defaultValue: '/privacy',
    },
    {
      name: 'termsText',
      friendlyName: 'Terms Text',
      type: 'string',
      defaultValue: 'Terms & Conditions',
    },
    {
      name: 'termsUrl',
      friendlyName: 'Terms URL',
      type: 'string',
      defaultValue: '/terms',
    },
    {
      name: 'linksColor',
      friendlyName: 'Links Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Divider, background
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showDivider',
      friendlyName: 'Show Divider',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'dividerColor',
      friendlyName: 'Divider Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showDivider")',
    },
    ...SECTION_BACKGROUND_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // TYPOGRAPHY & FONTS
    // ═══════════════════════════════════════════════════════════
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Logo height, button radius/sizing, link separator
    // ═══════════════════════════════════════════════════════════
    {
      name: 'logoMaxHeight',
      friendlyName: 'Logo Max Height',
      type: 'string',
      defaultValue: '80px',
      advanced: true,
    },
    {
      name: 'investButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '30px',
      advanced: true,
    },
    ...BUTTON_SIZE_INPUTS('investButton'),
    {
      name: 'linksSeparator',
      friendlyName: 'Links Separator',
      type: 'string',
      defaultValue: '|',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// INVEST BUTTON - Smart button with offering data integration
// ============================================================
Builder.registerComponent(BuilderInvestButton, {
  name: 'InvestButton',
  description: 'Standalone invest button. Drag anywhere on the page.',
  friendlyName: 'CTA: Invest Button',
  image: ICONS.invest,
  canHaveChildren: false,
  priority: 40,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Button text, feature toggles, additional content
    // ═══════════════════════════════════════════════════════════
    {
      name: 'buttonText',
      friendlyName: 'Button Text',
      type: 'string',
      defaultValue: 'Invest Now',
      helperText: 'Primary button text',
    },
    {
      name: 'showPricePerShare',
      friendlyName: 'Show Price/Rate Info',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Display price per share, interest rate, or discount below button text',
    },
    {
      name: 'additionalText',
      friendlyName: 'Additional Text',
      type: 'string',
      helperText: 'Optional promotional text below the button',
    },
    {
      name: 'additionalTextColor',
      friendlyName: 'Additional Text Color',
      type: 'color',
      showIf: 'options.get("additionalText")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Colors
    // ═══════════════════════════════════════════════════════════
    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: THEME.ctaButton,
    },
    {
      name: 'textColor',
      friendlyName: 'Text Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Sizing, font tweaks, IRA disclaimer toggle, slug override
    // ═══════════════════════════════════════════════════════════
    {
      name: 'borderRadius',
      friendlyName: 'Border Radius',
      type: 'string',
      defaultValue: '8px',
      advanced: true,
    },
    {
      name: 'fontSize',
      friendlyName: 'Font Size',
      type: 'string',
      defaultValue: '16px',
      advanced: true,
    },
    {
      name: 'fontWeight',
      friendlyName: 'Font Weight',
      type: 'number',
      defaultValue: 600,
      enum: [400, 500, 600, 700],
      advanced: true,
    },
    {
      name: 'padding',
      friendlyName: 'Padding',
      type: 'string',
      defaultValue: '12px 24px',
      helperText: 'CSS padding value',
      advanced: true,
    },
    {
      name: 'width',
      friendlyName: 'Button Width',
      type: 'string',
      defaultValue: 'auto',
      helperText: 'e.g. auto, 100%, 200px, 300px',
      advanced: true,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'string',
      defaultValue: '327px',
      helperText: 'Maximum button width',
      advanced: true,
    },
    {
      name: 'showIraDisclaimer',
      friendlyName: 'Show IRA Disclaimer',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show IRA disclaimer for IRA-only offerings',
      advanced: true,
    },
    {
      name: 'offeringSlug',
      friendlyName: 'Offering Slug',
      type: 'string',
      helperText:
        'The offering slug (usually auto-detected from URL). Only set this if you need to override.',
      advanced: true,
    },
  ],
  // Default styles for the wrapper
  defaultStyles: {
    marginTop: '20px',
    marginBottom: '20px',
    paddingTop: '0px',
    paddingBottom: '0px',
    display: 'flex',
    justifyContent: 'center',
  },
});

// ============================================================
// SCHEDULE CALL BUBBLE - Floating contact card
// ============================================================
Builder.registerComponent(BuilderScheduleCallBubble, {
  name: 'ScheduleCallBubble',
  friendlyName: 'Floating: Schedule a Call',
  description: 'Floating schedule-a-call card on the right side of the page (Reg D only).',
  image: ICONS.bubble,
  canHaveChildren: false,
  priority: 41,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Visibility, content, calendar URL
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showBubble',
      friendlyName: '👁️ Show Bubble',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Tip: in Builder, select from Layers if it is off-canvas.',
    },
    {
      name: 'showOnMobile',
      friendlyName: '📱 Show On Mobile',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'expandedByDefault',
      friendlyName: 'Expanded By Default',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showMinimize',
      friendlyName: 'Allow Minimize',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'rememberDismissal',
      friendlyName: '💾 Remember Dismissal',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'When ON, dismissal persists for the browser session. When OFF, card reappears on every page visit.',
      showIf: 'options.get("showMinimize")',
    },
    {
      name: 'minimizedLabel',
      friendlyName: 'Minimized Label',
      type: 'string',
      defaultValue: 'Schedule a call',
      showIf: 'options.get("showMinimize")',
    },
    {
      name: 'headline',
      friendlyName: '✍️ Headline',
      type: 'string',
      defaultValue: 'Need Help?',
    },
    {
      name: 'description',
      friendlyName: 'Link Text',
      type: 'string',
      defaultValue: 'Schedule a call with an Associate',
      helperText: 'Green clickable text that links to the calendar',
    },
    {
      name: 'subtitleText',
      friendlyName: 'Subtitle Text',
      type: 'string',
      defaultValue: '9AM - 5PM PST • Mon - Fri',
      helperText: 'Small text below the link (e.g. hours of operation)',
    },
    {
      name: 'subtitleColor',
      friendlyName: 'Subtitle Color',
      type: 'color',
      defaultValue: '#64748B',
    },
    {
      name: 'calendarUrlOverride',
      friendlyName: 'Calendar URL Override',
      type: 'string',
      helperText: 'Optional override for the schedule call link',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Card style, positioning
    // ═══════════════════════════════════════════════════════════
    {
      name: 'cardBackground',
      friendlyName: '🎨 Card Background',
      type: 'string',
      defaultValue: 'linear-gradient(267deg, #EFE9E0 14.5%, #F9F5F0 70.58%)',
      helperText: 'CSS background value (color, gradient, etc.)',
    },
    {
      name: 'cardBorderColor',
      friendlyName: 'Card Border Color',
      type: 'color',
      defaultValue: '#F0EAE0',
    },
    {
      name: 'buttonTextColor',
      friendlyName: 'Link Text Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for default SE green',
    },
    {
      name: 'positionMode',
      friendlyName: '📍 Position Mode',
      type: 'string',
      enum: ['fixed', 'sticky'],
      defaultValue: 'fixed',
      helperText: 'Fixed floats over the page, sticky stays within its section',
    },
    {
      name: 'topOffset',
      friendlyName: 'Top Offset (px)',
      type: 'number',
      defaultValue: 160,
    },
    {
      name: 'rightOffset',
      friendlyName: 'Right Offset (px)',
      type: 'number',
      defaultValue: 24,
    },
    {
      name: 'bottomOffset',
      friendlyName: 'Bottom Offset (px)',
      type: 'number',
      defaultValue: 24,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width (px)',
      type: 'number',
      defaultValue: 300,
    },
    {
      name: 'minimizedPosition',
      friendlyName: 'Minimized Position',
      type: 'string',
      enum: ['bottom-left', 'bottom-right'],
      defaultValue: 'bottom-right',
      showIf: 'options.get("showMinimize")',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// GET EQUITY BUBBLE - Floating invest summary card
// ============================================================
Builder.registerComponent(BuilderGetEquityBubble, {
  name: 'GetEquityBubble',
  friendlyName: 'Floating: Get Shares Now',
  description: 'Floating invest summary card with price, min investment, and CTA button.',
  image: ICONS.bubble,
  canHaveChildren: false,
  priority: 42,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Visibility, dynamic data, content + labels, manual overrides,
    // badges, disclosure, mobile sticky bar
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showBubble',
      friendlyName: '👁️ Show Bubble',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'useDynamicData',
      friendlyName: '📡 Use Dynamic Data',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'forceReserveCta',
      friendlyName: '🟧 Force Reserve Now CTA (TTW Mode)',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Toggle ON to flip the floating bubble to the orange "Reserve Now" button + /reserve URL. Use for Reg A+ / Reg CF Test-The-Waters previews where the BE doesn\'t expose a TTW signal yet (e.g. gacw-reg-a, status `not_submitted`). The four unambiguous TTW statuses auto-flip without this toggle. Requires "Use Dynamic Data" ON — the bubble needs the offering payload to compute the /reserve route.',
      showIf: 'options.get("useDynamicData")',
    },
    {
      name: 'expandedByDefault',
      friendlyName: 'Expanded By Default',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showMinimize',
      friendlyName: 'Allow Minimize',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'rememberDismissal',
      friendlyName: '💾 Remember Dismissal',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'When ON, dismissal persists for the browser session. When OFF, card reappears on every page visit.',
      showIf: 'options.get("showMinimize")',
    },
    {
      name: 'minimizedLabel',
      friendlyName: 'Minimized Label',
      type: 'string',
      defaultValue: 'Get Shares Now',
      showIf: 'options.get("showMinimize")',
    },
    {
      name: 'showMobileStickyBar',
      friendlyName: '📱 Show Mobile Sticky Bar',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Shows a sticky bottom bar on mobile with price + invest button',
    },
    {
      name: 'mobileBarShowPrice',
      friendlyName: '📱 Mobile: Show Price',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("showMobileStickyBar")',
    },
    {
      name: 'mobileBarButtonText',
      friendlyName: '📱 Mobile: Button Text Override',
      type: 'string',
      defaultValue: '',
      helperText: 'Leave blank to use the main Button Text',
      showIf: 'options.get("showMobileStickyBar")',
    },
    {
      name: 'mobileBarBackgroundColor',
      friendlyName: '📱 Mobile: Bar Background',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showMobileStickyBar")',
    },
    {
      name: 'titleText',
      friendlyName: '📌 Title Text',
      type: 'string',
      defaultValue: 'Get Shares Now',
      helperText: 'Heading shown at the top of the bubble (e.g. "Get Shares Now" or "Get Equity")',
    },
    {
      name: 'titleSuperscript',
      friendlyName: 'Title Footnote #',
      type: 'string',
      defaultValue: '',
      helperText: 'Superscript footnote number (e.g. "1", "*")',
    },
    {
      name: 'buttonText',
      friendlyName: '✍️ Button Text',
      type: 'string',
      defaultValue: 'Get Shares Now',
    },
    {
      name: 'waitlistButtonText',
      friendlyName: '✍️ Waitlist Button Text',
      type: 'string',
      defaultValue: 'Join the Waitlist',
      helperText: 'Shown when offering is over-subscribed',
    },
    {
      name: 'reserveButtonText',
      friendlyName: '✍️ Reserve Button Text (TTW)',
      type: 'string',
      defaultValue: 'Reserve Now',
      helperText:
        'Used automatically when the offering is in Test-the-Waters status. Matches the sticky sidebar / mobile bar / hero copy.',
    },
    {
      name: 'pricePerShareLabel',
      friendlyName: 'Price Label',
      type: 'string',
      defaultValue: 'Per Share',
    },
    {
      name: 'showPricePerShare',
      friendlyName: 'Show Price Per Share',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'minInvestLabel',
      friendlyName: 'Min Invest Label',
      type: 'string',
      defaultValue: 'Min Invest',
    },
    {
      name: 'showMinInvest',
      friendlyName: 'Show Min Invest',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'fundingGoalLabel',
      friendlyName: 'Funding Goal Label',
      type: 'string',
      defaultValue: 'Funding Goal',
    },
    {
      name: 'showFundingGoal',
      friendlyName: 'Show Funding Goal',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'manualPricePerShare',
      friendlyName: 'Manual Price',
      type: 'string',
      defaultValue: '$0.00',
      showIf: '!options.get("useDynamicData")',
    },
    {
      name: 'manualMinInvest',
      friendlyName: 'Manual Min Invest',
      type: 'string',
      defaultValue: '$0.00',
      showIf: '!options.get("useDynamicData")',
    },
    {
      name: 'manualFundingGoal',
      friendlyName: 'Manual Funding Goal',
      type: 'string',
      defaultValue: '$0.00',
      showIf: '!options.get("useDynamicData")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Button styling, badges, disclosure, positioning
    // ═══════════════════════════════════════════════════════════
    {
      name: 'buttonBackgroundColor',
      friendlyName: '🎨 Button Color',
      type: 'color',
      defaultValue: '#F47F2B',
    },
    {
      name: 'reserveButtonBackgroundColor',
      friendlyName: '🎨 Reserve Button Color (TTW)',
      type: 'color',
      defaultValue: '#E06004',
      helperText:
        'Used automatically when the offering is in Test-the-Waters status. Matches the sidebar Reserve Now button.',
    },
    {
      name: 'buttonTextColor',
      friendlyName: '🎨 Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'buttonWidth',
      friendlyName: 'Button Width',
      type: 'string',
      defaultValue: '327px',
      helperText: 'e.g. 327px, 100%, auto, 280px',
    },
    {
      name: 'buttonPadding',
      friendlyName: 'Button Padding',
      type: 'string',
      defaultValue: '11px 16px',
      helperText: 'CSS padding e.g. 11px 16px, 14px 24px',
    },
    {
      name: 'showBadges',
      friendlyName: '🏆 Show Criteria Badges',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show "Most Momentum" and/or "Most Funded" badges in the card.',
    },
    {
      name: 'badgesMode',
      friendlyName: '🏆 Badges Mode',
      type: 'string',
      defaultValue: 'auto',
      enum: [
        { label: 'Auto (from offering data)', value: 'auto' },
        { label: 'Most Momentum only', value: 'momentum' },
        { label: 'Most Funded only', value: 'popular' },
        { label: 'Both badges', value: 'both' },
      ],
      helperText:
        'Auto: detected from offering API. Or force specific badges for testing/override.',
      showIf: 'options.get("showBadges")',
    },
    {
      name: 'showDisclosure',
      friendlyName: '📜 Show Disclosure Text',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show regulatory disclosure text below the invest button',
    },
    {
      name: 'manualDisclosureText',
      friendlyName: 'Disclosure Text (Manual Override)',
      type: 'longText',
      defaultValue: '',
      helperText:
        'Leave empty to auto-generate from offering data (regulation type). Enter text to override.',
      showIf: 'options.get("showDisclosure")',
    },
    {
      name: 'rightOffset',
      friendlyName: '📍 Right Offset (px)',
      type: 'number',
      defaultValue: 24,
    },
    {
      name: 'bottomOffset',
      friendlyName: 'Bottom Offset (px)',
      type: 'number',
      defaultValue: 24,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width (px)',
      type: 'number',
      defaultValue: 320,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 27. VIDEO EMBED - YouTube, Vimeo, and custom embed codes
// ============================================================
Builder.registerComponent(BuilderVideoEmbed, {
  name: 'Video Embed',
  friendlyName: 'Utility: Video Embed',
  description:
    'Embed YouTube, Vimeo, or custom video players. Mobile click-to-play shows a thumbnail with play button for better performance on phones.',
  image: ICONS.video,
  priority: 3,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, video source, playback config
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'videoUrl',
      friendlyName: 'Video URL',
      type: 'text',
      helperText:
        'YouTube or Vimeo URL (e.g., https://www.youtube.com/watch?v=... or https://vimeo.com/...)',
    },
    {
      name: 'nativeVideoUrl',
      friendlyName: 'Native MP4 / HTML5 Video',
      type: 'file',
      allowedFileTypes: ['mp4', 'webm', 'mov', 'm4v'],
      helperText:
        'OPTIONAL. Drag-drop an MP4 / WebM / MOV file. When set, plays via native HTML5 <video> instead of a YouTube/Vimeo iframe — required for reliable user-controlled unmute on iOS when the page already has another video. Overrides Video URL when both are set.',
    },
    {
      name: 'nativeVideoPoster',
      friendlyName: 'Native Video Poster Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText:
        'OPTIONAL. First-frame image shown before the native video starts loading. Improves perceived load time on mobile.',
      showIf: 'options.get("nativeVideoUrl")',
    },
    {
      name: 'autoplay',
      friendlyName: 'Autoplay',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'muted',
      friendlyName: 'Muted',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Required for autoplay in most browsers',
    },
    {
      name: 'loop',
      friendlyName: 'Loop',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'controls',
      friendlyName: 'Show Controls',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showUnmuteButton',
      friendlyName: 'Show Unmute Button',
      type: 'boolean',
      defaultValue: false,
      // Background autoplay videos must boot muted to satisfy browser
      // unattended-autoplay policy. When the source clip actually has
      // an audio track that viewers should be able to turn on (e.g.
      // Greenfield Robotics "Grand Farm" video), turn this on to show
      // a tap-to-unmute overlay on top of the player. Leave it off for
      // silent loop-style background videos (Aquipor pour demo, etc.)
      // so the player stays clean. Applies to both the native HTML5
      // `<video>` path and Vimeo background-autoplay iframes.
      helperText:
        'Only useful when the video has sound. Shows a tap-to-unmute overlay on autoplay videos. Leave OFF for silent background loops.',
      showIf: 'options.get("autoplay") && !options.get("controls") && !options.get("embedCode")',
    },
    {
      name: 'mobileClickToPlay',
      friendlyName: 'Mobile Click-to-Play',
      type: 'boolean',
      defaultValue: true,
      showIf: '!options.get("autoplay") && !options.get("embedCode")',
      helperText:
        'On mobile, show a thumbnail with a play button instead of loading the iframe immediately. Improves performance and works better on iOS Safari.',
    },
    {
      name: 'desktopClickToPlay',
      friendlyName: 'Desktop Click-to-Play',
      type: 'boolean',
      defaultValue: false,
      showIf: '!options.get("autoplay") && !options.get("embedCode")',
      helperText:
        'On desktop, show a thumbnail with a play button instead of loading the iframe immediately. Recommended on pages with multiple videos — each Vimeo iframe loads ~600KB of JS, so deferring mount until click avoids bandwidth contention. Default off so existing campaigns are unaffected.',
    },
    {
      name: 'posterImage',
      friendlyName: 'Custom Thumbnail / Poster Image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      helperText:
        'OPTIONAL. Custom poster image shown before the video plays (replaces the auto-fetched Vimeo/YouTube thumbnail). Only visible when click-to-play is active on at least one viewport.',
      showIf: '!options.get("embedCode") && !options.get("nativeVideoUrl")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Aspect ratio, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'aspectRatio',
      friendlyName: 'Aspect Ratio',
      type: 'text',
      enum: [
        { label: '16:9 (Widescreen)', value: '16:9' },
        { label: '4:3 (Standard)', value: '4:3' },
        { label: '1:1 (Square)', value: '1:1' },
        { label: '9:16 (Vertical)', value: '9:16' },
        { label: 'Auto (detect from video)', value: 'auto' },
      ],
      defaultValue: '16:9',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'text',
      defaultValue: '100%',
    },
    // ═══════════════════════════════════════════════════════════
    // ADVANCED
    // ═══════════════════════════════════════════════════════════
    {
      name: 'embedCode',
      friendlyName: 'Custom Embed Code',
      type: 'longText',
      helperText:
        'Paste custom iframe embed code (for Loom, Wistia, etc.). Overrides Video URL if both provided.',
      advanced: true,
    },
    {
      name: 'borderRadius',
      friendlyName: 'Border Radius',
      type: 'text',
      defaultValue: '0px',
      advanced: true,
    },
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 28. MEDIA BLOCK - Drop-in image / video / GIF with good defaults
// ============================================================
Builder.registerComponent(BuilderMediaBlock, {
  name: 'Media Block',
  friendlyName: 'Utility: Media Block',
  description:
    'Drop-in image, video, or GIF. Sized correctly by default — no cropping or overflow.',
  image: ICONS.media,
  canHaveChildren: false,
  priority: 1,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Section ID, media source, alt text, caption, click target
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links',
    },
    {
      name: 'mediaType',
      friendlyName: '🖼️ Media Type',
      type: 'string',
      enum: ['image', 'video', 'gif'],
      defaultValue: 'image',
    },
    {
      name: 'mediaFile',
      friendlyName: '🖼️ Media File',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg', 'mp4', 'webm', 'mov'],
      helperText: 'Upload from asset library or your computer',
    },
    {
      name: 'mediaFileMobile',
      friendlyName: 'Media File (Mobile)',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg', 'mp4', 'webm', 'mov'],
      helperText: 'Optional: shown on screens < 640px',
    },
    {
      name: 'videoUrl',
      friendlyName: '🎬 Video URL (alternate)',
      type: 'text',
      helperText: "Use instead of file upload if video doesn't play (direct .mp4 link)",
      showIf: 'options.get("mediaType") === "video"',
    },
    {
      name: 'altText',
      friendlyName: 'Alt Text',
      type: 'string',
      helperText: 'Accessibility description of the media',
      showIf: 'options.get("mediaType") !== "video"',
    },
    {
      name: 'caption',
      friendlyName: 'Caption',
      type: 'string',
      defaultValue: '',
      helperText: 'Small text below the media',
    },
    {
      name: 'captionColor',
      friendlyName: 'Caption Color',
      type: 'color',
      defaultValue: '#6b6b6b',
      showIf: 'options.get("caption")',
    },
    {
      name: 'captionSize',
      friendlyName: 'Caption Size',
      type: 'string',
      enum: ['11px', '12px', '13px', '14px'],
      defaultValue: '13px',
      showIf: 'options.get("caption")',
    },
    {
      name: 'showDisclaimer',
      friendlyName: 'Show Disclaimer',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Adds an italic, muted footnote beneath the caption',
    },
    {
      name: 'disclaimerText',
      friendlyName: 'Disclaimer Text',
      type: 'longText',
      defaultValue: MEDIA_BLOCK_DEFAULT_DISCLAIMER_TEXT,
      helperText: 'Generic boilerplate is pre-filled — edit per-page as needed for marketing/legal',
      showIf: 'options.get("showDisclaimer")',
    },
    {
      name: 'clickUrl',
      friendlyName: 'Click URL',
      type: 'string',
      defaultValue: '',
      helperText: 'Make the media clickable (optional)',
    },
    {
      name: 'openInNewTab',
      friendlyName: 'Open in New Tab',
      type: 'boolean',
      defaultValue: true,
      showIf: 'options.get("clickUrl")',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Alignment, sizing, background, padding
    // ═══════════════════════════════════════════════════════════
    {
      name: 'alignment',
      friendlyName: 'Alignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'string',
      defaultValue: '100%',
      helperText: 'e.g. 100%, 800px, 600px — use 100% to fill the parent column',
    },
    {
      name: 'maxHeight',
      friendlyName: 'Max Height',
      type: 'string',
      defaultValue: '',
      helperText: 'e.g. 400px, 50vh (empty = auto)',
    },
    {
      name: 'objectFit',
      friendlyName: 'Sizing',
      type: 'string',
      enum: ['contain', 'cover', 'fill', 'none', 'scale-down'],
      defaultValue: 'contain',
      helperText:
        'Contain = full image visible, Cover = fills space (may crop), None = original size',
    },
    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: 'transparent',
    },
    {
      name: 'borderRadius',
      friendlyName: 'Border Radius',
      type: 'string',
      defaultValue: '0px',
      helperText: 'e.g. 8px, 16px, 50% for circle',
    },
    {
      name: 'shadow',
      friendlyName: 'Drop Shadow',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
      helperText: 'Padding above and below (px)',
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
      helperText: 'Padding left and right (px)',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
  },
});

// ============================================================
// 29. IRA CROSS-LINK - Banner linking IRA ↔ non-IRA page variants
// ============================================================
Builder.registerComponent(BuilderIraCrossLink, {
  name: 'IRA Cross-Link',
  friendlyName: 'Utility: IRA Cross-Link',
  description: 'Banner that links between IRA and non-IRA page variants of the same offering.',
  image: ICONS.link,
  canHaveChildren: false,
  priority: 44,
  inputs: [
    // ═══════════════════════════════════════════════════════════
    // CORE — Page mode, alternate slug, banner copy
    // ═══════════════════════════════════════════════════════════
    {
      name: 'isIraPage',
      friendlyName: 'Is IRA Page',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable if this page is the IRA version of the offering',
    },
    {
      name: 'alternateSlug',
      friendlyName: 'Alternate Offering Slug',
      type: 'string',
      helperText:
        'Slug of the alternate version. Leave empty to auto-derive (appends or removes "-ira" suffix)',
    },
    {
      name: 'bannerText',
      friendlyName: 'Banner Text',
      type: 'string',
      helperText: 'Custom banner message. Leave empty for auto-generated text based on page type.',
    },
    {
      name: 'linkText',
      friendlyName: 'Link Text',
      type: 'string',
      helperText: 'Custom link text. Leave empty for auto-generated text.',
    },
    // ═══════════════════════════════════════════════════════════
    // LAYOUT & PRESENTATION — Banner colors, max-width
    // ═══════════════════════════════════════════════════════════
    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: '#F0F7F8',
    },
    {
      name: 'textColor',
      friendlyName: 'Text Color',
      type: 'color',
      defaultValue: '#111827',
    },
    {
      name: 'linkColor',
      friendlyName: 'Link Color',
      type: 'color',
      defaultValue: '#185C65',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'string',
      defaultValue: '1200px',
    },
    // ═══════════════════════════════════════════════════════════
    // ADVANCED — Badge text/colors, border, padding, slug override
    // ═══════════════════════════════════════════════════════════
    {
      name: 'iraBadgeText',
      friendlyName: 'IRA Badge Text',
      type: 'string',
      defaultValue: 'IRA',
      advanced: true,
    },
    {
      name: 'nonIraBadgeText',
      friendlyName: 'Non-IRA Badge Text',
      type: 'string',
      defaultValue: 'Standard',
      advanced: true,
    },
    {
      name: 'badgeBackgroundColor',
      friendlyName: 'Badge Background',
      type: 'color',
      helperText: 'Leave empty for auto (teal for IRA, gray for standard)',
      advanced: true,
    },
    {
      name: 'badgeTextColor',
      friendlyName: 'Badge Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      advanced: true,
    },
    {
      name: 'borderColor',
      friendlyName: 'Border Color',
      type: 'color',
      defaultValue: '#D1E3E6',
      advanced: true,
    },
    {
      name: 'borderRadius',
      friendlyName: 'Border Radius',
      type: 'string',
      defaultValue: '8px',
      advanced: true,
    },
    {
      name: 'padding',
      friendlyName: 'Padding',
      type: 'string',
      defaultValue: '12px 24px',
      advanced: true,
    },
    {
      name: 'offeringSlug',
      friendlyName: 'Offering Slug',
      type: 'string',
      helperText: 'Override the offering slug (usually auto-detected from URL)',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '20px',
  },
});

// ============================================================
// 30. DISCLAIMER - Footnote-style rich text utility
// ============================================================
Builder.registerComponent(BuilderDisclaimer, {
  name: 'Disclaimer',
  friendlyName: 'Utility: Disclaimer',
  description:
    'Small italic muted footnote text. Drop under an image, video, or text block to surface required regulatory or supplementary copy.',
  image: ICONS.disclaimer,
  canHaveChildren: false,
  priority: 2,
  inputs: [
    {
      name: 'text',
      friendlyName: 'Disclaimer Text',
      type: 'richText',
      defaultValue:
        '<p>*Forward-looking statements and projections are estimates, not guarantees of future performance.</p>',
      helperText:
        'Supports bold, italic, and links. Renders inside a <div role="note"> for screen readers.',
    },
    {
      name: 'textColor',
      friendlyName: 'Text Color',
      type: 'color',
      helperText: 'Leave blank to inherit from the surrounding section.',
    },
    {
      name: 'align',
      friendlyName: 'Alignment',
      type: 'enum',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width (px)',
      type: 'number',
      defaultValue: 600,
      helperText: 'Keeps long disclaimers readable. Set to 0 to remove the limit.',
    },
    {
      name: 'marginTop',
      friendlyName: 'Margin Top (px)',
      type: 'number',
      defaultValue: 8,
      helperText: 'Space above the disclaimer (relative to the element it sits under).',
    },
    {
      name: 'marginBottom',
      friendlyName: 'Margin Bottom (px)',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'fontStyle',
      friendlyName: 'Font Style',
      type: 'enum',
      enum: ['italic', 'normal'],
      defaultValue: 'italic',
      advanced: true,
    },
    {
      name: 'fontSize',
      friendlyName: 'Font Size',
      type: 'string',
      helperText: 'Override the default 12px caption size (e.g. "11px", "13px").',
      advanced: true,
    },
    {
      name: 'opacity',
      friendlyName: 'Opacity',
      type: 'number',
      defaultValue: 0.7,
      helperText: '0.0 (invisible) to 1.0 (full strength). Defaults to 0.7 for the muted look.',
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
  },
});

// ============================================================
// 29. UPDATES SECTION - Company updates pulled from offering data
// ============================================================
Builder.registerComponent(BuilderUpdatesSection, {
  name: 'UpdatesSection',
  description: 'Company updates from the offering with pagination and share buttons.',
  friendlyName: 'Data: Updates',
  image: ICONS.updates,
  canHaveChildren: false,
  priority: 30,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links (e.g. "updates")',
    },

    // ═══════════════════════════════════════════════════════════
    // CORE — Eyebrow, title, header alignment, new-updates badge,
    // update card content
    // ═══════════════════════════════════════════════════════════
    {
      name: 'sectionLabel',
      friendlyName: '🏷️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small label above the title (e.g. "COMPANY NEWS")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'showTitle',
      friendlyName: 'Show Title',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'title',
      friendlyName: '📝 Title',
      type: 'string',
      defaultValue: 'Updates',
      helperText: 'Use | for line breaks',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#020E0B',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleFontWeight',
      friendlyName: 'Title Font Weight',
      type: 'number',
      enum: [400, 500, 600, 700, 800],
      defaultValue: 400,
      helperText: '400 = regular, 600 = semibold, 700 = bold',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Highlight Word(s)',
      type: 'string',
      defaultValue: '',
      helperText: 'Text within the title to highlight in a different color',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '36px',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '28px',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'headerAlignment',
      friendlyName: 'Header Alignment',
      type: 'enum',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left',
      showIf: 'options.get("showTitle")',
    },

    // NEW UPDATES
    {
      name: 'showNewUpdates',
      friendlyName: '🆕 Show New Updates',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Display recently approved updates in a separate section above all updates',
    },
    {
      name: 'newUpdatesTitle',
      friendlyName: 'New Updates Title',
      type: 'string',
      defaultValue: 'NEW UPDATES',
      showIf: 'options.get("showNewUpdates")',
    },
    {
      name: 'newUpdatesTitleColor',
      friendlyName: 'New Updates Color',
      type: 'color',
      defaultValue: '#E06004',
      showIf: 'options.get("showNewUpdates")',
      helperText: 'Color for the new updates heading, date, and title',
    },

    // UPDATE CARDS
    {
      name: 'dateColor',
      friendlyName: '📅 Date Color',
      type: 'color',
      defaultValue: '#577275',
    },
    {
      name: 'updateTitleColor',
      friendlyName: '🔤 Update Title Color',
      type: 'color',
      defaultValue: '#020E0B',
    },
    {
      name: 'updateTextColor',
      friendlyName: '📄 Update Body Color',
      type: 'color',
      defaultValue: '#577275',
    },
    {
      name: 'cardBackgroundColor',
      friendlyName: '🃏 Card Background',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'cardBorderColor',
      friendlyName: 'Card Border Color',
      type: 'color',
      defaultValue: '#E5E7EB',
    },
    {
      name: 'cardBorderRadius',
      friendlyName: 'Card Border Radius',
      type: 'string',
      defaultValue: '8px',
    },
    {
      name: 'cardSpacing',
      friendlyName: 'Card Spacing',
      type: 'string',
      defaultValue: '24px',
      helperText: 'Gap between update cards',
    },
    {
      name: 'columns',
      friendlyName: 'Columns',
      type: 'number',
      defaultValue: 1,
      helperText: 'Number of columns (1-3). Mobile always shows 1 column.',
      enum: [1, 2, 3],
    },
    {
      name: 'maxCardHeight',
      friendlyName: 'Max Card Height (px)',
      type: 'string',
      defaultValue: '0',
      helperText:
        'Max content height before "Read More" appears. Set to 0 to disable and show full content.',
    },
    {
      name: 'showShareButton',
      friendlyName: '🔗 Show Share Button',
      type: 'boolean',
      defaultValue: true,
    },

    // SHOW MORE BUTTON
    {
      name: 'showMoreButtonText',
      friendlyName: '🔘 Show More Text',
      type: 'string',
      defaultValue: 'Show More Updates',
    },
    {
      name: 'showMoreButtonColor',
      friendlyName: 'Button Background',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'showMoreButtonTextColor',
      friendlyName: 'Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'showMoreButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '10px',
    },

    // PAGINATION
    {
      name: 'initialCount',
      friendlyName: '📊 Updates Per Page',
      type: 'number',
      defaultValue: 5,
      helperText: 'Number of updates loaded initially and per "Show More" click',
    },

    // LAYOUT
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding',
      type: 'number',
      defaultValue: 60,
      helperText: 'Top and bottom padding in pixels',
    },

    // BACKGROUND
    ...SECTION_BACKGROUND_INPUTS,

    // FONTS
    ...TITLE_FONT_INPUTS,
    ...BODY_FONT_INPUTS,

    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
      advanced: true,
    },
    ...SECTION_INVEST_BUTTON_INPUTS,

    // ═══════════════════════════════════════════════════════════
    // PRESS — Integrated press articles below updates
    // ═══════════════════════════════════════════════════════════
    {
      name: 'showPress',
      friendlyName: '📰 Show Press Articles',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Show press articles below updates (auto-fetched from offering data)',
    },
    {
      name: 'pressTitleText',
      friendlyName: 'Press Title',
      type: 'string',
      defaultValue: 'PRESS',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressTitleColor',
      friendlyName: 'Press Title Color',
      type: 'color',
      defaultValue: '#020E0B',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressArticleTitleColor',
      friendlyName: 'Press Article Title Color',
      type: 'color',
      defaultValue: '#111827',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressArticleSummaryColor',
      friendlyName: 'Press Summary Color',
      type: 'color',
      defaultValue: '#4B5563',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressViewArticleButtonText',
      friendlyName: 'View Article Button Text',
      type: 'string',
      defaultValue: 'View Article',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressViewArticleButtonColor',
      friendlyName: 'View Article Button Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressViewArticleButtonTextColor',
      friendlyName: 'View Article Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
      showIf: 'options.get("showPress")',
    },
    {
      name: 'pressShowMoreButtonText',
      friendlyName: 'Press Show More Text',
      type: 'string',
      defaultValue: 'Show More Press',
      showIf: 'options.get("showPress")',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 30. PRESS SECTION - Press articles pulled from offering data
// ============================================================
Builder.registerComponent(BuilderPressSection, {
  name: 'PressSection',
  description: 'Press articles and media mentions from the offering with pagination.',
  friendlyName: 'Data: Press',
  image: ICONS.updates,
  canHaveChildren: false,
  priority: 31,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      helperText: 'Used for Top Nav anchor links (e.g. "press")',
    },

    // CORE
    {
      name: 'sectionLabel',
      friendlyName: '🏷️ Section Label',
      type: 'string',
      defaultValue: '',
      helperText: 'Small label above the title (e.g. "IN THE NEWS")',
    },
    {
      name: 'sectionLabelColor',
      friendlyName: 'Label Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'sectionLabelFontSize',
      friendlyName: 'Label Font Size',
      type: 'string',
      defaultValue: '14px',
      helperText: 'e.g. 11px, 13px, 14px',
      showIf: 'options.get("sectionLabel")',
    },
    {
      name: 'showTitle',
      friendlyName: 'Show Title',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'title',
      friendlyName: '📝 Title',
      type: 'string',
      defaultValue: 'PRESS',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleColor',
      friendlyName: 'Title Color',
      type: 'color',
      defaultValue: '#020E0B',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleHighlight',
      friendlyName: 'Highlight Word(s)',
      type: 'string',
      defaultValue: '',
      helperText: 'Text within the title to highlight in a different color',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleHighlightColor',
      friendlyName: 'Highlight Color',
      type: 'color',
      defaultValue: THEME.accent,
      showIf: 'options.get("titleHighlight")',
    },
    {
      name: 'titleFontSize',
      friendlyName: 'Title Font Size',
      type: 'string',
      defaultValue: '34px',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'titleMobileFontSize',
      friendlyName: 'Title Font Size (Mobile)',
      type: 'string',
      defaultValue: '24px',
      showIf: 'options.get("showTitle")',
    },
    {
      name: 'headerAlignment',
      friendlyName: 'Header Alignment',
      type: 'enum',
      enum: ['left', 'center'],
      defaultValue: 'left',
    },

    // ARTICLE CARD STYLE
    {
      name: 'articleTitleColor',
      friendlyName: '📰 Article Title Color',
      type: 'color',
      defaultValue: THEME.textPrimary,
    },
    {
      name: 'articleSummaryColor',
      friendlyName: 'Article Summary Color',
      type: 'color',
      defaultValue: THEME.textSecondary,
    },
    {
      name: 'cardBackgroundColor',
      friendlyName: '🎨 Card Background',
      type: 'color',
      defaultValue: THEME.cardBg,
    },
    {
      name: 'cardBorderColor',
      friendlyName: 'Card Border Color',
      type: 'color',
      defaultValue: '#E5E7EB',
    },
    {
      name: 'cardBorderRadius',
      friendlyName: 'Card Border Radius',
      type: 'string',
      defaultValue: '8px',
    },
    {
      name: 'cardSpacing',
      friendlyName: 'Card Spacing',
      type: 'string',
      defaultValue: '16px',
    },
    {
      name: 'showImages',
      friendlyName: 'Show Article Images',
      type: 'boolean',
      defaultValue: true,
    },

    // VIEW ARTICLE BUTTON
    {
      name: 'viewArticleButtonText',
      friendlyName: '🔗 View Article Button Text',
      type: 'string',
      defaultValue: 'View Article',
    },
    {
      name: 'viewArticleButtonColor',
      friendlyName: 'View Article Button Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'viewArticleButtonTextColor',
      friendlyName: 'View Article Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
    },

    // SHOW MORE BUTTON
    {
      name: 'showMoreButtonText',
      friendlyName: '📄 Show More Button Text',
      type: 'string',
      defaultValue: 'Show More Press',
    },
    {
      name: 'showMoreButtonColor',
      friendlyName: 'Show More Button Color',
      type: 'color',
      defaultValue: THEME.accent,
    },
    {
      name: 'showMoreButtonTextColor',
      friendlyName: 'Show More Button Text Color',
      type: 'color',
      defaultValue: '#FFFFFF',
    },
    {
      name: 'showMoreButtonBorderRadius',
      friendlyName: 'Button Border Radius',
      type: 'string',
      defaultValue: '10px',
    },

    // LAYOUT
    {
      name: 'verticalPadding',
      friendlyName: '↕️ Vertical Padding',
      type: 'number',
      defaultValue: 60,
      helperText: 'Top and bottom padding in pixels',
    },

    // BACKGROUND
    ...SECTION_BACKGROUND_INPUTS,

    // FONTS
    ...TITLE_FONT_INPUTS,

    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
      advanced: true,
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 30b. SPOTLIGHTS SECTION - Company video clips carousel
// ============================================================
Builder.registerComponent(BuilderSpotlightsSection, {
  name: 'SpotlightsSection',
  description: "The company's short-form video clips in a carousel (auto-fetched).",
  friendlyName: 'Data: Spotlights',
  image: ICONS.updates,
  canHaveChildren: false,
  priority: 32,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: 'spotlights',
      helperText: 'Used for Top Nav anchor links (e.g. "spotlights")',
    },
    {
      name: 'title',
      friendlyName: '📝 Title',
      type: 'string',
      defaultValue: 'Spotlights',
    },
    ...TITLE_FONT_INPUTS,
    {
      name: 'subtitle',
      friendlyName: 'Subtitle',
      type: 'string',
      defaultValue: '',
    },
    ...BODY_FONT_INPUTS,
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 60,
      helperText: 'Top and bottom padding in pixels',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
      advanced: true,
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 31. Classic: Section Navigation – Sticky TabMenu with anchors
// ============================================================
// Classic wrappers are deprecated. They embed legacy classic-layout
// components inside Builder pages. Hidden from the insert menu so new
// pages don't add them; existing pages keep working until migration completes.
// See: https://startengine.atlassian.net/wiki/spaces/Engineering/pages/2048917505
Builder.registerComponent(BuilderClassicSectionNav, {
  name: 'Classic: Section Navigation',
  friendlyName: 'Classic: Section Navigation (deprecated)',
  image: ICONS.topNav,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: '',
      helperText: 'HTML id for this nav element',
    },
    {
      name: 'tabs',
      friendlyName: 'Navigation Tabs',
      type: 'list',
      defaultValue: [
        { id: 'overview', label: 'Overview', sectionId: '' },
        { id: 'about', label: 'About', sectionId: 'about' },
        { id: 'terms', label: 'Terms', sectionId: 'terms' },
        { id: 'spotlights', label: 'Spotlights', sectionId: 'spotlights' },
        { id: 'press', label: 'Press & Updates', sectionId: 'press' },
        { id: 'rewards', label: 'Rewards', sectionId: 'rewards' },
        { id: 'discussion', label: 'Discussion', sectionId: 'discussion' },
        { id: 'faq', label: 'Investing FAQs', sectionId: 'faq' },
      ],
      subFields: [
        { name: 'id', friendlyName: 'Tab ID', type: 'string' },
        { name: 'label', friendlyName: 'Label', type: 'string' },
        {
          name: 'sectionId',
          friendlyName: 'Target Section ID',
          type: 'string',
          helperText: 'HTML id of the section to scroll to (empty = scroll to top)',
        },
      ],
    },
    {
      name: 'showLogo',
      friendlyName: 'Show SE Logo (sticky)',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showFollowButton',
      friendlyName: 'Show Follow Button (sticky)',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'autoHideTabs',
      friendlyName: 'Auto-hide Empty Tabs',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Automatically hide tabs when their data is empty (e.g. no rewards, no press)',
    },
    {
      name: 'scrollOffset',
      friendlyName: 'Scroll Offset',
      type: 'number',
      defaultValue: 40,
      helperText: 'Extra pixels above the target section when scrolling',
      advanced: true,
    },
    {
      name: 'activeTabColor',
      friendlyName: 'Active Tab Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for platform default (#185C65)',
    },
    {
      name: 'tabTextColor',
      friendlyName: 'Tab Text Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for inherit',
    },
    ...SECTION_BACKGROUND_INPUTS,
    {
      name: 'stickyBackgroundColor',
      friendlyName: 'Sticky Background Color',
      type: 'color',
      defaultValue: '#FBFDFA',
      helperText: 'Background when nav is sticky',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 34. Classic: Two-Column Layout – Left content + Right sticky sidebar
// ============================================================
Builder.registerComponent(BuilderClassicTwoColumnLayout, {
  name: 'Classic: Two-Column Layout',
  friendlyName: 'Classic: Two-Column Layout (deprecated)',
  image: ICONS.split,
  canHaveChildren: true,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'leftContent',
      friendlyName: 'Left Column Content',
      type: 'blocks',
      defaultValue: [],
      helperText: 'Drop components here for the left (main) column',
    },
    {
      name: 'stickyTopOffset',
      friendlyName: 'Sidebar Sticky Top Offset',
      type: 'number',
      defaultValue: 80,
      helperText: 'Pixels from top when sidebar becomes sticky',
      advanced: true,
    },
    {
      name: 'sidebarWidth',
      friendlyName: 'Sidebar Width',
      type: 'number',
      defaultValue: 369,
      advanced: true,
    },
    {
      name: 'leftColumnRatio',
      friendlyName: 'Left Column Size (out of 12)',
      type: 'number',
      defaultValue: 7.8,
      advanced: true,
    },
    {
      name: 'rightColumnRatio',
      friendlyName: 'Right Column Size (out of 12)',
      type: 'number',
      defaultValue: 4,
      advanced: true,
    },
    {
      name: 'showMobileBottomBar',
      friendlyName: 'Show Mobile Bottom Invest Bar',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showLoyaltyBonus',
      friendlyName: 'Show Loyalty Bonus (sidebar)',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showStats',
      friendlyName: 'Show Offering Stats (sidebar)',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showRequestLiveStream',
      friendlyName: 'Show Request Live Stream Card (sidebar)',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'Displays a Request Live Stream card below the invest CTA. Shown only to logged-in users on open (approved) offerings, and hidden on Reg D offerings.',
    },
    {
      name: 'investButtonColor',
      friendlyName: 'Invest Button Color',
      type: 'color',
      defaultValue: '',
    },
    {
      name: 'waitlistButtonColor',
      friendlyName: 'Waitlist Button Color',
      type: 'color',
      defaultValue: '',
    },
    {
      name: 'forceReserveCta',
      friendlyName: '🟧 Force Reserve Now CTA (TTW Mode)',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Toggle ON to flip the sidebar + mobile bottom bar to the orange "Reserve Now" button + /reserve URL, regardless of offering status. Use for Reg A+ / Reg CF Test-The-Waters previews where the BE doesn\'t expose a TTW signal yet (e.g. gacw-reg-a, status `not_submitted`). Threads through to both the desktop sidebar and the mobile sticky bar so the CTA stays in parity across breakpoints.',
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1280,
      helperText: 'Clamped between 1024–1440px',
      advanced: true,
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 35. Classic: Terms Section – Old campaign page terms design
// ============================================================
Builder.registerComponent(BuilderClassicTermsSection, {
  name: 'Classic: Terms Section',
  friendlyName: 'Classic: Terms Section (deprecated)',
  image: ICONS.terms,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: 'terms',
      helperText: 'HTML id for anchor navigation',
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 740,
      advanced: true,
    },
    {
      name: 'forceShowOnTtw',
      friendlyName: '🔓 Force Show on Test the Waters',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'OFF (default): hide automatically on TTW offerings — placeholder values like "$X" / "Nov XX, 2025" mislead visitors. ON: render terms anyway (e.g. partial-disclosure preview).',
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 36. Classic: Team Section – Old campaign page team design
// ============================================================
Builder.registerComponent(BuilderClassicTeamSection, {
  name: 'Classic: Team Section',
  friendlyName: 'Classic: Team Section (deprecated)',
  image: ICONS.team,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: 'team',
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 740,
      advanced: true,
    },
    {
      name: 'imageFit',
      friendlyName: 'Headshot Image Fit',
      type: 'string',
      enum: ['contain', 'cover'],
      enumLabels: {
        contain: 'Contain (full image, may letterbox)',
        cover: 'Cover (crop to fill, faster but can clip faces)',
      },
      defaultValue: 'contain',
      helperText:
        'How team-member headshots fit their cards. "Contain" shows the full image (may letterbox if the source aspect ratio differs). "Cover" crops to fill — only safe for square / curated headshots.',
    },
    {
      name: 'imageFitBackgroundColor',
      friendlyName: 'Letterbox Background',
      type: 'color',
      defaultValue: '#F3F4F6',
      helperText:
        'Background color shown around a "Contain"-fit headshot when the source image aspect ratio differs from the card. Pick a neutral that matches the section background.',
      showIf: 'options.get("imageFit") === "contain"',
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 37. Classic: Rewards Section – Old campaign page rewards design
// ============================================================
Builder.registerComponent(BuilderClassicRewardsSection, {
  name: 'Classic: Rewards Section',
  friendlyName: 'Classic: Rewards Section (deprecated)',
  image: ICONS.rewards,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: 'rewards',
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 740,
      advanced: true,
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 32. Classic: Invest Sidebar – Sticky sidebar with stats + CTA
// ============================================================
Builder.registerComponent(BuilderClassicInvestSidebar, {
  name: 'Classic: Invest Sidebar',
  friendlyName: 'Classic: Invest Sidebar (deprecated)',
  image: ICONS.invest,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'showLoyaltyBonus',
      friendlyName: 'Show Loyalty Bonus Banner',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showStats',
      friendlyName: 'Show Offering Stats',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Raised, Investors, Min Invest, Valuation, etc.',
    },
    {
      name: 'showRequestLiveStream',
      friendlyName: 'Show Request Live Stream Card',
      type: 'boolean',
      defaultValue: true,
      helperText:
        'Displays a Request Live Stream card below the invest CTA. Shown only to logged-in users on open (approved) offerings, and hidden on Reg D offerings.',
    },
    {
      name: 'investButtonColor',
      friendlyName: 'Invest Button Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for platform default (#185C65)',
    },
    {
      name: 'waitlistButtonColor',
      friendlyName: 'Waitlist Button Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Leave empty for platform default orange',
    },
    {
      name: 'forceReserveCta',
      friendlyName: '🟧 Force Reserve Now CTA (TTW Mode)',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Toggle ON to render the orange "Reserve Now" button + /reserve URL on the sidebar, regardless of offering status. Use for Reg A+ / Reg CF Test-The-Waters previews where the BE doesn\'t expose a TTW signal yet (e.g. gacw-reg-a, status `not_submitted`). The four unambiguous TTW statuses (`testing`, `test_the_waters`, `pre_launch`, `ttw`) auto-flip without this toggle.',
    },
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// ============================================================
// 33. Classic: Story Section – Pitch deck HTML from backend
// ============================================================
Builder.registerComponent(BuilderClassicStorySection, {
  name: 'Classic: Story Section',
  image: ICONS.pitchDeck,
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'sectionId',
      friendlyName: 'Section ID',
      type: 'string',
      defaultValue: 'story',
      helperText: 'HTML id for anchor navigation (e.g. "story")',
    },
    {
      name: 'verticalPadding',
      friendlyName: 'Vertical Padding',
      type: 'number',
      defaultValue: 0,
      helperText: 'Top and bottom padding in pixels',
    },
    {
      name: 'horizontalPadding',
      friendlyName: 'Horizontal Padding',
      type: 'number',
      defaultValue: 0,
      helperText: 'Left and right padding in pixels',
    },
    {
      name: 'maxWidth',
      friendlyName: 'Max Width',
      type: 'number',
      defaultValue: 1200,
      advanced: true,
    },
    ...SECTION_BACKGROUND_INPUTS,
    SECTION_BORDER_RADIUS_INPUT,
  ],
  defaultStyles: {
    marginTop: '0px',
    marginBottom: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
});

// Export components for potential direct usage (ordered by typical page flow)
export {
  BuilderHeroSection,
  BuilderSplitSection,
  BuilderStatsSection,
  BuilderLogosSection,
  BuilderCenteredSection,
  BuilderQuoteSection,
  BuilderFeaturesSection,
  BuilderFormSection,
  BuilderTeamSlider,
  BuilderTermsSection,
  BuilderTableSection,
  BuilderPerksSection,
  BuilderDiscussionSection,
  BuilderAccordionSection,
  BuilderCTASection,
  BuilderFooterSection,
  BuilderInvestButton,
  BuilderScheduleCallBubble,
  BuilderGetEquityBubble,
  BuilderVideoEmbed,
  BuilderMediaBlock,
  BuilderIraCrossLink,
  BuilderImage,
  BuilderDisclaimer,
  BuilderBannerSection,
  BuilderUpdatesSection,
  BuilderClassicStorySection,
  BuilderClassicSectionNav,
  BuilderClassicInvestSidebar,
  BuilderClassicTwoColumnLayout,
  BuilderClassicTermsSection,
  BuilderClassicTeamSection,
  BuilderClassicRewardsSection,
};
